import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  useSearchParams,
  useParams,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Calendar,
  UploadCloud,
  CheckCircle2,
  AlertTriangle,
  AlertCircle,
  Clock,
  LogOut,
  RefreshCw,
  Check,
  X,
  User,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Stethoscope,
  Eye,
  EyeOff,
  Paperclip,
  ShieldCheck,
  Search,
  RotateCcw,
  Mail,
  KeyRound,
  Info,
  FileText,
  Phone,
  HelpCircle,
  Send,
  ArrowUpDown,
  Building2,
  FileCheck2,
  ClipboardCheck,
  Users,
  Printer,
  Download,
  UserX,
  DollarSign,
} from "lucide-react";
import { clinicaService } from "../services/clinica.service";
import {
  getAuthToken,
  setAuthToken,
  getSelectedClinicaId,
  setSelectedClinicaId,
} from "../services/api";
import AtendimentoMedicoModal from "../components/AtendimentoMedicoModal";
import RecepcaoCheckinModal from "../components/RecepcaoCheckinModal";
import CertificadoA1Modal from "../components/CertificadoA1Modal";
import ClinicaSelectorModal from "../components/ClinicaSelectorModal";
import RepassesClinicaView from "../components/RepassesClinicaView";
import FormField from "../components/FormField";
import FormattedInput from "../components/FormattedInput";
import PageContainer from "../components/PageContainer";
import { formatCpf, formatCnpj, formatPhone } from "../utils/formatters";
import { applyProgressiveMask } from "../utils/maskUtils";
import type {
  ParceiroDashboardResponse,
  ParceiroClinicaPerfilContract,
  ParceiroPendenciaItemContract,
  ParceiroPaginationMeta,
  ParceiroResumoSla,
  ParceiroPendenciasQueryDto,
  ParceiroValidarTokenAcessoResponse,
  ParceiroContatosCadastradosResponse,
  ParceiroRecepcaoItemContract,
  MedicoPerfilParceiroContract,
  MedicoClinicaVinculadaContract,
  MedicoAtendimentoPendenteContract,
  MedicoRealizarAtendimentoResponse,
} from "@sstexames/shared-types";

/**
 * Utilitário de máscara de CNPJ: 00.000.000/0000-00
 */
function maskCnpj(value: string): string {
  return applyProgressiveMask("cnpj", value);
}

/**
 * Utilitário para formatar data YYYY-MM-DD para DD/MM/YYYY
 */
function formatDateBR(dateStr?: string | null): string {
  if (!dateStr) return "";
  const clean = dateStr.split("T")[0];
  const parts = clean.split("-");
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return clean;
}

/**
 * Determina se o procedimento é o Exame Clínico (ASO) que exige seleção de aptidão:
 * Para exames laboratoriais/complementares (Audiometria, Acuidade, Raio X, Sangue, etc.), NÃO abre modal.
 */
function isAsoExam(
  exameNome?: string,
  isAso?: boolean,
  isClinico?: boolean,
  filePath?: string | null,
): boolean {
  if (typeof isClinico === "boolean" && isClinico) return true;
  if (typeof isAso === "boolean" && isAso) return true;
  if (filePath && (filePath.includes('/aso/') || filePath.includes('aso_'))) return true;
  if (!exameNome) return false;
  const n = exameNome
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return (
    n.includes("clinico") ||
    n.includes("aso") ||
    n.includes("admissional") ||
    n.includes("periodico") ||
    n.includes("demissional") ||
    n.includes("retorno") ||
    n.includes("mudanca") ||
    n.includes("ocupacional")
  );
}

function isItemNoFuturo(data_agendamento?: string | null): boolean {
  if (!data_agendamento) return false;
  const dataLocal = new Date();
  const year = dataLocal.getFullYear();
  const month = String(dataLocal.getMonth() + 1).padStart(2, '0');
  const day = String(dataLocal.getDate()).padStart(2, '0');
  const hoje = `${year}-${month}-${day}`;
  const dataItem = data_agendamento.split('T')[0];
  return dataItem > hoje;
}

interface ExameFlutuanteItem {
  tipo: string;
  tag: string;
  accentDot: string;
  accentText: string;
}

interface CenarioFlutuante {
  positionClass: string;
  layoutClass: string;
  items: ExameFlutuanteItem[];
}

const CENARIOS_FLUTUANTES: CenarioFlutuante[] = [
  // 1. Acima do Box (Centralizado seguro)
  {
    positionClass: '-top-14 sm:-top-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'Exame Admissional',
        tag: 'NR-07 • Agilidade na Contratação',
        accentDot: 'bg-emerald-500',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Audiometria Ocupacional',
        tag: 'Exame Complementar PGR',
        accentDot: 'bg-teal-500',
        accentText: 'text-teal-700',
      },
    ],
  },
  // 2. Acima à Direita (contido na coluna do form, sem vazar a janela à direita)
  {
    positionClass: '-top-12 sm:-top-14 right-0 sm:right-2 flex justify-end px-2',
    layoutClass: 'flex flex-col sm:flex-row items-end sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'Exame Periódico',
        tag: 'Vigência Preventiva Anual',
        accentDot: 'bg-emerald-500',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Acuidade Visual',
        tag: 'Saúde Ocular Ocupacional',
        accentDot: 'bg-blue-500',
        accentText: 'text-blue-700',
      },
    ],
  },
  // 3. Abaixo do Box (Centralizado seguro)
  {
    positionClass: '-bottom-14 sm:-bottom-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'Exame Demissional',
        tag: 'Homologação e Desligamento',
        accentDot: 'bg-blue-500',
        accentText: 'text-blue-700',
      },
      {
        tipo: 'Retorno ao Trabalho',
        tag: 'Aptidão Clínica Ocupacional',
        accentDot: 'bg-teal-500',
        accentText: 'text-teal-700',
      },
    ],
  },
  // 4. Acima à Esquerda (seguro, na margem do próprio form, sem ir para a imagem à esquerda)
  {
    positionClass: '-top-12 sm:-top-14 left-0 sm:left-2 flex justify-start px-2',
    layoutClass: 'flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'Mudança de Risco',
        tag: 'Atualização de GHE e Riscos PGR',
        accentDot: 'bg-amber-500',
        accentText: 'text-amber-700',
      },
      {
        tipo: 'Exame Clínico NR-07',
        tag: 'Avaliação Médica Ocupacional',
        accentDot: 'bg-emerald-600',
        accentText: 'text-emerald-700',
      },
    ],
  },
  // 5. Acima do Box (ASO Digital & Espirometria Respiratória)
  {
    positionClass: '-top-14 sm:-top-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'ASO Digital em Tempo Real',
        tag: 'Assinatura Eletrônica ICP-Brasil',
        accentDot: 'bg-emerald-600',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Espirometria Ocupacional',
        tag: 'Avaliação Respiratória NR-07',
        accentDot: 'bg-teal-600',
        accentText: 'text-teal-700',
      },
    ],
  },
  // 6. Acima à Direita (Toxicologia & Imagem Raio-X OIT)
  {
    positionClass: '-top-12 sm:-top-14 right-0 sm:right-2 flex justify-end px-2',
    layoutClass: 'flex flex-col sm:flex-row items-end sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'Toxicológico Ocupacional',
        tag: 'Laboratorial Credenciado CLT',
        accentDot: 'bg-purple-500',
        accentText: 'text-purple-700',
      },
      {
        tipo: 'Raio-X de Tórax OIT',
        tag: 'Padrão Internacional OIT',
        accentDot: 'bg-indigo-500',
        accentText: 'text-indigo-700',
      },
    ],
  },
  // 7. Abaixo do Box (Cardiovascular & Psicossocial NR-33/NR-35)
  {
    positionClass: '-bottom-14 sm:-bottom-16 left-1/2 -translate-x-1/2 w-full flex justify-center px-2',
    layoutClass: 'flex flex-wrap sm:flex-nowrap items-center justify-center gap-3 sm:gap-4',
    items: [
      {
        tipo: 'Eletrocardiograma (ECG)',
        tag: 'Saúde Cardiovascular do Colaborador',
        accentDot: 'bg-rose-500',
        accentText: 'text-rose-700',
      },
      {
        tipo: 'Psicossocial Ocupacional',
        tag: 'Aptidão Mental NR-33 e NR-35',
        accentDot: 'bg-cyan-600',
        accentText: 'text-cyan-700',
      },
    ],
  },
  // 8. Acima à Esquerda (eSocial & Rede Nacional Credenciada)
  {
    positionClass: '-top-12 sm:-top-14 left-0 sm:left-2 flex justify-start px-2',
    layoutClass: 'flex flex-col sm:flex-row items-start sm:items-center gap-2.5 sm:gap-3',
    items: [
      {
        tipo: 'eSocial S-2220 / S-2240',
        tag: 'Envio Automático e Seguro',
        accentDot: 'bg-emerald-500',
        accentText: 'text-emerald-700',
      },
      {
        tipo: 'Rede Nacional SST',
        tag: '+3.500 Clínicas Credenciadas',
        accentDot: 'bg-teal-600',
        accentText: 'text-teal-700',
      },
    ],
  },
];

export default function PortalClinica() {
  const [searchParams] = useSearchParams();
  const { token: tokenParam, cnpj: cnpjRouteParam } = useParams<{
    token?: string;
    cnpj?: string;
  }>();
  const location = useLocation();
  const navigate = useNavigate();

  const rawParam = searchParams.get("cnpj") || cnpjRouteParam || "";
  const isCnpj = rawParam.replace(/\D/g, "").length === 14;
  const cnpjParam = isCnpj ? rawParam.replace(/\D/g, "") : "";

  const tokenFromQuery = searchParams.get("token");
  const emailFromQuery = searchParams.get("email");
  const tipoParam = searchParams.get("tipo");
  const isDefinirSenhaRoute = location.pathname.includes("/definir-senha");
  const initialToken =
    tokenFromQuery || (isDefinirSenhaRoute && tokenParam ? tokenParam : "");
  const isMedicoReset = Boolean(emailFromQuery || tipoParam === "medico");

  // Modo de Autenticação / Telas Públicas
  const [authMode, setAuthMode] = useState<
    "login" | "solicitar_acesso" | "definir_senha"
  >(() => {
    return initialToken ? "definir_senha" : "login";
  });

  // Tipo de Acesso Selecionado (Clínica vs Médico)
  const [tipoAcesso, setTipoAcesso] = useState<"CLINICA" | "MEDICO">(() => {
    if (isMedicoReset) return "MEDICO";
    return (
      (localStorage.getItem("clinics_tipo_acesso") as "CLINICA" | "MEDICO") ||
      "CLINICA"
    );
  });

  // Efeito 1: Digitação do Título (Typing Effect balanceado)
  const fullText = 'Gestão inteligente de Saúde e Segurança do Trabalho';
  const [typedCharsCount, setTypedCharsCount] = useState(0);
  const [typingComplete, setTypingComplete] = useState(false);

  useEffect(() => {
    let current = 0;
    const interval = setInterval(() => {
      current += 1;
      setTypedCharsCount(current);
      if (current >= fullText.length) {
        clearInterval(interval);
        setTypingComplete(true);
      }
    }, 45); // Cadência suave nem muito rápida nem muito lenta

    return () => clearInterval(interval);
  }, []);

  const typedText = useMemo(() => fullText.slice(0, typedCharsCount), [typedCharsCount]);

  // Efeito 2: Exames Flutuantes ao Redor do Card (Entrada 1 a 1 com intervalo de 2.4s, e Saída em Fumaça)
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [item1Visible, setItem1Visible] = useState(false);
  const [item2Visible, setItem2Visible] = useState(false);

  useEffect(() => {
    // 1. Imediato: Primeiro item materializa na fumaça
    const t0 = setTimeout(() => {
      setItem1Visible(true);
      setItem2Visible(false);
    }, 0);

    // 2. Após 2.4s: Segundo item materializa na fumaça ao lado / abaixo
    const t1 = setTimeout(() => {
      setItem2Visible(true);
    }, 2400);

    // 3. Após mais 3.0s (t = 5.4s): Primeiro item se dissipa em fumaça
    const t2 = setTimeout(() => {
      setItem1Visible(false);
    }, 5400);

    // 4. Após mais 250ms (t = 5.65s): Segundo item se dissipa em fumaça
    const t3 = setTimeout(() => {
      setItem2Visible(false);
    }, 5650);

    // 5. Após a fumaça dissipar completamente (t = 6.6s): Avança para a próxima cena
    const t4 = setTimeout(() => {
      setCurrentSceneIndex((prev) => (prev + 1) % CENARIOS_FLUTUANTES.length);
    }, 6600);

    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
    };
  }, [currentSceneIndex]);

  const currentScene = CENARIOS_FLUTUANTES[currentSceneIndex];

  // Efeito 3: Apresentação da Caixinha de Login (Primeiro Boas-Vindas, depois o Formulário)
  const [showWelcome, setShowWelcome] = useState(false);
  const [showFormBox, setShowFormBox] = useState(false);

  useEffect(() => {
    const welcomeTimer = setTimeout(() => setShowWelcome(true), 80);
    const formTimer = setTimeout(() => setShowFormBox(true), 480);

    return () => {
      clearTimeout(welcomeTimer);
      clearTimeout(formTimer);
    };
  }, []);

  // Estados de Login da Clínica
  const [cnpjInput, setCnpjInput] = useState(maskCnpj(cnpjParam));
  const [senhaInput, setSenhaInput] = useState("");
  const [showSenha, setShowSenha] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [senhaNaoDefinida, setSenhaNaoDefinida] = useState(false);

  // Estados de Login do Médico
  const [medicoEmail, setMedicoEmail] = useState("");
  const [medicoSenha, setMedicoSenha] = useState("");
  const [medicoShowSenha, setMedicoShowSenha] = useState(false);
  const [medicoCodigo2fa, setMedicoCodigo2fa] = useState("");
  const [medicoRequires2fa, setMedicoRequires2fa] = useState(false);
  const [medicoForgotModalOpen, setMedicoForgotModalOpen] = useState(false);
  const [medicoForgotEmail, setMedicoForgotEmail] = useState("");
  const [medicoForgotLoading, setMedicoForgotLoading] = useState(false);
  const [medicoForgotSuccess, setMedicoForgotSuccess] = useState<string | null>(
    null,
  );

  // Estados do Médico Autenticado
  const [medicoPerfil, setMedicoPerfil] =
    useState<MedicoPerfilParceiroContract | null>(null);
  const [medicoClinicas, setMedicoClinicas] = useState<
    MedicoClinicaVinculadaContract[]
  >([]);
  const [selectedClinica, setSelectedClinica] =
    useState<MedicoClinicaVinculadaContract | null>(null);
  const [medicoAtendimentos, setMedicoAtendimentos] = useState<
    MedicoAtendimentoPendenteContract[]
  >([]);
  const [medicoAtendimentosLoading, setMedicoAtendimentosLoading] =
    useState(false);
  const [medicoSearch, setMedicoSearch] = useState("");
  const [medicoTipoFiltro, setMedicoTipoFiltro] = useState<string>("todos");
  const [medicoDataFiltro, setMedicoDataFiltro] = useState<string>(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  });
  const [medicoAbaAtiva, setMedicoAbaAtiva] = useState<'fila' | 'aguardando_exames' | 'aguardando_laudo' | 'realizados'>('fila');
  const [medicoAbaCounts, setMedicoAbaCounts] = useState<{
    fila: number;
    aguardando_exames: number;
    aguardando_laudo: number;
    realizados: number;
  }>({ fila: 0, aguardando_exames: 0, aguardando_laudo: 0, realizados: 0 });

  // Ordenação nas Tabelas do Médico e da Recepção
  const [medicoSortField, setMedicoSortField] = useState<string>("data");
  const [medicoSortOrder, setMedicoSortOrder] = useState<"asc" | "desc">("asc");
  const [recepcaoSortField, setRecepcaoSortField] = useState<string>("horario");
  const [recepcaoSortOrder, setRecepcaoSortOrder] = useState<"asc" | "desc">("asc");

  // Modais do Médico
  const [isCertificadoModalOpen, setIsCertificadoModalOpen] = useState(false);
  const [isClinicaSelectorOpen, setIsClinicaSelectorOpen] = useState(false);
  const [selectedAtendimento, setSelectedAtendimento] =
    useState<MedicoAtendimentoPendenteContract | null>(null);
  const [isAtendimentoModalOpen, setIsAtendimentoModalOpen] = useState(false);
  const [atendimentoSucessoUltimo, setAtendimentoSucessoUltimo] =
    useState<MedicoRealizarAtendimentoResponse | null>(null);

  // Aba Ativa da Clínica Credenciada: 'laudos' | 'recepcao' | 'repasses'
  const [clinicaAbaAtiva, setClinicaAbaAtiva] = useState<'laudos' | 'recepcao' | 'repasses'>('laudos');

  // Estados da Recepção / Check-in de Pacientes (Portal da Clínica)
  const [recepcaoItens, setRecepcaoItens] = useState<ParceiroRecepcaoItemContract[]>([]);
  const [recepcaoLoading, setRecepcaoLoading] = useState(false);
  const [recepcaoSubAba, setRecepcaoSubAba] = useState<'entrada' | 'concluidos'>('entrada');
  const [recepcaoSearch, setRecepcaoSearch] = useState('');
  const [recepcaoData, setRecepcaoData] = useState(() => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });
  const [recepcaoDataFim, setRecepcaoDataFim] = useState<string>('');
  const [recepcaoQuickDate, setRecepcaoQuickDate] = useState<'hoje' | '7dias' | 'mes' | 'todos'>('hoje');
  const [recepcaoFiltroStatus, setRecepcaoFiltroStatus] = useState<'todos' | 'presentes' | 'aguardando' | 'faltas'>('todos');
  const [checkinModalItem, setCheckinModalItem] = useState<ParceiroRecepcaoItemContract | null>(null);
  const [expandedRecepcaoIds, setExpandedRecepcaoIds] = useState<Set<number>>(new Set());
  const [togglingExameId, setTogglingExameId] = useState<number | null>(null);

  // Estados de Solicitar Acesso (Primeiro Acesso / Recuperar Senha)
  const [cnpjSolicitacao, setCnpjSolicitacao] = useState(maskCnpj(cnpjParam));
  const [solicitandoAcesso, setSolicitandoAcesso] = useState(false);

  // Estados de Contatos Cadastrados e Recuperação Direcionada
  const [contatosCadastrados, setContatosCadastrados] =
    useState<ParceiroContatosCadastradosResponse | null>(null);
  const [buscandoContatos, setBuscandoContatos] = useState(false);
  const [reenviandoEmailId, setReenviandoEmailId] = useState<number | null>(
    null,
  );
  const [sucessoReenvioMsg, setSucessoReenvioMsg] = useState<string | null>(
    null,
  );

  // Estados do Modal de Solicitação de Contato Ativo
  const [modalContatoAtivoOpen, setModalContatoAtivoOpen] = useState(false);
  const [contatoNome, setContatoNome] = useState("");
  const [contatoCargo, setContatoCargo] = useState("");
  const [contatoTelefone, setContatoTelefone] = useState("");
  const [contatoEmail, setContatoEmail] = useState("");
  const [contatoMensagem, setContatoMensagem] = useState("");
  const [enviandoContatoAtivo, setEnviandoContatoAtivo] = useState(false);
  const [erroContatoAtivo, setErroContatoAtivo] = useState<string | null>(null);
  const [contatoAtivoSucesso, setContatoAtivoSucesso] = useState<string | null>(
    null,
  );

  // Estados de Definir Senha (via link do e-mail com token)
  const [tokenAcesso, setTokenAcesso] = useState<string>(() => initialToken);
  const [tokenValidando, setTokenValidando] = useState<boolean>(() =>
    Boolean(initialToken),
  );
  const [tokenValidoInfo, setTokenValidoInfo] =
    useState<ParceiroValidarTokenAcessoResponse | null>(null);
  const [tokenInvalidoErro, setTokenInvalidoErro] = useState<string | null>(
    null,
  );
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState("");
  const [showNovaSenha, setShowNovaSenha] = useState(false);
  const [definindoSenha, setDefinindoSenha] = useState(false);

  // Estados do Modal de Alterar Senha (usuário logado)
  const [showAlterarSenhaModal, setShowAlterarSenhaModal] = useState(false);
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenhaModal, setNovaSenhaModal] = useState("");
  const [confirmarNovaSenhaModal, setConfirmarNovaSenhaModal] = useState("");
  const [showSenhaAtual, setShowSenhaAtual] = useState(false);
  const [showNovaSenhaModal, setShowNovaSenhaModal] = useState(false);
  const [alterandoSenha, setAlterandoSenha] = useState(false);
  const [erroAlterarSenha, setErroAlterarSenha] = useState<string | null>(null);

  // Estados do Modal de Upload de ASO (com Seleção de Aptidão)
  interface ModalUploadAsoData {
    agendamentoId: number;
    colaboradorId: number;
    colaboradorNome: string;
    colaboradorCpf?: string;
    empresaNome?: string;
    tipoExameOcupacional?: string;
    exameNome: string;
    isSubstituicao?: boolean;
  }
  interface ModalExamesPendentesData {
    colaboradorNome: string;
    exames: string[];
  }
  const [modalExamesPendentes, setModalExamesPendentes] =
    useState<ModalExamesPendentesData | null>(null);
  const [modalUploadAso, setModalUploadAso] =
    useState<ModalUploadAsoData | null>(null);
  const [modalAptidao, setModalAptidao] = useState<
    "apto" | "inapto" | "apto_com_restricao" | "inapto_temporario" | ""
  >("");
  const [modalArquivo, setModalArquivo] = useState<File | null>(null);
  const [modalObservacao, setModalObservacao] = useState("");
  const [modalErro, setModalErro] = useState<string | null>(null);

  // Estados do Modal de Reabertura de Atendimento Clínico (Auditoria SST)
  interface ModalReabrirData {
    agendamentoId: number;
    colaboradorNome?: string;
  }
  const [modalReabrir, setModalReabrir] = useState<ModalReabrirData | null>(null);
  const [modalReabrirMotivo, setModalReabrirMotivo] = useState("");
  const [modalReabrirLoading, setModalReabrirLoading] = useState(false);
  const [modalReabrirErro, setModalReabrirErro] = useState<string | null>(null);

  // Estado de Forçar Geração de ASO (agendamento_id em loading)
  const [gerandoAsoId, setGerandoAsoId] = useState<number | null>(null);

  // Estado Principal da Clínica Autenticada
  const [clinica, setClinica] = useState<ParceiroClinicaPerfilContract | null>(
    null,
  );
  const [pendencias, setPendencias] = useState<ParceiroPendenciaItemContract[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successToast, setSuccessToast] = useState<string | null>(null);
  const [conclusaoFeedback, setConclusaoFeedback] = useState<{
    colaboradorNome: string;
    mensagem: string;
  } | null>(null);

  // Estados de Paginação e Filtros
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusSla, setStatusSla] = useState<
    "todos" | "no_prazo" | "atrasado" | "aguardando_previsao"
  >("todos");
  const [dataInicio, setDataInicio] = useState<string>("");
  const [dataFim, setDataFim] = useState<string>("");
  const [quickDate, setQuickDate] = useState<"hoje" | "7dias" | "mes" | "todos">("todos");
  const [modoRecepcao] = useState<"pendentes" | "concluidos">("pendentes");
  const [ordenacao, setOrdenacao] = useState<"recentes" | "antigos">(
    "recentes",
  );
  const [meta, setMeta] = useState<ParceiroPaginationMeta | null>(null);
  const [resumoSla, setResumoSla] = useState<ParceiroResumoSla | null>(null);

  // Estado do Acordeon de Colaboradores (guarda IDs dos colaboradores expandidos)
  const [expandedColabs, setExpandedColabs] = useState<Record<number, boolean>>(
    {},
  );

  // Estado para Edição Inline de Previsão de Entrega (SLA)
  const [editingPrevisaoId, setEditingPrevisaoId] = useState<number | null>(
    null,
  );
  const [dataPrevisaoInput, setDataPrevisaoInput] = useState<string>("");
  const [salvandoPrevisao, setSalvandoPrevisao] = useState(false);

  // Estado de upload individual por exame (agendamento_id -> boolean de loading)
  const [uploadingExamId, setUploadingExamId] = useState<number | null>(null);

  // Estado para visualização de PDF
  const [visualizandoResultadoId, setVisualizandoResultadoId] = useState<
    number | null
  >(null);
  const [visualizandoAsoId, setVisualizandoAsoId] = useState<number | null>(null);
  const [downloadingKitId, setDownloadingKitId] = useState<number | null>(null);

  // Auto-dismiss do Toast e Feedback de Conclusão
  useEffect(() => {
    if (successToast) {
      const timer = setTimeout(() => setSuccessToast(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [successToast]);

  useEffect(() => {
    if (conclusaoFeedback) {
      const timer = setTimeout(() => setConclusaoFeedback(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [conclusaoFeedback]);

  /**
   * Carrega os dados do dashboard com os parâmetros e paginação atuais
   */
  const carregarDados = useCallback(
    async (
      clinicaAlvoId?: number,
      overrides?: {
        page?: number;
        limit?: number;
        search?: string;
        status_sla?: string;
        data_inicio?: string;
        data_fim?: string;
        ordenacao?: "recentes" | "antigos";
        modo?: "pendentes" | "concluidos";
      },
    ) => {
      const idParaBuscar = clinicaAlvoId ?? clinica?.id;
      if (!idParaBuscar) return;

      setLoading(true);
      setError(null);

      const params: ParceiroPendenciasQueryDto = {
        page: overrides?.page ?? page,
        limit: overrides?.limit ?? limit,
        search: overrides?.search !== undefined ? overrides.search : search,
        status_sla: overrides?.status_sla ?? statusSla,
        data_inicio:
          overrides?.data_inicio !== undefined
            ? overrides.data_inicio
            : dataInicio,
        data_fim:
          overrides?.data_fim !== undefined ? overrides.data_fim : dataFim,
        ordenacao: overrides?.ordenacao ?? ordenacao,
        modo: overrides?.modo ?? modoRecepcao,
      };

      try {
        const data: ParceiroDashboardResponse =
          await clinicaService.getPendencias(idParaBuscar, params);
        setClinica(data.clinica);
        setPendencias(data.pendencias);
        if (data.meta) setMeta(data.meta);
        if (data.resumo_sla) setResumoSla(data.resumo_sla);

        // Expandir todos os colaboradores da página atual por padrão
        const initialExpanded: Record<number, boolean> = {};
        data.pendencias.forEach((item) => {
          initialExpanded[item.colaborador_id] = true;
        });
        setExpandedColabs(initialExpanded);
      } catch (err: unknown) {
        const errorMsg =
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
          "Erro ao carregar atendimentos da clínica credenciada.";
        setError(errorMsg);
      } finally {
        setLoading(false);
      }
    },
    [
      clinica?.id,
      page,
      limit,
      search,
      statusSla,
      dataInicio,
      dataFim,
      ordenacao,
      modoRecepcao,
    ],
  );

  // Detecção de link de Primeiro Acesso / Definir Senha via token
  useEffect(() => {
    if (initialToken) {
      if (isMedicoReset) {
        if (!emailFromQuery) {
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setTokenInvalidoErro('Link de recuperação do médico incompleto. O e-mail não foi informado na URL.');
          setTokenValidando(false);
          return;
        }
        clinicaService
          .validarTokenResetMedico(initialToken, emailFromQuery)
          .then((data) => {
            setTokenValidoInfo({
              valid: true,
              clinica_nome: data.name || data.email,
              clinica_cnpj: '',
            });
          })
          .catch((err) => {
            const msg =
              (err as { response?: { data?: { message?: string } } })?.response?.data
                ?.message || 'Link de recuperação do médico inválido ou expirado.';
            setTokenInvalidoErro(msg);
          })
          .finally(() => {
            setTokenValidando(false);
          });
      } else {
        clinicaService
          .validarTokenAcesso(initialToken)
          .then((data) => {
            setTokenValidoInfo(data);
          })
          .catch((err) => {
            const msg =
              (err as { response?: { data?: { message?: string } } })?.response?.data
                ?.message || 'Link ou token de acesso inválido ou expirado.';
            setTokenInvalidoErro(msg);
          })
          .finally(() => {
            setTokenValidando(false);
          });
      }
    }
  }, [initialToken, isMedicoReset, emailFromQuery]);

  /**
   * Carrega os atendimentos clínicos pendentes para a clínica selecionada pelo médico
   */
  const carregarAtendimentosMedico = useCallback(
    async (clinicaId?: number, aba?: 'fila' | 'aguardando_exames' | 'aguardando_laudo' | 'realizados') => {
      const targetId =
        clinicaId || selectedClinica?.id || getSelectedClinicaId();
      if (!targetId) return;
      setMedicoAtendimentosLoading(true);
      try {
        const targetAba = aba || medicoAbaAtiva;
        const payload: Record<string, unknown> = {
          clinica_id: targetId,
          aba: targetAba,
          limit: 100,
        };
        if (medicoDataFiltro) {
          payload.data_inicio = medicoDataFiltro;
          payload.data_fim = medicoDataFiltro;
        }

        const res = await clinicaService.getAtendimentos(payload);
        setMedicoAtendimentos(res.data || []);
        if (res.counts) {
          setMedicoAbaCounts(res.counts);
        }
      } catch (err: unknown) {
        console.error("Erro ao carregar atendimentos do médico:", err);
      } finally {
        setMedicoAtendimentosLoading(false);
      }
    },
    [selectedClinica?.id, medicoAbaAtiva, medicoDataFiltro, setMedicoAtendimentos, setMedicoAtendimentosLoading, setMedicoAbaCounts],
  );

  /**
   * Carrega os agendamentos do dia na recepção da clínica
   */
  const carregarRecepcao = useCallback(
    async (
      dataStr?: string,
      searchStr?: string,
      modoOverride?: 'entrada' | 'concluidos',
      dataFimOverride?: string,
      silent = false,
    ) => {
      if (!clinica?.id) return;
      if (!silent) setRecepcaoLoading(true);
      try {
        const targetData = dataStr !== undefined ? dataStr : recepcaoData;
        const targetDataFim = dataFimOverride !== undefined ? dataFimOverride : recepcaoDataFim;
        const targetSearch = searchStr !== undefined ? searchStr : recepcaoSearch;
        const targetModo = modoOverride !== undefined ? modoOverride : recepcaoSubAba;

        const itens = await clinicaService.getRecepcaoHoje({
          data: targetData || undefined,
          data_fim: targetDataFim || undefined,
          search: targetSearch || undefined,
          clinica_id: clinica.id,
          modo: targetModo,
        });
        setRecepcaoItens(itens);
      } catch (err: unknown) {
        console.error('Erro ao carregar recepção da clínica:', err);
      } finally {
        if (!silent) setRecepcaoLoading(false);
      }
    },
    [
      clinica,
      recepcaoData,
      recepcaoDataFim,
      recepcaoSearch,
      recepcaoSubAba,
      setRecepcaoLoading,
      setRecepcaoItens,
    ],
  );

  const toggleRecepcaoExpand = (id: number) => {
    setExpandedRecepcaoIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleToggleExameRealizado = async (
    agendamentoId: number,
    realizadoAtual: boolean,
    exameId?: number,
  ) => {
    const novoStatus = !realizadoAtual;
    setTogglingExameId(exameId || agendamentoId);

    // Atualização otimista no estado local da recepção para resposta imediata sem desmontar tabela
    setRecepcaoItens((prev) =>
      prev.map((item) => {
        const matchesAgendamento =
          item.agendamento_id === agendamentoId ||
          (item.exames || []).some(
            (ex) => ex.agendamento_id === agendamentoId || (exameId && ex.id === exameId),
          );

        if (matchesAgendamento) {
          const examesAtualizados = (item.exames || []).map((ex) => {
            const isMatch = exameId
              ? ex.id === exameId
              : ex.agendamento_id === agendamentoId && ex.is_clinico;
            if (isMatch) {
              return {
                ...ex,
                realizado: novoStatus,
                status: (novoStatus ? 'REALIZADO' : 'PENDENTE') as 'REALIZADO' | 'PENDENTE',
                data_realizacao: novoStatus
                  ? new Date().toISOString().split('T')[0]
                  : null,
              };
            }
            return ex;
          });
          const concluidosCount = examesAtualizados.filter(
            (e) => e.status === 'CONCLUIDO' || e.realizado,
          ).length;
          return {
            ...item,
            exames: examesAtualizados,
            exames_concluidos_count: concluidosCount,
          };
        }
        return item;
      }),
    );

    try {
      const res = await clinicaService.marcarExameRealizado(
        {
          agendamento_id: agendamentoId,
          exame_id: exameId,
          realizado: novoStatus,
        },
        clinica?.id,
      );
      setSuccessToast(res.message);
      // Sincronização em background silenciosa sem disparar spinner que colapsa altura da página
      await carregarRecepcao(undefined, undefined, undefined, undefined, true);
    } catch (err: unknown) {
      // Reverter estado otimista em caso de erro na API
      setRecepcaoItens((prev) =>
        prev.map((item) => {
          const matchesAgendamento =
            item.agendamento_id === agendamentoId ||
            (item.exames || []).some(
              (ex) => ex.agendamento_id === agendamentoId || (exameId && ex.id === exameId),
            );

          if (matchesAgendamento) {
            const examesRevertidos = (item.exames || []).map((ex) => {
              const isMatch = exameId
                ? ex.id === exameId || ex.agendamento_id === exameId
                : ex.agendamento_id === agendamentoId || ex.is_clinico;
              if (isMatch) {
                return {
                  ...ex,
                  realizado: realizadoAtual,
                  status: (realizadoAtual ? 'REALIZADO' : 'PENDENTE') as 'REALIZADO' | 'PENDENTE',
                };
              }
              return ex;
            });
            const concluidosCount = examesRevertidos.filter(
              (e) => e.status === 'CONCLUIDO' || e.realizado,
            ).length;
            return {
              ...item,
              exames: examesRevertidos,
              exames_concluidos_count: concluidosCount,
            };
          }
          return item;
        }),
      );
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Erro ao alterar status do exame.';
      setError(msg);
    } finally {
      setTogglingExameId(null);
    }
  };

  const handlePrevisaoExameRecepcao = async (
    agendamentoId: number,
    novaData: string,
  ) => {
    if (!novaData) return;
    try {
      await clinicaService.definirPrevisaoResultado([agendamentoId], novaData);
      setSuccessToast('Data prevista de entrega alinhada com sucesso!');
      carregarRecepcao();
      carregarDados();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Erro ao definir previsão de entrega.';
      setError(msg);
    }
  };

  const [registrandoFaltaId, setRegistrandoFaltaId] = useState<number | null>(null);

  const handleRegistrarFalta = async (agendamentoId: number, colabNome?: string) => {
    const confirmou = window.confirm(
      `Confirma o registro de FALTA (No-Show) para o colaborador "${colabNome || 'selecionado'}"?\n\nEsta ação registrará o não comparecimento e encerrará o agendamento.`,
    );
    if (!confirmou) return;

    try {
      setRegistrandoFaltaId(agendamentoId);
      await clinicaService.atualizarStatusAgendamento(
        agendamentoId,
        'faltou',
        'Colaborador não compareceu na clínica credenciada (No-Show registrado na recepção).',
      );
      setSuccessToast('Falta (No-Show) registrada com sucesso!');
      carregarRecepcao();
      carregarDados();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || 'Erro ao registrar falta.';
      setError(msg);
    } finally {
      setRegistrandoFaltaId(null);
    }
  };

  // Redireciona para / se já estiver autenticado ou se estiver na rota /definir-senha sem token
  useEffect(() => {
    if (initialToken || isDefinirSenhaRoute) {
      if (!initialToken && !tokenAcesso) {
        navigate('/', { replace: true });
      }
      return;
    }
    if ((clinica || medicoPerfil) && (location.pathname !== '/' || location.search)) {
      navigate('/', { replace: true });
    }
  }, [
    clinica,
    medicoPerfil,
    isDefinirSenhaRoute,
    initialToken,
    tokenAcesso,
    location.pathname,
    location.search,
    navigate,
  ]);

  // Checagem de Sessão Ativa ao carregar a página
  useEffect(() => {
    if (initialToken || isDefinirSenhaRoute) {
      return; // Usuário veio via link de e-mail para cadastrar/redefinir senha
    }
    const existingToken = getAuthToken();
    if (existingToken && !clinica && !medicoPerfil) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLoading(true);
      clinicaService
        .getMe()
        .then((perfil) => {
          if ("role" in perfil && perfil.role === "medico") {
            setMedicoPerfil(perfil.medico);
            setMedicoClinicas(perfil.clinicas || []);
            localStorage.setItem("clinics_tipo_acesso", "MEDICO");
            setTipoAcesso("MEDICO");
            const savedClinicaId = getSelectedClinicaId();
            const found = (perfil.clinicas || []).find(
              (c: MedicoClinicaVinculadaContract) => c.id === savedClinicaId,
            );
            if (found) {
              setSelectedClinica(found);
              carregarAtendimentosMedico(found.id);
            } else if (perfil.clinicas && perfil.clinicas.length === 1) {
              setSelectedClinica(perfil.clinicas[0]);
              setSelectedClinicaId(perfil.clinicas[0].id);
              carregarAtendimentosMedico(perfil.clinicas[0].id);
            } else if (perfil.clinicas && perfil.clinicas.length > 1) {
              setIsClinicaSelectorOpen(true);
            }
          } else if ("id" in perfil) {
            setClinica(perfil);
            localStorage.setItem("clinics_tipo_acesso", "CLINICA");
            setTipoAcesso("CLINICA");
            carregarDados(perfil.id);
          }
          if (location.pathname !== "/" || location.search) {
            navigate("/", { replace: true });
          }
        })
        .catch(() => {
          clinicaService.logout();
          setClinica(null);
          setMedicoPerfil(null);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [clinica, medicoPerfil, carregarDados, carregarAtendimentosMedico, location.pathname, location.search, navigate, initialToken, isDefinirSenhaRoute]);

  // Recarrega lista de atendimentos quando a aba do médico é alterada
  useEffect(() => {
    if (medicoPerfil && selectedClinica?.id) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      carregarAtendimentosMedico(selectedClinica.id, medicoAbaAtiva);
    }
  }, [medicoAbaAtiva, medicoPerfil, selectedClinica?.id, medicoDataFiltro, carregarAtendimentosMedico]);

  // Listener para evento 'clinics:unauthorized' (token expirado)
  useEffect(() => {
    const handleUnauthorized = () => {
      setClinica(null);
      setMedicoPerfil(null);
      setMedicoClinicas([]);
      setSelectedClinica(null);
      setMedicoAtendimentos([]);
      setPendencias([]);
      setMeta(null);
      setResumoSla(null);
      setError("Sua sessão expirou. Por favor, realize o login novamente.");
      setAuthMode("login");
    };
    window.addEventListener("clinics:unauthorized", handleUnauthorized);
    return () =>
      window.removeEventListener("clinics:unauthorized", handleUnauthorized);
  }, []);

  /**
   * Realizar Login do Médico com E-mail e Senha (e 2FA)
   */
  const handleDoctorLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!medicoEmail || !medicoSenha) {
      setError("Informe seu e-mail e senha.");
      return;
    }
    setLoginLoading(true);
    setError(null);
    try {
      const resp = await clinicaService.loginMedico({
        email: medicoEmail,
        password: medicoSenha,
        code_2fa: medicoRequires2fa ? medicoCodigo2fa : undefined,
      });

      if (resp.requires_2fa) {
        setMedicoRequires2fa(true);
        setSuccessToast(
          "Código de verificação 2FA enviado para seu e-mail! Insira abaixo para concluir o acesso.",
        );
        return;
      }

      if (resp.token && resp.medico) {
        setAuthToken(resp.token);
        localStorage.setItem("clinics_tipo_acesso", "MEDICO");
        setTipoAcesso("MEDICO");
        setMedicoPerfil(resp.medico);
        setMedicoClinicas(resp.clinicas || []);
        setSuccessToast(`Bem-vindo, Dr(a). ${resp.medico.nome}!`);

        if (resp.clinicas && resp.clinicas.length === 1) {
          setSelectedClinica(resp.clinicas[0]);
          setSelectedClinicaId(resp.clinicas[0].id);
          carregarAtendimentosMedico(resp.clinicas[0].id);
        } else if (resp.clinicas && resp.clinicas.length > 1) {
          const savedClinicaId = getSelectedClinicaId();
          const found = resp.clinicas.find((c) => c.id === savedClinicaId);
          if (found) {
            setSelectedClinica(found);
            carregarAtendimentosMedico(found.id);
          } else {
            setIsClinicaSelectorOpen(true);
          }
        }

        if (location.pathname !== "/" || location.search) {
          navigate("/", { replace: true });
        }
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      setError(
        msg || "Falha na autenticação do médico. Verifique seu e-mail e senha.",
      );
    } finally {
      setLoginLoading(false);
    }
  };

  /**
   * Solicitar recuperação de senha para médico
   */
  const handleDoctorForgot = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!medicoForgotEmail) {
      setError("Informe o e-mail cadastrado do médico.");
      return;
    }
    setMedicoForgotLoading(true);
    setMedicoForgotSuccess(null);
    setError(null);
    try {
      const res = await clinicaService.forgotPasswordMedico(medicoForgotEmail);
      setMedicoForgotSuccess(
        res.message ||
          "Instruções para redefinição de senha enviadas com sucesso!",
      );
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;
      setError(msg || "Falha ao solicitar redefinição de senha.");
    } finally {
      setMedicoForgotLoading(false);
    }
  };

  /**
   * Logout do médico
   */
  const handleDoctorLogout = () => {
    clinicaService.logout();
    setClinica(null);
    setMedicoPerfil(null);
    setMedicoClinicas([]);
    setSelectedClinica(null);
    setMedicoAtendimentos([]);
    setTipoAcesso("MEDICO");
    setAuthMode("login");
    navigate("/", { replace: true });
  };

  /**
   * Realizar Login com CNPJ e Senha
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCnpj = cnpjInput.replace(/\D/g, "");
    if (cleanCnpj.length !== 14) {
      setError("Por favor, informe o CNPJ completo com 14 dígitos.");
      return;
    }
    if (!senhaInput) {
      setError("Por favor, informe a senha da clínica.");
      return;
    }

    setLoginLoading(true);
    setError(null);
    setSenhaNaoDefinida(false);

    try {
      const response = await clinicaService.login({
        cnpj: cleanCnpj,
        senha: senhaInput,
      });

      localStorage.setItem("clinics_tipo_acesso", "CLINICA");
      setTipoAcesso("CLINICA");
      setClinica(response.clinica);
      setSuccessToast(`Bem-vindo, ${response.clinica.nome}!`);
      carregarDados(response.clinica.id);

      if (location.pathname !== "/" || location.search) {
        navigate("/", { replace: true });
      }
    } catch (err: unknown) {
      const respData = (
        err as { response?: { data?: { message?: string; code?: string } } }
      )?.response?.data;
      if (
        respData?.code === "SENHA_NAO_DEFINIDA" ||
        respData?.message?.includes("ainda não possui senha")
      ) {
        setSenhaNaoDefinida(true);
        setCnpjSolicitacao(cnpjInput);
      }
      setError(
        respData?.message ||
          "Falha na autenticação. Verifique seu CNPJ e senha.",
      );
    } finally {
      setLoginLoading(false);
    }
  };

  /**
   * Solicitar Primeiro Acesso ou Recuperação de Senha por E-mail
   */
  const handleSolicitarAcesso = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCnpj = cnpjSolicitacao.replace(/\D/g, "");
    if (cleanCnpj.length !== 14) {
      setError("Por favor, informe o CNPJ completo com 14 dígitos.");
      return;
    }

    setSolicitandoAcesso(true);
    setError(null);
    setSucessoReenvioMsg(null);

    try {
      const res = await clinicaService.solicitarAcesso({ cnpj: cleanCnpj });
      setSucessoReenvioMsg(res.message);
      setSuccessToast(res.message);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Erro ao solicitar acesso da clínica credenciada.";
      setError(msg);
    } finally {
      setSolicitandoAcesso(false);
    }
  };

  /**
   * Consultar e-mails cadastrados (mascarados) para o CNPJ
   */
  const handleBuscarContatos = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const cleanCnpj = cnpjSolicitacao.replace(/\D/g, "");
    if (cleanCnpj.length !== 14) {
      setError("Por favor, informe o CNPJ completo com 14 dígitos.");
      return;
    }

    setBuscandoContatos(true);
    setError(null);
    setSucessoReenvioMsg(null);

    try {
      const res = await clinicaService.getContatosCadastrados(cleanCnpj);
      setContatosCadastrados(res);
      if (res.contatos.length === 0) {
        setError(
          "Nenhum e-mail de contato localizado para esta unidade credenciada. Solicite um contato ativo abaixo.",
        );
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Clínica não encontrada ou erro ao consultar contatos.";
      setError(msg);
      setContatosCadastrados(null);
    } finally {
      setBuscandoContatos(false);
    }
  };

  /**
   * Reenviar link direcionado para um e-mail específico cadastrado
   */
  const handleReenviarDirecionado = async (emailId: number) => {
    const cleanCnpj = cnpjSolicitacao.replace(/\D/g, "");
    if (cleanCnpj.length !== 14) {
      setError("Informe o CNPJ com 14 dígitos.");
      return;
    }

    setReenviandoEmailId(emailId);
    setError(null);
    setSucessoReenvioMsg(null);

    try {
      const res = await clinicaService.solicitarAcessoDirecionado({
        cnpj: cleanCnpj,
        email_id: emailId,
      });
      setSucessoReenvioMsg(res.message);
      setSuccessToast(res.message);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Falha ao reenviar o link de acesso.";
      setError(msg);
    } finally {
      setReenviandoEmailId(null);
    }
  };

  /**
   * Solicitar contato ativo de suporte da equipe SST Exames
   */
  const handleSolicitarContatoAtivo = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanCnpj = (cnpjSolicitacao || cnpjInput).replace(/\D/g, "");
    if (cleanCnpj.length !== 14) {
      setErroContatoAtivo(
        "Informe o CNPJ da clínica credenciada (14 dígitos).",
      );
      return;
    }
    if (
      !contatoNome.trim() ||
      !contatoCargo.trim() ||
      !contatoTelefone.trim() ||
      !contatoEmail.trim()
    ) {
      setErroContatoAtivo(
        "Preencha todos os campos obrigatórios assinalados com (*).",
      );
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contatoEmail.trim())) {
      setErroContatoAtivo(
        "Informe um endereço de e-mail corporativo válido (exemplo: contato@clinica.com.br).",
      );
      return;
    }

    setEnviandoContatoAtivo(true);
    setErroContatoAtivo(null);

    try {
      const res = await clinicaService.solicitarContatoAtivo({
        cnpj: cleanCnpj,
        nome: contatoNome.trim(),
        cargo: contatoCargo.trim(),
        telefone: contatoTelefone.trim(),
        email: contatoEmail.trim(),
        mensagem: contatoMensagem.trim() || undefined,
      });
      setContatoAtivoSucesso(res.message);
      setSuccessToast(res.message);
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        "Falha ao registrar solicitação de contato ativo. Tente novamente mais tarde.";
      setErroContatoAtivo(msg);
    } finally {
      setEnviandoContatoAtivo(false);
    }
  };

  /**
   * Definir Senha através do Token recebido por E-mail
   */
  const handleDefinirSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    if (novaSenha.length < 6) {
      setError("A nova senha deve possuir no mínimo 6 caracteres.");
      return;
    }
    if (novaSenha !== confirmarNovaSenha) {
      setError("A confirmação de senha não confere com a nova senha digitada.");
      return;
    }

    setDefinindoSenha(true);
    setError(null);

    try {
      if (isMedicoReset) {
        const targetEmail = emailFromQuery || '';
        await clinicaService.resetPasswordMedico({
          token: tokenAcesso || initialToken,
          email: targetEmail,
          nova_senha: novaSenha,
        });
        setSuccessToast('Senha do médico redefinida com sucesso! Acesse o portal com seu e-mail e nova senha.');
        setTipoAcesso('MEDICO');
        setMedicoEmail(targetEmail);
        localStorage.setItem('clinics_tipo_acesso', 'MEDICO');
      } else {
        await clinicaService.definirSenha({
          token: tokenAcesso || initialToken,
          nova_senha: novaSenha,
        });
        if (tokenValidoInfo?.clinica_cnpj) {
          setCnpjInput(maskCnpj(tokenValidoInfo.clinica_cnpj));
        }
        setSuccessToast('Senha definida com sucesso! Agora você já pode entrar no portal com seu CNPJ e senha.');
        setTipoAcesso('CLINICA');
        localStorage.setItem('clinics_tipo_acesso', 'CLINICA');
      }

      setAuthMode("login");
      setNovaSenha("");
      setConfirmarNovaSenha("");
      setTokenAcesso("");
      navigate("/", { replace: true });
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Erro ao definir senha. O link pode ter expirado.";
      setError(msg);
    } finally {
      setDefinindoSenha(false);
    }
  };

  /**
   * Alterar Senha (quando já logado)
   */
  const handleAlterarSenha = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!senhaAtual) {
      setErroAlterarSenha("Informe a senha atual.");
      return;
    }
    if (novaSenhaModal.length < 6) {
      setErroAlterarSenha("A nova senha deve possuir no mínimo 6 caracteres.");
      return;
    }
    if (novaSenhaModal !== confirmarNovaSenhaModal) {
      setErroAlterarSenha(
        "A confirmação de senha não confere com a nova senha.",
      );
      return;
    }

    setAlterandoSenha(true);
    setErroAlterarSenha(null);

    try {
      await clinicaService.alterarSenha({
        senha_atual: senhaAtual,
        nova_senha: novaSenhaModal,
      });

      setShowAlterarSenhaModal(false);
      setSenhaAtual("");
      setNovaSenhaModal("");
      setConfirmarNovaSenhaModal("");
      setSuccessToast("Senha da clínica credenciada alterada com sucesso!");
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message ||
        "Erro ao alterar senha. Verifique a senha atual informada.";
      setErroAlterarSenha(msg);
    } finally {
      setAlterandoSenha(false);
    }
  };

  /**
   * Sair do Portal
   */
  const handleLogout = () => {
    clinicaService.logout();
    setClinica(null);
    setMedicoPerfil(null);
    setMedicoClinicas([]);
    setSelectedClinica(null);
    setMedicoAtendimentos([]);
    setPendencias([]);
    setMeta(null);
    setResumoSla(null);
    setSenhaInput("");
    setSenhaNaoDefinida(false);
    setAuthMode("login");
    navigate("/", { replace: true });
  };

  /**
   * Recarregar pendências da clínica ativa
   */
  const recarregarPendencias = () => {
    carregarDados();
    setSuccessToast("Lista de atendimentos atualizada!");
  };

  /**
   * Alternar expansão de colaborador no acordeon
   */
  const toggleColaboradorAccordion = (colaboradorId: number) => {
    setExpandedColabs((prev) => ({
      ...prev,
      [colaboradorId]: !prev[colaboradorId],
    }));
  };

  /**
   * Selecionar filtro de SLA (Cards)
   */
  const handleSelectSlaFilter = (
    tab: "todos" | "no_prazo" | "atrasado" | "aguardando_previsao",
  ) => {
    setStatusSla(tab);
    setPage(1);
    carregarDados(undefined, { status_sla: tab, page: 1 });
  };

  // Debounce para busca textual automática em tempo real ao digitar
  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== search && clinica?.id) {
        setSearch(trimmed);
        setPage(1);
        carregarDados(undefined, { search: trimmed, page: 1 });
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [searchInput, search, clinica?.id, carregarDados]);

  /**
   * Submeter busca textual
   */
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    setSearch(trimmed);
    setPage(1);
    carregarDados(undefined, { search: trimmed, page: 1 });
  };

  /**
   * Atualizar input de busca e limpar imediatamente se esvaziado
   */
  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setSearchInput(val);
    if (!val.trim() && search) {
      setSearch("");
      setPage(1);
      carregarDados(undefined, { search: "", page: 1 });
    }
  };

  /**
   * Limpar busca textual
   */
  const handleClearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    carregarDados(undefined, { search: "", page: 1 });
  };

  /**
   * Aplicar filtro de datas
   */
  const handleApplyDateFilter = () => {
    setPage(1);
    carregarDados(undefined, {
      data_inicio: dataInicio,
      data_fim: dataFim,
      page: 1,
    });
  };

  /**
   * Atalhos de data rápida para pendências
   */
  const handleSetQuickDate = (mode: 'todos' | 'hoje' | '7dias' | 'mes') => {
    const d = new Date();
    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    let ini = "";
    let fim = "";

    if (mode === 'hoje') {
      ini = today;
      fim = today;
    } else if (mode === '7dias') {
      const d7 = new Date();
      d7.setDate(d.getDate() - 7);
      ini = `${d7.getFullYear()}-${String(d7.getMonth() + 1).padStart(2, '0')}-${String(d7.getDate()).padStart(2, '0')}`;
      fim = today;
    } else if (mode === 'mes') {
      ini = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      fim = today;
    }

    setQuickDate(mode);
    setDataInicio(ini);
    setDataFim(fim);
    setPage(1);
    carregarDados(undefined, { data_inicio: ini, data_fim: fim, page: 1 });
  };

  /**
   * Alterar ordenação por data de agendamento
   */
  const handleOrdenacaoChange = (novaOrdenacao: "recentes" | "antigos") => {
    setOrdenacao(novaOrdenacao);
    setPage(1);
    carregarDados(undefined, { ordenacao: novaOrdenacao, page: 1 });
  };

  /**
   * Limpar todos os filtros aplicados
   */
  const handleClearAllFilters = () => {
    setSearchInput("");
    setSearch("");
    setStatusSla("todos");
    setDataInicio("");
    setDataFim("");
    setQuickDate("todos");
    setOrdenacao("recentes");
    setPage(1);
    carregarDados(undefined, {
      search: "",
      status_sla: "todos",
      data_inicio: "",
      data_fim: "",
      ordenacao: "recentes",
      page: 1,
    });
  };

  /**
   * Alterar quantidade de registros por página
   */
  const handleChangeLimit = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
    carregarDados(undefined, { limit: newLimit, page: 1 });
  };

  /**
   * Navegar para página
   */
  const handleGoToPage = (newPage: number) => {
    if (newPage < 1 || (meta && newPage > meta.total_pages)) return;
    setPage(newPage);
    carregarDados(undefined, { page: newPage });
  };

  /**
   * Salvar Previsão de Entrega (SLA)
   */
  const handleSalvarPrevisao = async (agendamentoId: number) => {
    if (!dataPrevisaoInput) {
      setError("Informe a data de previsão de entrega.");
      return;
    }

    setSalvandoPrevisao(true);
    try {
      await clinicaService.definirPrevisaoResultado(
        [agendamentoId],
        dataPrevisaoInput,
      );

      setEditingPrevisaoId(null);
      setDataPrevisaoInput("");
      setSuccessToast("Data de previsão de entrega alinhada com sucesso!");
      carregarDados();
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Erro ao definir previsão de entrega.";
      setError(msg);
    } finally {
      setSalvandoPrevisao(false);
    }
  };

  /**
   * Upload direto de arquivo PDF na linha do exame
   */
  const handleUploadExamePdf = async (
    agendamentoId: number,
    colaboradorId: number,
    colaboradorNome: string,
    file: File,
    valor = "laudo_emitido",
    agendamentoItemId?: number,
  ) => {
    if (file.type !== "application/pdf") {
      alert("Por favor, anexe um arquivo no formato PDF.");
      return;
    }

    setUploadingExamId(agendamentoId);
    setError(null);

    try {
      const response = await clinicaService.uploadLaudoExame(
        agendamentoId,
        file,
        undefined,
        valor,
        agendamentoItemId,
      );

      if (response.success) {
        if (response.todos_exames_concluidos) {
          setSuccessToast(
            `🎉 Todos os exames de ${colaboradorNome} foram laudados com sucesso! Colaborador concluído.`,
          );
          setConclusaoFeedback({
            colaboradorNome,
            mensagem: `Todos os exames de ${colaboradorNome} foram laudados e encaminhados para a fila de auditoria da SST Exames!`,
          });
        } else {
          setSuccessToast(
            `✅ Laudo anexado com sucesso para ${colaboradorNome}!`,
          );
        }
        carregarDados();
        carregarRecepcao();
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Falha ao enviar arquivo de laudo. Tente novamente.";
      setError(msg);
    } finally {
      setUploadingExamId(null);
    }
  };

  /**
   * Visualizar arquivo de laudo/ASO anexado com autenticação
   * Servido diretamente pela aplicação via Blob (sem expor URLs externas)
   */
  const handleVisualizarPdf = async (resultadoId?: number) => {
    if (!resultadoId) {
      alert("Identificador do laudo não encontrado.");
      return;
    }

    setVisualizandoResultadoId(resultadoId);
    try {
      const blob = await clinicaService.downloadLaudo(resultadoId);
      const blobUrl = window.URL.createObjectURL(blob);
      const opened = window.open(blobUrl, "_blank");
      if (!opened) {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `laudo_${resultadoId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (e) {
      console.error("Erro ao carregar laudo:", e);
      alert("Não foi possível carregar o arquivo do laudo no momento.");
    } finally {
      setVisualizandoResultadoId(null);
    }
  };

  /**
   * Baixa e abre o ASO em nova aba do navegador via streaming
   */
  const handleDownloadAso = async (agendamentoId: number) => {
    setVisualizandoAsoId(agendamentoId);
    try {
      const blob = await clinicaService.downloadAso(agendamentoId);
      const blobUrl = window.URL.createObjectURL(blob);
      const opened = window.open(blobUrl, "_blank");
      if (!opened) {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `aso_${agendamentoId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      console.error("Erro ao carregar ASO:", e);
      setError("Não foi possível carregar o arquivo do ASO no momento.");
    } finally {
      setVisualizandoAsoId(null);
    }
  };

  /**
   * Baixa e abre o Kit de Exame (Guia + Ficha + 3 Vias do ASO)
   */
  const handleDownloadKit = async (pedidoId: number, colaboradorId: number, agendamentoId?: number) => {
    try {
      if (agendamentoId) {
        setDownloadingKitId(agendamentoId);
      }
      const codigo = btoa(`${pedidoId}_${colaboradorId}`);
      const blob = await clinicaService.downloadKitExame(codigo);
      const blobUrl = window.URL.createObjectURL(blob);
      const opened = window.open(blobUrl, "_blank");
      if (!opened) {
        const a = document.createElement("a");
        a.href = blobUrl;
        a.download = `kit_${codigo}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (e) {
      console.error("Erro ao baixar Kit de Exame:", e);
      setError("Não foi possível carregar o arquivo do Kit de Exame no momento.");
    } finally {
      setDownloadingKitId(null);
    }
  };

  const handleAbrirModalReabrir = (
    agendamentoId: number,
    colaboradorNome?: string,
  ) => {
    setModalReabrir({ agendamentoId, colaboradorNome });
    setModalReabrirMotivo("");
    setModalReabrirErro(null);
  };

  const handleConfirmarReabertura = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalReabrir) return;

    if (!modalReabrirMotivo.trim() || modalReabrirMotivo.trim().length < 5) {
      setModalReabrirErro(
        "Por favor, informe uma justificativa válida com no mínimo 5 caracteres para fins de auditoria.",
      );
      return;
    }

    try {
      setModalReabrirLoading(true);
      setModalReabrirErro(null);
      await clinicaService.reabrirAtendimento(
        modalReabrir.agendamentoId,
        modalReabrirMotivo.trim(),
      );
      setSuccessToast(
        "Atendimento reaberto com sucesso! O paciente retornou para aguardando laudo.",
      );
      setModalReabrir(null);
      setIsAtendimentoModalOpen(false);
      setSelectedAtendimento(null);
      setMedicoAbaAtiva("aguardando_laudo");
      carregarAtendimentosMedico(undefined, "aguardando_laudo");
    } catch (err: unknown) {
      const errResponse = err as {
        response?: { data?: { message?: string } };
      };
      setModalReabrirErro(
        errResponse?.response?.data?.message || "Erro ao reabrir atendimento.",
      );
    } finally {
      setModalReabrirLoading(false);
    }
  };

  const handleForcarGerarAso = async (agendamentoId: number) => {
    try {
      setGerandoAsoId(agendamentoId);
      setError(null);
      const res = await clinicaService.forcarGerarAso(agendamentoId);
      setSuccessToast(
        res.message || "ASO gerado e assinado digitalmente com sucesso!",
      );
      carregarAtendimentosMedico();
    } catch (err: unknown) {
      const errResponse = err as {
        response?: { data?: { message?: string } };
      };
      setError(
        errResponse?.response?.data?.message ||
          "Falha ao gerar e assinar ASO. Verifique se o certificado A1 está ativo e tente novamente.",
      );
    } finally {
      setGerandoAsoId(null);
    }
  };

  /**
   * Renderiza o Modal do Sistema de Reabertura de Atendimento Clínico
   */
  const renderModalReabrir = () => {
    if (!modalReabrir) return null;
    return (
      <div className="fixed inset-0 z-[70] bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
        <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl border border-slate-100 space-y-5 animate-in zoom-in-95 duration-200">
          {/* Header */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-rose-50 text-rose-600 rounded-2xl flex items-center justify-center shrink-0 border border-rose-100">
                <RotateCcw className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Reabrir Atendimento Clínico
                </h3>
                <p className="text-xs text-slate-500">
                  Gera uma nova revisão auditada do ASO
                </p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => !modalReabrirLoading && setModalReabrir(null)}
              disabled={modalReabrirLoading}
              className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Warning Callout */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3.5 flex items-start gap-3 text-xs text-amber-800">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <p className="font-semibold text-amber-900">Atenção para Auditoria Técnica</p>
              <p className="text-amber-700 leading-relaxed">
                A reabertura cancela a versão atual do ASO e retorna o paciente para a fila de atendimento clínico. A justificativa será registrada no histórico permanente de auditoria.
              </p>
            </div>
          </div>

          {/* Colaborador Info */}
          {modalReabrir.colaboradorNome && (
            <div className="bg-slate-50 rounded-2xl p-3 border border-slate-200 flex items-center justify-between text-xs">
              <span className="text-slate-500 font-medium">Colaborador:</span>
              <span className="font-bold text-slate-800">{modalReabrir.colaboradorNome}</span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleConfirmarReabertura} className="space-y-4">
            <FormField
              label="Motivo / Justificativa da Reabertura"
              required
              htmlFor="motivo-reabertura"
            >
              <textarea
                id="motivo-reabertura"
                rows={4}
                value={modalReabrirMotivo}
                onChange={(e) => setModalReabrirMotivo(e.target.value)}
                placeholder="Informe detalhadamente a justificativa para a reabertura do atendimento (mínimo de 5 caracteres)..."
                className="w-full text-xs text-slate-800 bg-slate-50 border border-slate-200 rounded-2xl p-3 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all resize-none"
                disabled={modalReabrirLoading}
                required
              />
              <div className="flex justify-between items-center mt-1 text-[11px] text-slate-400">
                <span>Mínimo de 5 caracteres</span>
                <span className={modalReabrirMotivo.trim().length >= 5 ? "text-emerald-600 font-medium" : ""}>
                  {modalReabrirMotivo.length} caracteres
                </span>
              </div>
            </FormField>

            {/* Erro */}
            {modalReabrirErro && (
              <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-700 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{modalReabrirErro}</span>
              </div>
            )}

            {/* Ações */}
            <div className="flex items-center justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setModalReabrir(null)}
                disabled={modalReabrirLoading}
                className="px-4 py-2.5 text-xs font-semibold text-primary-700 bg-white border border-primary-300 hover:bg-primary-50 hover:border-primary-400 rounded-xl transition-colors cursor-pointer"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={modalReabrirLoading || modalReabrirMotivo.trim().length < 5}
                className="px-5 py-2.5 text-xs font-bold text-white bg-rose-600 hover:bg-rose-700 active:bg-rose-800 rounded-xl shadow-md transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {modalReabrirLoading ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Reabrindo...</span>
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-3.5 h-3.5" />
                    <span>Confirmar Reabertura</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  /**
   * Confirmação do upload de ASO via Modal (com parecer de aptidão selecionado)
   */
  const handleConfirmarUploadAso = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!modalUploadAso) return;

    if (!modalAptidao) {
      setModalErro(
        "Por favor, selecione a situação de aptidão (Apto, Inapto, Apto com Restrições ou Inapto Temporariamente).",
      );
      return;
    }
    if (!modalArquivo) {
      setModalErro("Por favor, anexe o arquivo PDF do ASO.");
      return;
    }

    setUploadingExamId(modalUploadAso.agendamentoId);
    setModalErro(null);

    try {
      const response = await clinicaService.uploadLaudoExame(
        modalUploadAso.agendamentoId,
        modalArquivo,
        modalObservacao.trim() || undefined,
        modalAptidao,
      );

      if (response.success) {
        if (response.todos_exames_concluidos) {
          setSuccessToast(
            `🎉 Todos os exames de ${modalUploadAso.colaboradorNome} foram concluídos com sucesso! Colaborador finalizado.`,
          );
          setConclusaoFeedback({
            colaboradorNome: modalUploadAso.colaboradorNome,
            mensagem: `Todos os exames de ${modalUploadAso.colaboradorNome} foram anexados com sucesso e encaminhados para a fila de auditoria da SST Exames!`,
          });
        } else {
          setSuccessToast(
            `✅ ASO anexado com sucesso para ${modalUploadAso.colaboradorNome}!`,
          );
        }
        setModalUploadAso(null);
        setModalArquivo(null);
        setModalAptidao("");
        setModalObservacao("");
        carregarDados();
        carregarRecepcao(undefined, undefined, undefined, undefined, true);
      }
    } catch (err: unknown) {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Falha ao enviar arquivo do ASO. Tente novamente.";
      setModalErro(msg);
    } finally {
      setUploadingExamId(null);
    }
  };

  /**
   * Helper de Renderização do Badge de SLA
   */
  const renderSlaBadge = (statusSla?: string) => {
    switch (statusSla) {
      case "atrasado":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-rose-50 text-rose-700 border border-rose-200">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-600" />
            Atrasado
          </span>
        );
      case "no_prazo":
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
            <Clock className="w-3.5 h-3.5 text-primary-600" />
            No Prazo
          </span>
        );
      case "aguardando_previsao":
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-800 border border-amber-200">
            <Clock className="w-3.5 h-3.5 text-amber-600" />
            Aguardando Previsão
          </span>
        );
    }
  };

  const hasActiveFilters = Boolean(
    search.trim() ||
    statusSla !== "todos" ||
    dataInicio ||
    dataFim ||
    ordenacao !== "recentes",
  );

  const totalColaboradores = meta?.total ?? pendencias.length;
  const totalPages = meta?.total_pages ?? 1;
  const startRecord = totalColaboradores === 0 ? 0 : (page - 1) * limit + 1;
  const endRecord = Math.min(page * limit, totalColaboradores);

  /**
   * Renderiza o Modal de Solicitação de Contato Ativo
   */
  const renderModalContatoAtivo = () => {
    if (!modalContatoAtivoOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-fade-in">
        <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full border border-gray-200 shadow-2xl relative max-h-[90vh] overflow-y-auto">
          <button
            type="button"
            onClick={() => {
              setModalContatoAtivoOpen(false);
              setErroContatoAtivo(null);
              setContatoAtivoSucesso(null);
            }}
            className="absolute right-5 top-5 p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-primary-50 text-primary-700 rounded-2xl shrink-0">
              <Phone className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">
                Solicitar Contato Ativo
              </h3>
              <p className="text-xs text-gray-500">
                Suporte especializado para clínicas credenciadas SST Exames.
              </p>
            </div>
          </div>

          {contatoAtivoSucesso ? (
            <div className="space-y-4 py-3 text-center animate-fade-in">
              <div className="w-14 h-14 bg-primary-100 text-primary-600 rounded-2xl mx-auto flex items-center justify-center shadow-xs">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-base font-bold text-gray-900">
                  Solicitação Enviada com Sucesso!
                </h4>
                <p className="text-xs text-gray-600 mt-2 leading-relaxed">
                  {contatoAtivoSucesso}
                </p>
                <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-xl text-left flex items-start gap-2.5">
                  <Clock className="w-4 h-4 text-primary-600 shrink-0 mt-0.5" />
                  <p className="text-xs text-primary-900 font-medium leading-relaxed">
                    Nossa equipe técnica e de credenciamento recebeu sua
                    solicitação e{" "}
                    <strong>
                      irá retornar o contato o mais breve possível
                    </strong>{" "}
                    para regularizar o acesso da sua unidade credenciada.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  setModalContatoAtivoOpen(false);
                  setContatoAtivoSucesso(null);
                }}
                className="w-full py-2.5 px-4 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
              >
                Fechar
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSolicitarContatoAtivo}
              className="space-y-3.5 text-left"
            >
              <div className="p-3 bg-primary-50/70 border border-primary-100 rounded-xl text-xs text-primary-900 leading-relaxed">
                Informe os dados de contato do responsável atual da unidade.
                Nossa equipe entrará em contato para checagem de segurança e
                envio de novas credenciais.
              </div>

              {erroContatoAtivo && (
                <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 font-medium">
                  {erroContatoAtivo}
                </div>
              )}

              <FormField
                label="CNPJ da Clínica"
                required
                htmlFor="contato-cnpj"
              >
                <FormattedInput
                  type="cnpj"
                  id="contato-cnpj"
                  value={cnpjSolicitacao || cnpjInput}
                  onChange={(val) => setCnpjSolicitacao(val)}
                  placeholder="00.000.000/0000-00"
                  required
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </FormField>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField
                  label="Nome do Solicitante"
                  required
                  htmlFor="contato-nome"
                >
                  <input
                    id="contato-nome"
                    type="text"
                    value={contatoNome}
                    onChange={(e) => setContatoNome(e.target.value)}
                    placeholder="Seu nome completo"
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </FormField>

                <FormField
                  label="Cargo / Função"
                  required
                  htmlFor="contato-cargo"
                >
                  <input
                    id="contato-cargo"
                    type="text"
                    value={contatoCargo}
                    onChange={(e) => setContatoCargo(e.target.value)}
                    placeholder="Ex: Gestor, Recepção"
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <FormField
                  label="Telefone / WhatsApp"
                  required
                  htmlFor="contato-telefone"
                >
                  <FormattedInput
                    type="phone"
                    id="contato-telefone"
                    value={contatoTelefone}
                    onChange={(val) => setContatoTelefone(val)}
                    placeholder="(00) 00000-0000"
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </FormField>

                <FormField
                  label="E-mail Institucional"
                  required
                  htmlFor="contato-email"
                >
                  <input
                    id="contato-email"
                    type="email"
                    value={contatoEmail}
                    onChange={(e) => setContatoEmail(e.target.value)}
                    placeholder="contato@clinica.com.br"
                    required
                    className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                </FormField>
              </div>

              <FormField
                label="Observações Adicionais"
                htmlFor="contato-mensagem"
              >
                <textarea
                  id="contato-mensagem"
                  rows={2}
                  value={contatoMensagem}
                  onChange={(e) => setContatoMensagem(e.target.value)}
                  placeholder="Explique brevemente a necessidade (ex: troca de responsável, mudança de domínio de e-mail)"
                  className="w-full px-3.5 py-2 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                />
              </FormField>

              <div className="pt-2 flex items-center justify-end gap-2 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => {
                    setModalContatoAtivoOpen(false);
                    setErroContatoAtivo(null);
                  }}
                  className="px-4 py-2.5 text-xs font-semibold text-primary-700 bg-white border border-primary-300 hover:bg-primary-50 hover:border-primary-400 rounded-xl transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={enviandoContatoAtivo}
                  className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white rounded-xl text-xs font-bold transition-colors shadow-2xs flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                >
                  {enviandoContatoAtivo ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Enviando solicitação...</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>Enviar Solicitação</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    );
  };

  // =========================================================================
  // FLUXO NÃO AUTENTICADO: LOGIN, PRIMEIRO ACESSO E DEFINIR SENHA (SPLIT-SCREEN)
  // =========================================================================
  if (!clinica && !medicoPerfil) {
    return (
      <div className="min-h-screen lg:h-screen flex relative bg-[#042614] lg:bg-white overflow-hidden">
        {/* Background de Imagem para Mobile */}
        <div className="absolute inset-0 z-0 lg:hidden pointer-events-none">
          <div className="absolute inset-0 bg-[#042614]/85 mix-blend-multiply z-10" />
          <img 
            src="/mapa_brasil.webp" 
            alt="SST Exames" 
            className="absolute inset-0 w-full h-full object-cover opacity-50 mix-blend-overlay"
          />
        </div>

        {/* Lado Esquerdo - Painel Visual Imersivo (Oculto em mobile, 50% em desktop) */}
        <div className="hidden lg:flex lg:w-1/2 relative bg-[#042614] overflow-hidden shadow-2xl z-10 flex-shrink-0">
          {/* Camadas de cor e textura */}
          <div className="absolute inset-0 bg-gradient-to-br from-[#021f10]/90 via-[#05331a]/80 to-[#02180c]/95 z-10" />
          
          {/* Imagem do Mapa do Brasil com blend sofisticado */}
          <img 
            src="/mapa_brasil.webp" 
            alt="SST Exames Corporativo" 
            className="absolute inset-0 w-full h-full object-cover opacity-45 mix-blend-luminosity scale-105"
          />

          {/* Linhas Topográficas e de Fluxo de Dados Animadas */}
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none z-15 opacity-75"
            viewBox="0 0 900 1000"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            preserveAspectRatio="xMidYMid slice"
          >
            <defs>
              <linearGradient id="streamGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#10b981" stopOpacity="0.1" />
                <stop offset="50%" stopColor="#34d399" stopOpacity="0.85" />
                <stop offset="100%" stopColor="#059669" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient id="streamGrad2" x1="100%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#059669" stopOpacity="0.15" />
                <stop offset="50%" stopColor="#6ee7b7" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#047857" stopOpacity="0.2" />
              </linearGradient>
              <radialGradient id="nodePulseGlow" cx="50%" cy="50%" r="50%">
                <stop offset="0%" stopColor="#34d399" stopOpacity="0.9" />
                <stop offset="100%" stopColor="#059669" stopOpacity="0" />
              </radialGradient>
            </defs>

            {/* Curva Principal de Fluxo 1 */}
            <path
              d="M 500 60 C 440 200, 680 320, 580 470 C 490 610, 660 760, 540 940"
              stroke="url(#streamGrad1)"
              strokeWidth="2.5"
              strokeDasharray="140 220"
              className="animate-flow-dash"
              strokeLinecap="round"
            />

            {/* Curva Secundária Mais Rápida 2 */}
            <path
              d="M 680 120 C 590 280, 730 400, 630 570 C 550 700, 710 830, 610 970"
              stroke="url(#streamGrad2)"
              strokeWidth="1.8"
              strokeDasharray="90 180"
              className="animate-flow-dash-fast"
              strokeLinecap="round"
            />

            {/* Linha Sinusoidal Suave 3 */}
            <path
              d="M 430 160 C 370 310, 520 450, 460 590 C 410 730, 540 860, 470 990"
              stroke="url(#streamGrad1)"
              strokeWidth="1.5"
              strokeDasharray="180 300"
              className="animate-flow-dash-slow"
              strokeLinecap="round"
              opacity="0.6"
            />

            {/* Conexão Transversal */}
            <path
              d="M 370 510 C 490 470, 610 530, 760 480"
              stroke="url(#streamGrad2)"
              strokeWidth="1.5"
              strokeDasharray="80 140"
              className="animate-flow-dash-reverse"
              strokeLinecap="round"
              opacity="0.5"
            />

            {/* Nós Conectados no Mapa */}
            <g transform="translate(610, 680)">
              <circle r="14" fill="url(#nodePulseGlow)" className="animate-ping opacity-60" />
              <circle r="4" fill="#34d399" />
              <circle r="7" stroke="#10b981" strokeWidth="1" opacity="0.8" />
            </g>

            <g transform="translate(540, 480)">
              <circle r="11" fill="url(#nodePulseGlow)" className="animate-ping opacity-50" />
              <circle r="3.5" fill="#6ee7b7" />
              <circle r="6" stroke="#34d399" strokeWidth="1" opacity="0.7" />
            </g>

            <g transform="translate(710, 410)">
              <circle r="11" fill="url(#nodePulseGlow)" className="animate-ping opacity-50" />
              <circle r="3.5" fill="#34d399" />
              <circle r="6" stroke="#10b981" strokeWidth="1" opacity="0.6" />
            </g>

            <g transform="translate(520, 820)">
              <circle r="9" fill="url(#nodePulseGlow)" className="animate-ping opacity-40" />
              <circle r="3" fill="#34d399" />
            </g>
          </svg>

          {/* Conteúdo sobre a imagem: Logo e Título com Typing */}
          <div className="relative z-20 flex flex-col justify-center px-12 xl:px-20 text-white h-full w-full max-w-2xl">
            <img 
              src="/sst_logo_negativo.svg" 
              alt="SST Exames" 
              className="h-16 w-auto object-contain mb-10 origin-left transform scale-100 self-start drop-shadow-md" 
            />
            
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold mb-6 leading-[1.18] tracking-tight min-h-[96px] sm:min-h-[120px] lg:min-h-[140px]">
              <span className="text-emerald-400 drop-shadow-sm">
                {typedText.slice(0, 22)}
              </span>
              <span className="text-white">
                {typedText.slice(22)}
              </span>
              <span
                className={`inline-block w-1.5 lg:w-2 h-7 sm:h-9 lg:h-11 bg-emerald-400 ml-1.5 align-middle ${
                  typingComplete ? 'animate-pulse' : 'opacity-100'
                }`}
              />
            </h1>

            <p className="text-sm text-emerald-100/70 max-w-lg leading-relaxed">
              Plataforma digital integrada de medicina ocupacional, agendamentos centralizados e laudos regulatórios para todo o Brasil.
            </p>
          </div>
        </div>

        {/* Lado Direito - Formulários */}
        <div className="flex-1 flex flex-col justify-center py-8 px-4 sm:px-6 lg:px-12 xl:px-20 bg-transparent lg:bg-gray-50/80 relative z-20 h-full overflow-y-auto lg:overflow-visible">
          <div className="mx-auto w-full max-w-sm lg:max-w-md relative my-auto pt-10 pb-8">
            {/* Exames Flutuantes Livres ao redor do Card (Pílulas Glassmorphism Premium com Levitação Suave) */}
            <div
              className={`hidden lg:flex absolute z-30 pointer-events-none ${currentScene.positionClass}`}
            >
              <div className={currentScene.layoutClass}>
                {currentScene.items.map((item, idx) => {
                  const isVisible = idx === 0 ? item1Visible : item2Visible;
                  return (
                    <div
                      key={item.tipo}
                      className={`${isVisible ? 'smoke-appear' : 'smoke-dissolve'}`}
                    >
                      <div
                        className={`${
                          idx === 1 ? 'animate-float-flow-2' : 'animate-float-flow-1'
                        } flex items-center gap-2.5 sm:gap-3 select-none`}
                      >
                        <div className="relative flex items-center justify-center w-2.5 h-2.5 shrink-0">
                          <span className={`animate-pulse-glow absolute inline-flex h-full w-full rounded-full ${item.accentDot} opacity-60`} />
                          <span className={`relative inline-flex rounded-full h-2 w-2 ${item.accentDot}`} />
                        </div>
                        <div className="flex flex-col text-left">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs sm:text-[13px] font-bold text-gray-800 tracking-tight whitespace-nowrap">
                              {item.tipo}
                            </span>
                            <span className="inline-flex items-center px-1.5 py-0.5 text-[9px] font-bold text-emerald-800 bg-emerald-50/90 rounded-full">
                              SST
                            </span>
                          </div>
                          <span className={`text-[10px] sm:text-[11px] ${item.accentText} font-semibold tracking-tight whitespace-nowrap`}>
                            {item.tag}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Logo para mobile */}
            <div className="lg:hidden mb-6 flex justify-center drop-shadow-lg">
              <img src="/sst_logo_negativo.svg" alt="SST Exames" className="h-12 w-auto" />
            </div>

            {/* 1. Primeiro: Cabeçalho com transição suave */}
            <div
              className={`transition-all duration-700 ease-out transform mb-6 text-center ${
                showWelcome
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 -translate-y-4'
              }`}
            >
              <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white lg:text-gray-900 transition-all duration-300">
                {authMode === 'login' && (tipoAcesso === 'MEDICO' ? 'Portal do Médico' : 'Portal da Clínica')}
                {authMode === 'solicitar_acesso' && 'Primeiro Acesso da Clínica'}
                {authMode === 'definir_senha' && (isMedicoReset ? 'Redefinir Senha do Médico' : 'Cadastrar Senha da Clínica')}
              </h2>
              <p className="mt-2 text-xs sm:text-sm font-medium text-emerald-100/90 lg:text-gray-500 max-w-xs mx-auto transition-all duration-300">
                {authMode === 'login' && (tipoAcesso === 'MEDICO' ? 'Entre com seu e-mail e senha para realizar atendimentos.' : 'Faça login com seu CNPJ e senha para gerenciar atendimentos.')}
                {authMode === 'solicitar_acesso' && 'Receba as instruções por e-mail para criar sua senha.'}
                {authMode === 'definir_senha' && (isMedicoReset ? `Defina uma nova senha para sua conta de médico examinador (${emailFromQuery || 'examinador'}).` : 'Defina uma senha de acesso para a clínica credenciada.')}
              </p>
            </div>

            {/* Notificação Toast de Sucesso */}
            {successToast && (
              <div className="mb-4 p-4 bg-emerald-50/90 border border-emerald-200/80 rounded-xl flex items-center justify-between shadow-xs animate-fade-in relative z-50">
                <div className="flex items-center gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-emerald-800">
                    {successToast}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setSuccessToast(null)}
                  className="text-emerald-700 hover:text-emerald-900 p-1 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Notificação de Erro */}
            {error && (
              <div className="mb-4 p-4 bg-red-50/90 border border-red-200/80 rounded-xl flex items-center justify-between shadow-xs relative z-50">
                <div className="flex items-center gap-2.5">
                  <AlertTriangle className="w-5 h-5 text-red-600 shrink-0" />
                  <span className="text-xs sm:text-sm font-medium text-red-700">
                    {error}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setError(null)}
                  className="text-red-700 hover:text-red-900 p-1 rounded-lg"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            <div 
              className={`w-full bg-white p-6 sm:p-7 shadow-2xl shadow-emerald-950/15 lg:shadow-xl lg:shadow-emerald-950/10 rounded-2xl border border-gray-100/80 transition-all duration-700 ease-out transform ${
                showFormBox
                  ? 'opacity-100 translate-y-0 scale-100'
                  : 'opacity-0 translate-y-8 scale-[0.98]'
              } relative z-40`}
            >
              {/* Linha de acento no topo do Card */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 via-teal-400 to-emerald-600 rounded-t-2xl" />
              {/* TELA 1A: DEFINIR SENHA VIA TOKEN DE E-MAIL */}
              {authMode === "definir_senha" && (
                <div>
                  <div className="text-center mb-6">
                    <div className={`inline-flex p-3 ${isMedicoReset ? 'bg-primary-50 text-primary-700' : 'bg-primary-50 text-primary-700'} rounded-2xl mb-3`}>
                      <KeyRound className={`w-7 h-7 ${isMedicoReset ? 'text-primary-600' : 'text-primary-600'}`} />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900">
                      {isMedicoReset ? 'Redefinir Senha do Médico' : 'Cadastrar Senha da Clínica'}
                    </h3>
                  </div>

                  {tokenValidando ? (
                    <div className="py-8 flex flex-col items-center justify-center gap-3">
                      <RefreshCw className={`w-6 h-6 animate-spin ${isMedicoReset ? 'text-primary-600' : 'text-primary-600'}`} />
                      <span className="text-sm text-gray-500 font-medium">Validando link de acesso...</span>
                    </div>
                  ) : tokenInvalidoErro ? (
                    <div className="py-4 space-y-4">
                      <div className="p-4 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-medium">
                        {tokenInvalidoErro}
                      </div>
                      {isMedicoReset ? (
                        <button
                          type="button"
                          onClick={() => {
                            setAuthMode('login');
                            setTipoAcesso('MEDICO');
                            localStorage.setItem('clinics_tipo_acesso', 'MEDICO');
                            setMedicoForgotEmail(emailFromQuery || '');
                            setMedicoForgotModalOpen(true);
                            navigate('/', { replace: true });
                          }}
                          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl text-sm transition-all shadow-xs cursor-pointer"
                        >
                          Solicitar Novo Link de Recuperação
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setAuthMode('solicitar_acesso')}
                          className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-bold rounded-xl text-sm transition-all"
                        >
                          Solicitar Novo Link de Acesso
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <p className="text-xs sm:text-sm text-gray-500 mb-5 text-center">
                        {isMedicoReset ? (
                          <>
                            Defina uma nova senha para a conta do médico{' '}
                            <strong className="text-gray-800">
                              {tokenValidoInfo?.clinica_nome || emailFromQuery}
                            </strong>
                            .
                          </>
                        ) : tokenValidoInfo?.clinica_nome ? (
                          <>
                            Defina a senha para{" "}
                            <strong className="text-gray-800">
                              {tokenValidoInfo.clinica_nome}
                            </strong>
                            .
                          </>
                        ) : (
                          "Defina a senha de acesso da clínica credenciada."
                        )}
                      </p>

                      <form
                        onSubmit={handleDefinirSenha}
                        className="space-y-4 text-left"
                      >
                        <FormField
                          label="Nova Senha"
                          required
                          htmlFor="nova-senha-input"
                        >
                          <div className="relative">
                            <input
                              id="nova-senha-input"
                              type={showNovaSenha ? "text" : "password"}
                              value={novaSenha}
                              onChange={(e) => setNovaSenha(e.target.value)}
                              placeholder="Mínimo de 6 caracteres"
                              minLength={6}
                              required
                              className={`w-full pl-4 pr-11 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 ${isMedicoReset ? 'focus:ring-primary-500 focus:border-primary-500' : 'focus:ring-primary-500 focus:border-primary-500'} outline-none transition-all placeholder:text-gray-400`}
                            />
                            <button
                              type="button"
                              onClick={() => setShowNovaSenha(!showNovaSenha)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 cursor-pointer"
                            >
                              {showNovaSenha ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </FormField>

                        <FormField
                          label="Confirmar Nova Senha"
                          required
                          htmlFor="confirmar-nova-senha-input"
                        >
                          <input
                            id="confirmar-nova-senha-input"
                            type={showNovaSenha ? "text" : "password"}
                            value={confirmarNovaSenha}
                            onChange={(e) =>
                              setConfirmarNovaSenha(e.target.value)
                            }
                            placeholder="Repita a nova senha"
                            minLength={6}
                            required
                            className={`w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 ${isMedicoReset ? 'focus:ring-primary-500 focus:border-primary-500' : 'focus:ring-primary-500 focus:border-primary-500'} outline-none transition-all placeholder:text-gray-400`}
                          />
                        </FormField>

                        <button
                          type="submit"
                          disabled={definindoSenha || !novaSenha || !confirmarNovaSenha}
                          className={`w-full py-3.5 px-4 ${isMedicoReset ? 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800' : 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800'} disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer`}
                        >
                          {definindoSenha ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>{isMedicoReset ? 'Redefinindo senha...' : 'Cadastrando senha...'}</span>
                            </>
                          ) : (
                            <>
                              <span>Salvar Nova Senha</span>
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </button>

                        <div className="pt-2 text-center">
                          <button
                            type="button"
                            onClick={() => {
                              if (isMedicoReset) {
                                setTipoAcesso('MEDICO');
                                localStorage.setItem('clinics_tipo_acesso', 'MEDICO');
                              }
                              setAuthMode('login');
                              navigate('/', { replace: true });
                            }}
                            className="text-xs text-gray-500 hover:text-gray-800 font-medium cursor-pointer"
                          >
                            Voltar para a tela de Login
                          </button>
                        </div>
                      </form>
                    </>
                  )}
                </div>
              )}

              {/* TELA 1B: SOLICITAR PRIMEIRO ACESSO / RECUPERAR SENHA */}
              {authMode === "solicitar_acesso" && (
                <div>
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold text-gray-900">
                      Recuperação de Acesso
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      Informe o CNPJ da clínica para identificar os contatos
                      cadastrados ou solicitar suporte ativo
                    </p>
                  </div>

                  {/* Feedback de Reenvio com Sucesso */}
                  {sucessoReenvioMsg && (
                    <div className="mb-4 p-3.5 bg-primary-50 border border-primary-200 rounded-xl text-left space-y-1">
                      <div className="flex items-center gap-2 text-primary-800 font-bold text-xs">
                        <CheckCircle2 className="w-4 h-4 text-primary-600 shrink-0" />
                        <span>Link enviado com sucesso!</span>
                      </div>
                      <p className="text-xs text-primary-700">
                        {sucessoReenvioMsg}
                      </p>
                    </div>
                  )}

                  {/* Form de CNPJ e Busca de Contatos */}
                  <form
                    onSubmit={handleBuscarContatos}
                    className="space-y-4 text-left"
                  >
                    <FormField
                      label="CNPJ da Clínica"
                      required
                      htmlFor="cnpj-solicitacao-input"
                    >
                      <div className="flex gap-2">
                        <FormattedInput
                          type="cnpj"
                          id="cnpj-solicitacao-input"
                          value={cnpjSolicitacao}
                          onChange={(val) => {
                            setCnpjSolicitacao(val);
                            setContatosCadastrados(null);
                            setSucessoReenvioMsg(null);
                          }}
                          placeholder="00.000.000/0000-00"
                          required
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder:text-gray-400"
                        />
                        <button
                          type="submit"
                          disabled={
                            buscandoContatos ||
                            cnpjSolicitacao.replace(/\D/g, "").length !== 14
                          }
                          className="px-4 py-3 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 text-white font-bold rounded-xl text-xs transition-all flex items-center gap-1.5 shrink-0 shadow-xs cursor-pointer"
                        >
                          {buscandoContatos ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                          <span>Localizar</span>
                        </button>
                      </div>
                    </FormField>
                  </form>

                  {/* Lista de Contatos Cadastrados com Máscara */}
                  {contatosCadastrados && (
                    <div className="mt-5 space-y-3 text-left animate-fade-in">
                      <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl">
                        <div className="text-[11px] font-bold text-slate-500 uppercase tracking-wider">
                          Unidade Identificada
                        </div>
                        <div className="text-sm font-bold text-slate-900">
                          {contatosCadastrados.clinica_nome}
                        </div>
                        <div className="text-xs text-slate-600 font-mono">
                          {formatCnpj(contatosCadastrados.clinica_cnpj)}
                        </div>
                      </div>

                      {contatosCadastrados.contatos.length > 0 ? (
                        <div>
                          <div className="text-xs font-bold text-gray-700 mb-2 uppercase tracking-wider">
                            E-mails autorizados para recebimento:
                          </div>
                          <div className="space-y-2">
                            {contatosCadastrados.contatos.map((contato) => (
                              <div
                                key={contato.id}
                                className="p-3 bg-white border border-gray-200 rounded-xl flex items-center justify-between gap-3 shadow-2xs hover:border-primary-300 transition-colors"
                              >
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 flex items-center justify-center shrink-0">
                                    <Mail className="w-4 h-4" />
                                  </div>
                                  <div className="truncate font-mono text-xs font-semibold text-gray-800">
                                    {contato.email_mascarado}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  disabled={reenviandoEmailId === contato.id}
                                  onClick={() =>
                                    handleReenviarDirecionado(contato.id)
                                  }
                                  className="px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 font-bold rounded-lg text-xs transition-all shrink-0 flex items-center gap-1 border border-primary-200 cursor-pointer"
                                >
                                  {reenviandoEmailId === contato.id ? (
                                    <>
                                      <RefreshCw className="w-3 h-3 animate-spin" />
                                      <span>Enviando...</span>
                                    </>
                                  ) : (
                                    <>
                                      <Send className="w-3 h-3" />
                                      <span>Reenviar Link</span>
                                    </>
                                  )}
                                </button>
                              </div>
                            ))}
                          </div>

                          <div className="mt-3">
                            <button
                              type="button"
                              disabled={solicitandoAcesso}
                              onClick={handleSolicitarAcesso}
                              className="w-full py-2.5 px-3 bg-white border border-primary-300 text-primary-700 hover:bg-primary-50 hover:border-primary-400 font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-2 cursor-pointer"
                            >
                              {solicitandoAcesso ? (
                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Mail className="w-3.5 h-3.5" />
                              )}
                              <span>Enviar para todos os e-mails acima</span>
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
                          Nenhum e-mail de contato localizado no cadastro desta
                          clínica credenciada.
                        </div>
                      )}

                      {/* Card de Contingência: Solicitar Contato Ativo */}
                      <div className="p-3.5 bg-primary-50 border border-primary-200 rounded-xl flex items-start gap-3">
                        <HelpCircle className="w-5 h-5 text-primary-600 shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-xs font-semibold text-primary-950">
                            Não reconhece nenhum desses contatos?
                          </p>
                          <p className="text-[11px] text-primary-800 mt-0.5">
                            Se a sua equipe perdeu acesso a essas caixas postais
                            ou deseja atualizar o responsável, solicite um
                            contato ativo.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setErroContatoAtivo(null);
                              setContatoAtivoSucesso(null);
                              setModalContatoAtivoOpen(true);
                            }}
                            className="mt-2 text-xs font-bold text-primary-700 hover:text-primary-900 underline flex items-center gap-1 cursor-pointer"
                          >
                            <span>Solicitar Contato Ativo</span>
                            <ArrowRight className="w-3 h-3" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Opção direta de contato ativo caso ainda não tenha pesquisado */}
                  {!contatosCadastrados && (
                    <div className="pt-2 text-center">
                      <button
                        type="button"
                        onClick={() => {
                          setErroContatoAtivo(null);
                          setContatoAtivoSucesso(null);
                          setModalContatoAtivoOpen(true);
                        }}
                        className="text-xs text-primary-700 hover:text-primary-800 font-semibold flex items-center justify-center gap-1.5 mx-auto cursor-pointer"
                      >
                        <HelpCircle className="w-3.5 h-3.5" />
                        <span>
                          Não reconhece nenhum contato? Solicite um contato
                          ativo
                        </span>
                      </button>
                    </div>
                  )}

                  <div className="pt-4 text-center border-t border-gray-100 mt-5">
                    <button
                      type="button"
                      onClick={() => {
                        setAuthMode("login");
                        setError(null);
                        setSucessoReenvioMsg(null);
                        setContatosCadastrados(null);
                        if (location.pathname !== "/") {
                          navigate("/", { replace: true });
                        }
                      }}
                      className="text-xs text-gray-500 hover:text-gray-800 font-medium cursor-pointer"
                    >
                      Já possui senha? Voltar para o Login
                    </button>
                  </div>
                </div>
              )}

              {/* TELA 1C: LOGIN (TIPO ACESSO: CLÍNICA OU MÉDICO) */}
              {authMode === "login" && (
                <div>
                  {/* Seletor Tipo Acesso: Clínica vs Médico */}
                  <div className="flex rounded-xl bg-gray-100 p-1 mb-6 border border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        setTipoAcesso("CLINICA");
                        localStorage.setItem("clinics_tipo_acesso", "CLINICA");
                        setError(null);
                      }}
                      className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                        tipoAcesso === "CLINICA"
                          ? "bg-white text-gray-900 shadow-xs"
                          : "text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      <Building2 className="w-4 h-4 text-primary-600" />
                      <span>Clínica Credenciada</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setTipoAcesso("MEDICO");
                        localStorage.setItem("clinics_tipo_acesso", "MEDICO");
                        setError(null);
                      }}
                      className={`flex-1 py-2.5 px-3 rounded-lg text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
                        tipoAcesso === "MEDICO"
                          ? "bg-white text-primary-800 shadow-xs"
                          : "text-gray-500 hover:text-gray-800"
                      }`}
                    >
                      <Stethoscope className="w-4 h-4 text-primary-600" />
                      <span>Médico Examinador</span>
                    </button>
                  </div>

                  {tipoAcesso === "CLINICA" ? (
                    <div>
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900">
                          Acesso da Clínica
                        </h3>
                      </div>

                      {/* Banner amigável quando clínica ainda não configurou senha */}
                      {senhaNaoDefinida && (
                        <div className="mb-5 p-4 bg-amber-50 border border-amber-200 rounded-2xl text-left space-y-2.5 animate-fade-in">
                          <div className="flex items-center gap-2 text-amber-900 font-bold text-xs">
                            <Info className="w-4 h-4 text-amber-600 shrink-0" />
                            <span>Primeiro Acesso Necessário</span>
                          </div>
                          <p className="text-xs text-amber-800">
                            Esta clínica credenciada ainda não possui uma senha
                            configurada. Clique abaixo para receber o link nos
                            e-mails cadastrados.
                          </p>
                          <button
                            type="button"
                            onClick={() => {
                              setAuthMode("solicitar_acesso");
                              setError(null);
                            }}
                            className="w-full py-2 px-3 bg-amber-600 hover:bg-amber-700 text-white font-bold rounded-xl text-xs transition-all flex items-center justify-center gap-1.5 shadow-2xs"
                          >
                            <Mail className="w-3.5 h-3.5" />
                            <span>Solicitar Primeiro Acesso</span>
                          </button>
                        </div>
                      )}

                      <form
                        onSubmit={handleLogin}
                        className="space-y-4 text-left"
                      >
                        <FormField
                          label="CNPJ da Clínica"
                          required
                          htmlFor="cnpj-input"
                        >
                          <FormattedInput
                            type="cnpj"
                            id="cnpj-input"
                            value={cnpjInput}
                            onChange={(val) => setCnpjInput(val)}
                            placeholder="00.000.000/0000-00"
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder:text-gray-400"
                          />
                        </FormField>

                        <FormField
                          label="Senha"
                          required
                          htmlFor="senha-input"
                        >
                          <div className="relative">
                            <input
                              id="senha-input"
                              type={showSenha ? "text" : "password"}
                              value={senhaInput}
                              onChange={(e) => setSenhaInput(e.target.value)}
                              placeholder="Informe a senha da clínica"
                              required
                              className="w-full pl-4 pr-11 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder:text-gray-400"
                            />
                            <button
                              type="button"
                              onClick={() => setShowSenha(!showSenha)}
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 cursor-pointer"
                            >
                              {showSenha ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </FormField>

                        <button
                          type="submit"
                          disabled={
                            loginLoading ||
                            cnpjInput.replace(/\D/g, "").length !== 14 ||
                            !senhaInput
                          }
                          className="w-full py-3.5 px-4 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer"
                        >
                          {loginLoading ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Autenticando...</span>
                            </>
                          ) : (
                            <>
                              <span>Acessar Portal</span>
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </form>
                    </div>
                  ) : (
                    <div>
                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900">
                          Acesso do Médico
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                          Consulte agendamentos, realize exames clínicos e emita
                          ASO assinado
                        </p>
                      </div>

                      <form
                        onSubmit={handleDoctorLogin}
                        className="space-y-4 text-left"
                      >
                        <FormField
                          label="E-mail do Médico"
                          required
                          htmlFor="medico-email-input"
                        >
                          <input
                            id="medico-email-input"
                            type="email"
                            value={medicoEmail}
                            onChange={(e) => setMedicoEmail(e.target.value)}
                            placeholder="seu.email@exemplo.com.br"
                            required
                            disabled={medicoRequires2fa}
                            className="w-full px-4 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder:text-gray-400 disabled:bg-gray-50"
                          />
                        </FormField>

                        <FormField
                          label="Senha"
                          required
                          htmlFor="medico-senha-input"
                        >
                          <div className="relative">
                            <input
                              id="medico-senha-input"
                              type={medicoShowSenha ? "text" : "password"}
                              value={medicoSenha}
                              onChange={(e) => setMedicoSenha(e.target.value)}
                              placeholder="Informe sua senha de médico"
                              required
                              disabled={medicoRequires2fa}
                              className="w-full pl-4 pr-11 py-3 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none transition-all placeholder:text-gray-400 disabled:bg-gray-50"
                            />
                            <button
                              type="button"
                              onClick={() =>
                                setMedicoShowSenha(!medicoShowSenha)
                              }
                              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 cursor-pointer"
                            >
                              {medicoShowSenha ? (
                                <EyeOff className="w-4 h-4" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </button>
                          </div>
                        </FormField>

                        {medicoRequires2fa && (
                          <div className="p-3.5 bg-primary-50 border border-primary-200 rounded-xl space-y-2 animate-fade-in">
                            <div className="flex items-center gap-1.5 text-xs font-bold text-primary-900">
                              <ShieldCheck className="w-4 h-4 text-primary-600 shrink-0" />
                              <span>Autenticação em Duas Etapas (2FA)</span>
                            </div>
                            <p className="text-xs text-primary-800">
                              Enviamos um código numérico de 6 dígitos para o
                              seu e-mail cadastrado. Digite-o abaixo:
                            </p>
                            <input
                              id="medico-2fa-input"
                              type="text"
                              maxLength={6}
                              value={medicoCodigo2fa}
                              onChange={(e) =>
                                setMedicoCodigo2fa(
                                  e.target.value.replace(/\D/g, ""),
                                )
                              }
                              placeholder="000000"
                              required
                              autoFocus
                              className="w-full px-4 py-2.5 text-center tracking-widest text-lg font-bold border border-primary-300 rounded-xl text-slate-800 bg-white focus:ring-2 focus:ring-primary-500 outline-none"
                            />
                          </div>
                        )}

                        <button
                          type="submit"
                          disabled={
                            loginLoading ||
                            !medicoEmail ||
                            !medicoSenha ||
                            (medicoRequires2fa && medicoCodigo2fa.length < 6)
                          }
                          className="w-full py-3.5 px-4 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 disabled:opacity-50 text-white font-bold rounded-xl text-sm transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer"
                        >
                          {loginLoading ? (
                            <>
                              <RefreshCw className="w-4 h-4 animate-spin" />
                              <span>Autenticando Médico...</span>
                            </>
                          ) : (
                            <>
                              <span>
                                {medicoRequires2fa
                                  ? "Confirmar Código 2FA"
                                  : "Acessar Atendimentos"}
                              </span>
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </button>
                      </form>

                      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-col items-center gap-2 text-center">
                        <button
                          type="button"
                          onClick={() => {
                            setMedicoForgotModalOpen(true);
                            setMedicoForgotEmail(medicoEmail);
                            setError(null);
                            setMedicoForgotSuccess(null);
                          }}
                          className="text-xs text-primary-700 hover:text-primary-800 font-semibold cursor-pointer flex items-center gap-1.5"
                        >
                          <KeyRound className="w-3.5 h-3.5" />
                          <span>Esqueceu sua senha de médico?</span>
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Opções de Suporte e Primeiro Acesso fora do Card Branco (somente Clínica) */}
            {authMode === "login" && tipoAcesso === "CLINICA" && (
              <div className="mt-6 flex flex-col items-center gap-2.5 text-center animate-fade-in">
                <button
                  type="button"
                  onClick={() => {
                    setCnpjSolicitacao(cnpjInput);
                    setAuthMode("solicitar_acesso");
                    setError(null);
                    setSucessoReenvioMsg(null);
                  }}
                  className="text-xs font-semibold text-green-100 lg:text-primary-700 hover:text-white lg:hover:text-primary-800 transition-colors flex items-center justify-center gap-1.5 cursor-pointer py-1.5 px-3 rounded-lg hover:bg-black/10 lg:hover:bg-primary-50/70"
                >
                  <KeyRound className="w-3.5 h-3.5 text-green-300 lg:text-primary-600 shrink-0" />
                  <span>Primeiro acesso ou esqueceu sua senha?</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setErroContatoAtivo(null);
                    setContatoAtivoSucesso(null);
                    setModalContatoAtivoOpen(true);
                  }}
                  className="text-xs font-medium text-green-200/90 lg:text-gray-500 hover:text-white lg:hover:text-gray-700 transition-colors flex items-center justify-center gap-1.5 cursor-pointer py-1 px-3 rounded-lg hover:bg-black/10 lg:hover:bg-gray-100"
                >
                  <HelpCircle className="w-3.5 h-3.5 text-green-300 lg:text-gray-400 shrink-0" />
                  <span>
                    Não reconhece os dados cadastrados?{" "}
                    <strong className="underline decoration-green-400 lg:decoration-gray-400">
                      Solicitar contato ativo
                    </strong>
                  </span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Modal de Solicitação de Contato Ativo no Fluxo de Login / Recuperação */}
        {renderModalContatoAtivo()}

        {/* Modal de Recuperação de Senha do Médico */}
        {medicoForgotModalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs animate-fade-in">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100">
              <div className="px-6 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <KeyRound className="w-5 h-5 text-primary-200" />
                  <h3 className="text-base font-bold">
                    Recuperar Senha do Médico
                  </h3>
                </div>
                <button
                  type="button"
                  onClick={() => setMedicoForgotModalOpen(false)}
                  className="p-1 rounded-lg hover:bg-white/20 text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                {medicoForgotSuccess ? (
                  <div className="p-4 bg-primary-50 border border-primary-200 rounded-xl space-y-2 text-left">
                    <div className="flex items-center gap-2 text-primary-800 font-bold text-sm">
                      <CheckCircle2 className="w-5 h-5 text-primary-600 shrink-0" />
                      <span>E-mail enviado!</span>
                    </div>
                    <p className="text-xs text-primary-700">
                      {medicoForgotSuccess}
                    </p>
                    <button
                      type="button"
                      onClick={() => setMedicoForgotModalOpen(false)}
                      className="mt-2 w-full py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer"
                    >
                      Voltar ao Login
                    </button>
                  </div>
                ) : (
                  <form
                    onSubmit={handleDoctorForgot}
                    className="space-y-4 text-left"
                  >
                    <p className="text-xs text-slate-600 leading-relaxed">
                      Informe o e-mail cadastrado em seu perfil de médico.
                      Enviaremos um link seguro para você redefinir sua senha.
                    </p>

                    <FormField
                      label="E-mail Cadastrado"
                      required
                    >
                      <input
                        type="email"
                        value={medicoForgotEmail}
                        onChange={(e) => setMedicoForgotEmail(e.target.value)}
                        placeholder="medico@exemplo.com.br"
                        required
                        className="w-full px-3.5 py-2.5 border border-slate-300 rounded-xl text-slate-800 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                      />
                    </FormField>

                    <div className="flex items-center justify-end gap-2 pt-2">
                      <button
                        type="button"
                        onClick={() => setMedicoForgotModalOpen(false)}
                        className="px-4 py-2 text-xs font-semibold text-primary-700 bg-white border border-primary-300 hover:bg-primary-50 rounded-lg cursor-pointer transition-colors"
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        disabled={medicoForgotLoading || !medicoForgotEmail}
                        className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-lg disabled:opacity-50 flex items-center gap-1.5 shadow-xs cursor-pointer"
                      >
                        {medicoForgotLoading ? (
                          <>
                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                            <span>Enviando...</span>
                          </>
                        ) : (
                          <>
                            <Mail className="w-3.5 h-3.5" />
                            <span>Enviar Link de Recuperação</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // =========================================================================
  // FLUXO AUTENTICADO: WORKSPACE DO MÉDICO EXAMINADOR
  // =========================================================================
  if (medicoPerfil) {
    const handleMedicoSort = (field: string) => {
      if (medicoSortField === field) {
        setMedicoSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
      } else {
        setMedicoSortField(field);
        setMedicoSortOrder("asc");
      }
    };

    const filteredAtendimentos = medicoAtendimentos.filter((item) => {
      if (medicoAbaAtiva === "fila") {
        const st = (item.status || "").toLowerCase();
        const stOp = (item.status_operacional || "").toUpperCase();
        if (
          st === "realizado" ||
          st === "concluido" ||
          st === "concluído" ||
          stOp === "REALIZADO" ||
          stOp === "CONCLUIDO" ||
          Boolean(item.aso_url)
        ) {
          return false;
        }
      }
      if (medicoTipoFiltro !== "todos") {
        const tipoNorm = (item.tipo_exame || "").toLowerCase();
        if (!tipoNorm.includes(medicoTipoFiltro.toLowerCase())) return false;
      }
      if (medicoDataFiltro) {
        if (medicoAbaAtiva === "fila") {
          // Se a entrada foi liberada na recepção, o paciente está presente aguardando atendimento agora
          if (!item.entrada_liberada && !item.recepcao_checkin_em) {
            if (!item.data_agendamento?.startsWith(medicoDataFiltro)) return false;
          }
        } else if (medicoAbaAtiva === "realizados") {
          if (!item.data_agendamento?.startsWith(medicoDataFiltro)) return false;
        }
      }
      if (medicoSearch.trim()) {
        const q = medicoSearch.toLowerCase().trim();
        const nomeMatch = (item.colaborador?.nome || "")
          .toLowerCase()
          .includes(q);
        const cleanQueryCpf = q.replace(/\D/g, "");
        const cpfMatch =
          cleanQueryCpf.length > 0 &&
          (item.colaborador?.cpf || "")
            .replace(/\D/g, "")
            .includes(cleanQueryCpf);
        const empMatch = (item.colaborador?.empresa?.nome || "")
          .toLowerCase()
          .includes(q);
        const tipoMatch = (item.tipo_exame || "").toLowerCase().includes(q);
        const pedidoMatch = (item.pedido_codigo || "").toLowerCase().includes(q);
        if (!nomeMatch && !cpfMatch && !empMatch && !tipoMatch && !pedidoMatch) return false;
      }
      return true;
    });

    const sortedAtendimentos = [...filteredAtendimentos].sort((a, b) => {
      let valA = "";
      let valB = "";
      if (medicoSortField === "colaborador") {
        valA = a.colaborador?.nome || "";
        valB = b.colaborador?.nome || "";
      } else if (medicoSortField === "empresa") {
        valA = a.colaborador?.empresa?.nome || "";
        valB = b.colaborador?.empresa?.nome || "";
      } else if (medicoSortField === "tipo") {
        valA = a.tipo_exame || "";
        valB = b.tipo_exame || "";
      } else if (medicoSortField === "data") {
        valA = a.recepcao_checkin_em || a.data_agendamento || "";
        valB = b.recepcao_checkin_em || b.data_agendamento || "";
      } else if (medicoSortField === "status") {
        valA = a.status || "";
        valB = b.status || "";
      }
      const cmp = valA.localeCompare(valB, "pt-BR", { numeric: true, sensitivity: "base" });
      return medicoSortOrder === "asc" ? cmp : -cmp;
    });

    const isCertificadoValido = medicoPerfil.has_valid_certificate;
    const exigeCertificadoA1 = selectedClinica?.exige_certificado_a1 ?? true;
    const podeAtender = !exigeCertificadoA1 || isCertificadoValido;

    return (
      <div className="min-h-screen bg-slate-50 text-slate-900 flex flex-col justify-between font-sans">
        {/* Top Header Médico */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-xs">
          <div className="w-full px-4 sm:px-6 lg:px-8 py-3.5 flex flex-wrap justify-between items-center gap-4">
            <div className="flex items-center gap-3">
              <img
                src="/sst_logo.svg"
                alt="SST Exames"
                className="h-8 w-auto object-contain"
              />
              <div className="h-5 w-px bg-slate-200 hidden sm:block" />
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-xs sm:text-sm font-bold text-slate-800">
                  Portal do Parceiro
                </span>
                <span className="px-2 py-0.5 rounded-full text-2xs font-semibold bg-primary-100 text-primary-800 border border-primary-200">
                  Médico Examinador
                </span>
              </div>
            </div>

            {/* Ações e Identificação do Médico */}
            <div className="flex items-center gap-2.5 sm:gap-3 flex-wrap">
              {/* Seletor de Clínica Ativa ou Aviso quando não há clínicas */}
              {medicoClinicas.length === 0 ? (
                <div
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold bg-amber-50 text-amber-800 border border-amber-300 shadow-2xs"
                  title="Nenhuma clínica vinculada ao seu perfil"
                >
                  <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                  <span>Sem clínicas vinculadas</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsClinicaSelectorOpen(true)}
                  className="inline-flex items-center gap-2 text-xs font-semibold text-slate-700 hover:text-primary-700 bg-slate-100 hover:bg-primary-50/70 border border-slate-200 hover:border-primary-300 px-3 py-1.5 rounded-xl transition-all shadow-2xs cursor-pointer"
                  title="Trocar Clínica de Atendimento"
                >
                  <Building2 className="w-4 h-4 text-primary-600" />
                  <span className="max-w-[150px] truncate sm:max-w-[200px]">
                    {selectedClinica
                      ? selectedClinica.nome
                      : "Selecionar Clínica"}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>
              )}

              {/* Status do Certificado Digital A1 */}
              <button
                type="button"
                onClick={() => setIsCertificadoModalOpen(true)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold border transition-all shadow-2xs cursor-pointer ${
                  isCertificadoValido
                    ? "bg-primary-50 text-primary-800 border-primary-200 hover:bg-primary-100/70"
                    : "bg-rose-50 text-rose-800 border-rose-200 hover:bg-rose-100/70 animate-pulse"
                }`}
                title={
                  isCertificadoValido
                    ? "Certificado A1 Válido - Clique para detalhes"
                    : "Certificado A1 Pendente - Clique para vincular"
                }
              >
                <ShieldCheck
                  className={`w-4 h-4 ${isCertificadoValido ? "text-primary-600" : "text-rose-600"}`}
                />
                <span className="hidden md:inline">
                  {isCertificadoValido
                    ? "Certificado A1 Ativo"
                    : "Certificado A1 Pendente"}
                </span>
                <span className="md:hidden">
                  {isCertificadoValido ? "A1 Ativo" : "A1 Pendente"}
                </span>
              </button>

              {/* Identificação do Médico */}
              <div className="hidden lg:flex flex-col text-right pr-2">
                <span className="text-xs font-bold text-slate-800 leading-tight">
                  Dr(a). {medicoPerfil.nome}
                </span>
                <span className="text-2xs text-slate-500">
                  CRM/{medicoPerfil.uf} {medicoPerfil.crm}{" "}
                  {medicoPerfil.rqe ? `• RQE ${medicoPerfil.rqe}` : ""}
                </span>
              </div>

              {/* Botão Sair */}
              <button
                type="button"
                onClick={handleDoctorLogout}
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-600 hover:text-rose-600 bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 px-3 py-1.5 rounded-xl transition-all shadow-2xs cursor-pointer"
                title="Sair do Portal"
              >
                <LogOut className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Sair</span>
              </button>
            </div>
          </div>
        </header>

        {/* Conteúdo Principal do Médico */}
        <main className="flex-grow w-full px-4 sm:px-6 lg:px-8 py-8">
          <PageContainer>
          {/* Toast de Sucesso */}
          {successToast && (
            <div className="p-4 bg-primary-50 border border-primary-200 rounded-2xl flex items-center justify-between text-primary-900 shadow-xs animate-fade-in">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary-600 shrink-0" />
                <span className="text-sm font-semibold">{successToast}</span>
              </div>
              <button
                type="button"
                onClick={() => setSuccessToast(null)}
                className="text-primary-700 hover:text-primary-900 p-1 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Notificação de Erro */}
          {error && (
            <div className="p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-center justify-between text-rose-900 shadow-xs animate-fade-in">
              <div className="flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0" />
                <span className="text-sm font-semibold">{error}</span>
              </div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="text-rose-700 hover:text-rose-900 p-1 rounded-lg cursor-pointer"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Toast de Último Atendimento com Link do ASO Assinado */}
          {atendimentoSucessoUltimo?.aso_url && (
            <div className="p-4 bg-primary-50 border border-primary-200 rounded-2xl flex items-center justify-between text-primary-950 shadow-xs animate-fade-in">
              <div className="flex items-center gap-3">
                <FileCheck2 className="w-5 h-5 text-primary-600 shrink-0" />
                <span className="text-sm font-semibold">
                  Atendimento concluído e ASO assinado com sucesso!
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (atendimentoSucessoUltimo.agendamento_id) {
                    handleDownloadAso(atendimentoSucessoUltimo.agendamento_id);
                  } else {
                    alert("ID do agendamento não disponível para download.");
                  }
                }}
                disabled={visualizandoAsoId === atendimentoSucessoUltimo.agendamento_id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-lg transition-colors shadow-2xs disabled:opacity-50"
              >
                {visualizandoAsoId === atendimentoSucessoUltimo.agendamento_id && atendimentoSucessoUltimo.agendamento_id ? (
                  <>
                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                    <span>Abrindo...</span>
                  </>
                ) : (
                  <>
                    <FileText className="w-3.5 h-3.5" />
                    <span>Visualizar ASO Assinado</span>
                  </>
                )}
              </button>
            </div>
          )}

          {/* ALERTA CRÍTICO: Bloqueio de Atendimento se Certificado A1 Inválido/Pendente e exigido pela clínica */}
          {!podeAtender && (
            <div className="p-5 bg-gradient-to-r from-amber-50 to-rose-50 border border-amber-300/80 rounded-2xl shadow-xs flex flex-col sm:flex-row sm:items-center justify-between gap-4 animate-fade-in">
              <div className="flex items-start gap-3.5">
                <div className="p-2.5 bg-amber-100 rounded-xl text-amber-700 shrink-0">
                  <ShieldCheck className="w-6 h-6" />
                </div>
                <div>
                  <h4 className="text-sm font-bold text-amber-900">
                    Certificado Digital ICP-Brasil A1 Obrigatório
                  </h4>
                  <p className="text-xs text-amber-800 mt-1 leading-relaxed max-w-2xl">
                    Para realizar atendimentos clínicos ocupacionais e emitir
                    ASOs válidos com assinatura digital, é indispensável
                    cadastrar o seu Certificado Digital A1 (.pfx ou .p12). Sem
                    um certificado válido, a conclusão de qualquer atendimento
                    estará bloqueada por conformidade legal.
                  </p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setIsCertificadoModalOpen(true)}
                className="shrink-0 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-xs cursor-pointer"
              >
                <UploadCloud className="w-4 h-4" />
                <span>Vincular Certificado A1</span>
              </button>
            </div>
          )}

          {/* Banner de Boas-Vindas e Clínica Ativa */}
          <div className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Stethoscope className="w-5 h-5 text-primary-600" />
                <h2 className="text-xl font-bold text-slate-900">
                  Fila de Atendimentos Clínicos
                </h2>
                {selectedClinica && (
                  <span
                    className={`ml-1.5 px-2.5 py-0.5 rounded-full text-2xs font-extrabold uppercase border ${
                      selectedClinica.hora_marcada === false
                        ? 'bg-purple-50 text-purple-700 border-purple-200'
                        : 'bg-primary-50 text-primary-700 border-primary-200'
                    }`}
                  >
                    {selectedClinica.hora_marcada === false
                      ? 'Ordem de Chegada'
                      : 'Horário Marcado'}
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-500">
                {medicoClinicas.length === 0
                  ? 'Você não possui nenhuma clínica credenciada vinculada ao seu cadastro médico. Entre em contato com a gestão SST Exames.'
                  : selectedClinica
                  ? `Atendendo na unidade: ${selectedClinica.nome} (CNPJ: ${formatCnpj(selectedClinica.cnpj)})`
                  : "Nenhuma clínica selecionada. Por favor, escolha a unidade onde está atendendo hoje."}
              </p>
            </div>

            <div className="flex items-center gap-3">
              {medicoClinicas.length === 0 ? (
                <div className="px-3.5 py-2 bg-amber-50 border border-amber-300 text-amber-800 text-xs font-bold rounded-xl flex items-center gap-2 shadow-2xs">
                  <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                  <span>Nenhuma clínica vinculada</span>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setIsClinicaSelectorOpen(true)}
                  className="px-3.5 py-2 bg-white hover:bg-primary-50 text-primary-700 border border-primary-300 text-xs font-bold rounded-xl transition-colors flex items-center gap-2 cursor-pointer shadow-2xs"
                >
                  <Building2 className="w-4 h-4 text-primary-600" />
                  <span>Trocar Unidade</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => carregarAtendimentosMedico(undefined, medicoAbaAtiva)}
                disabled={medicoAtendimentosLoading || medicoClinicas.length === 0}
                className="px-3.5 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 border border-primary-200 text-xs font-bold rounded-xl transition-colors flex items-center gap-2 disabled:opacity-50 cursor-pointer"
              >
                <RefreshCw
                  className={`w-4 h-4 ${medicoAtendimentosLoading ? "animate-spin" : ""}`}
                />
                <span>Atualizar</span>
              </button>
            </div>
          </div>

          {/* Barra de Filtros e Busca */}
          <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex-1 min-w-[260px] relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={medicoSearch}
                onChange={(e) => setMedicoSearch(e.target.value)}
                placeholder="Buscar por colaborador, CPF, empresa ou exame..."
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-xl text-xs text-slate-900 font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
              />
              {medicoSearch && (
                <button
                  type="button"
                  onClick={() => setMedicoSearch("")}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Filtro por Tipo de Exame */}
              <select
                value={medicoTipoFiltro}
                onChange={(e) => setMedicoTipoFiltro(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-xl text-xs font-semibold text-slate-700 focus:ring-2 focus:ring-primary-500 outline-none bg-white cursor-pointer"
              >
                <option value="todos">Todos os Tipos</option>
                <option value="admissional">Admissional</option>
                <option value="periodico">Periódico</option>
                <option value="demissional">Demissional</option>
                <option value="retorno">Retorno ao Trabalho</option>
                <option value="mudanca">Mudança / Troca de Função</option>
              </select>

              {/* Filtro de Data */}
              <input
                type="date"
                value={medicoDataFiltro}
                onChange={(e) => setMedicoDataFiltro(e.target.value)}
                className="px-3 py-2 border border-slate-300 rounded-xl text-xs font-medium text-slate-700 focus:ring-2 focus:ring-primary-500 outline-none bg-white"
              />

              {(medicoSearch ||
                medicoTipoFiltro !== "todos" ||
                medicoDataFiltro) && (
                <button
                  type="button"
                  onClick={() => {
                    setMedicoSearch("");
                    setMedicoTipoFiltro("todos");
                    setMedicoDataFiltro("");
                  }}
                  className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
                  title="Limpar Filtros"
                >
                  <RotateCcw className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Navegação de Abas do Médico */}
          <div className="flex border-b border-slate-200 bg-white px-3 sm:px-6 rounded-2xl shadow-xs gap-2 sm:gap-4 overflow-x-auto">
            <button
              type="button"
              onClick={() => setMedicoAbaAtiva('fila')}
              className={`py-3.5 px-3 border-b-2 font-bold text-xs sm:text-sm flex items-center gap-2 cursor-pointer transition-all whitespace-nowrap ${
                medicoAbaAtiva === 'fila'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
              }`}
            >
              <Stethoscope className="w-4 h-4" />
              <span>Fila de Atendimento</span>
              <span
                className={`px-2 py-0.5 rounded-full text-2xs font-extrabold ${
                  medicoAbaAtiva === 'fila'
                    ? 'bg-primary-100 text-primary-800'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {medicoAbaCounts.fila}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setMedicoAbaAtiva('aguardando_exames')}
              className={`py-3.5 px-3 border-b-2 font-bold text-xs sm:text-sm flex items-center gap-2 cursor-pointer transition-all whitespace-nowrap ${
                medicoAbaAtiva === 'aguardando_exames'
                  ? 'border-amber-500 text-amber-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>Aguardando Exames</span>
              <span
                className={`px-2 py-0.5 rounded-full text-2xs font-extrabold ${
                  medicoAbaAtiva === 'aguardando_exames'
                    ? 'bg-amber-100 text-amber-900 border border-amber-300'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {medicoAbaCounts.aguardando_exames}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setMedicoAbaAtiva('aguardando_laudo')}
              className={`py-3.5 px-3 border-b-2 font-bold text-xs sm:text-sm flex items-center gap-2 cursor-pointer transition-all whitespace-nowrap ${
                medicoAbaAtiva === 'aguardando_laudo'
                  ? 'border-indigo-500 text-indigo-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
              }`}
            >
              <FileCheck2 className="w-4 h-4" />
              <span>Aguardando Laudo</span>
              <span
                className={`px-2 py-0.5 rounded-full text-2xs font-extrabold ${
                  medicoAbaAtiva === 'aguardando_laudo'
                    ? 'bg-indigo-100 text-indigo-900 border border-indigo-300'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {medicoAbaCounts.aguardando_laudo}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setMedicoAbaAtiva('realizados')}
              className={`py-3.5 px-3 border-b-2 font-bold text-xs sm:text-sm flex items-center gap-2 cursor-pointer transition-all whitespace-nowrap ${
                medicoAbaAtiva === 'realizados'
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Atendimentos Concluídos</span>
              <span
                className={`px-2 py-0.5 rounded-full text-2xs font-extrabold ${
                  medicoAbaAtiva === 'realizados'
                    ? 'bg-primary-100 text-primary-800'
                    : 'bg-slate-100 text-slate-600'
                }`}
              >
                {medicoAbaCounts.realizados ?? 0}
              </span>
            </button>
          </div>

          {/* Card de Destaque: Próximo Paciente (Ordem de Chegada na Fila) */}
          {medicoAbaAtiva === 'fila' && selectedClinica && selectedClinica.hora_marcada === false && (() => {
            const proximo = filteredAtendimentos.find(
              (a) => a.is_proximo || (a.entrada_liberada && a.status !== 'Realizado'),
            );
            if (!proximo) return null;

            return (
              <div className="p-5 bg-gradient-to-r from-primary-50 via-primary-50 to-primary-50 border-2 border-primary-300 rounded-2xl shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4 animate-fade-in">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="px-2.5 py-0.5 rounded-full text-2xs font-extrabold uppercase bg-primary-600 text-white tracking-wider">
                      Próximo da Fila • Ordem de Chegada
                    </span>
                    {proximo.recepcao_checkin_em && (
                      <span className="text-2xs font-semibold text-primary-800 bg-primary-100/80 px-2 py-0.5 rounded-full border border-primary-200">
                        Entrada na recepção: {new Date(proximo.recepcao_checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-black text-slate-900">
                    {proximo.colaborador?.nome}
                  </h3>
                  <div className="text-xs text-slate-600 flex items-center gap-2 flex-wrap">
                    <span>CPF: {formatCpf(proximo.colaborador?.cpf)}</span>
                    <span>• Empresa: {proximo.colaborador?.empresa?.nome}</span>
                    <span>• Exame: {proximo.tipo_exame}</span>
                    {proximo.pedido_codigo && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                        Pedido: {proximo.pedido_codigo}
                      </span>
                    )}
                  </div>
                </div>

                <button
                  type="button"
                  disabled={!podeAtender || (isItemNoFuturo(proximo.data_agendamento) && !proximo.entrada_liberada)}
                  onClick={() => {
                    if (!podeAtender) {
                      setError('É obrigatório possuir um Certificado Digital A1 válido vinculado para realizar o atendimento.');
                      setIsCertificadoModalOpen(true);
                      return;
                    }
                    setSelectedAtendimento(proximo);
                    setIsAtendimentoModalOpen(true);
                  }}
                  className={`px-5 py-3 font-bold text-xs rounded-xl transition-all flex items-center justify-center gap-2 shadow-xs shrink-0 cursor-pointer ${
                    podeAtender
                      ? 'bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white shadow-primary-200 hover:scale-[1.02]'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed border border-slate-300'
                  }`}
                  title={podeAtender ? 'Atender agora o próximo da fila' : 'Certificado A1 obrigatório para atender'}
                >
                  <Stethoscope className="w-4 h-4" />
                  <span>{podeAtender ? 'Atender Próximo da Fila' : 'A1 Obrigatório para Atender'}</span>
                </button>
              </div>
            );
          })()}

          {/* Tabela de Atendimentos */}
          <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
            {medicoAtendimentosLoading ? (
              <div className="py-20 flex flex-col items-center justify-center space-y-3">
                <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                <p className="text-xs font-semibold text-slate-500">
                  Carregando atendimentos clínicos...
                </p>
              </div>
            ) : filteredAtendimentos.length === 0 ? (
              <div className="py-16 text-center space-y-3 px-4">
                <div className="w-12 h-12 rounded-full bg-slate-100 text-slate-400 mx-auto flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6" />
                </div>
                <h3 className="text-base font-bold text-slate-800">
                  {medicoAbaAtiva === 'aguardando_exames'
                    ? 'Nenhum agendamento aguardando exames complementares'
                    : medicoAbaAtiva === 'realizados'
                    ? 'Nenhum atendimento concluído encontrado'
                    : 'Nenhum atendimento clínico pendente na fila'}
                </h3>
                <p className="text-xs text-slate-500 max-w-md mx-auto">
                  {medicoAbaAtiva === 'aguardando_exames'
                    ? 'Todos os atendimentos com anamnese realizada já possuem os laudos complementares anexados e foram finalizados com ASO emitido.'
                    : medicoAbaAtiva === 'realizados'
                    ? 'Nenhum atendimento foi concluído na data ou filtros informados.'
                    : selectedClinica
                    ? 'Não há colaboradores aguardando realização de exame clínico nesta unidade no momento.'
                    : 'Selecione uma clínica credenciada para carregar a fila de atendimentos.'}
                </p>
                {!selectedClinica && medicoClinicas.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setIsClinicaSelectorOpen(true)}
                    className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-xs font-bold rounded-xl transition-colors shadow-xs cursor-pointer"
                  >
                    <Building2 className="w-4 h-4" />
                    <span>Selecionar Clínica</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-200 text-left">
                  <thead className="bg-slate-50 select-none">
                    <tr>
                      <th
                        onClick={() => handleMedicoSort('colaborador')}
                        className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1.5">
                          <span>Colaborador</span>
                          {medicoSortField === 'colaborador' ? (
                            medicoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleMedicoSort('empresa')}
                        className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1.5">
                          <span>Empresa</span>
                          {medicoSortField === 'empresa' ? (
                            medicoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      <th
                        onClick={() => handleMedicoSort('tipo')}
                        className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                      >
                        <div className="flex items-center gap-1.5">
                          <span>Tipo de Exame</span>
                          {medicoSortField === 'tipo' ? (
                            medicoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                          ) : (
                            <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                          )}
                        </div>
                      </th>
                      {medicoAbaAtiva === 'aguardando_exames' ? (
                        <>
                          <th className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider">
                            Biometria Facial
                          </th>
                          <th className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider">
                            Exames Complementares
                          </th>
                        </>
                      ) : medicoAbaAtiva === 'realizados' ? (
                        <>
                          <th
                            onClick={() => handleMedicoSort('data')}
                            className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex items-center gap-1.5">
                              <span>Data Atendimento</span>
                              {medicoSortField === 'data' ? (
                                medicoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                              )}
                            </div>
                          </th>
                          <th
                            onClick={() => handleMedicoSort('status')}
                            className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex items-center gap-1.5">
                              <span>Assinatura A1</span>
                              {medicoSortField === 'status' ? (
                                medicoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                              )}
                            </div>
                          </th>
                        </>
                      ) : (
                        <>
                          <th
                            onClick={() => handleMedicoSort('data')}
                            className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex items-center gap-1.5">
                              <span>Data / Horário</span>
                              {medicoSortField === 'data' ? (
                                medicoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                              )}
                            </div>
                          </th>
                          <th
                            onClick={() => handleMedicoSort('status')}
                            className="px-6 py-3.5 text-xs font-bold text-slate-600 uppercase tracking-wider cursor-pointer hover:bg-slate-100 transition-colors"
                          >
                            <div className="flex items-center gap-1.5">
                              <span>Status Recepção</span>
                              {medicoSortField === 'status' ? (
                                medicoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-slate-400 opacity-60" />
                              )}
                            </div>
                          </th>
                        </>
                      )}
                      <th className="px-6 py-3.5 text-right text-xs font-bold text-slate-600 uppercase tracking-wider">
                        Ação
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {sortedAtendimentos.map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-slate-50/70 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-slate-900 text-sm">
                            {item.colaborador?.nome || "—"}
                          </div>
                          <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                            <span>CPF: {formatCpf(item.colaborador?.cpf) || "—"}</span>
                            {item.colaborador?.cargo && (
                              <span>• Cargo: {item.colaborador.cargo}</span>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-slate-800 text-sm">
                            {item.colaborador?.empresa?.nome || "—"}
                          </div>
                          <div className="text-xs text-slate-400 mt-0.5">
                            CNPJ: {formatCnpj(item.colaborador?.empresa?.cnpj) || "—"}
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                            {item.tipo_exame || "Exame Clínico (ASO)"}
                          </span>
                          {item.pedido_codigo && (
                            <div className="mt-1">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                Pedido: {item.pedido_codigo}
                              </span>
                            </div>
                          )}
                        </td>

                        {/* Colunas específicas por Aba */}
                        {medicoAbaAtiva === 'aguardando_exames' ? (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.biometria_coletada ? (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-2xs font-bold bg-primary-100 text-primary-800 border border-primary-300">
                                  <CheckCircle2 className="w-3.5 h-3.5 text-primary-600" />
                                  <span>Biometria Coletada</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-2xs font-semibold bg-slate-100 text-slate-600">
                                  Pendente
                                </span>
                              )}
                            </td>

                            <td className="px-6 py-4">
                              <div className="space-y-1">
                                <span className="text-2xs font-bold text-amber-900 bg-amber-100/80 px-2.5 py-0.5 rounded-full border border-amber-300 inline-block">
                                  {item.exames_total_count
                                    ? `${item.exames_total_count - (item.exames_pendentes_count || 0)} de ${item.exames_total_count} exames prontos`
                                    : 'Aguardando laudos'}
                                </span>
                                {item.exames_resumo && item.exames_resumo.length > 0 && (
                                  <div className="flex flex-wrap gap-1 max-w-xs">
                                    {item.exames_resumo.map((er, idx) => (
                                      <span
                                        key={idx}
                                        className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                                          er.status === 'REALIZADO'
                                            ? 'bg-primary-50 text-primary-800 border border-primary-200'
                                            : 'bg-amber-100 text-amber-900 border border-amber-300'
                                        }`}
                                      >
                                        {er.nome} {er.status === 'REALIZADO' ? '✓' : '(pendente)'}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </td>
                          </>
                        ) : medicoAbaAtiva === 'realizados' ? (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600 font-medium">
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                <span>{formatDateBR(item.data_agendamento)}</span>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-2xs font-bold bg-primary-100 text-primary-800 border border-primary-300">
                                <ShieldCheck className="w-3.5 h-3.5 text-primary-600" />
                                <span>ICP-Brasil A1</span>
                              </span>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="px-6 py-4 whitespace-nowrap text-xs text-slate-600 font-medium">
                              <div className="flex items-center gap-1.5">
                                <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                <span>{formatDateBR(item.data_agendamento)}</span>
                                {item.hora_agendamento && (
                                  <span className="text-slate-400 font-mono">({item.hora_agendamento})</span>
                                )}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {item.entrada_liberada ? (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-800 border border-primary-200">
                                  <Check className="w-3 h-3 text-primary-600" />
                                  <span>Liberado p/ Atendimento</span>
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200" title="Aguardando check-in na recepção da clínica">
                                  <Clock className="w-3 h-3 text-amber-600" />
                                  <span>Aguardando Recepção</span>
                                </span>
                              )}
                            </td>
                          </>
                        )}

                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                          {medicoAbaAtiva === 'realizados' ? (
                            item.aso_url ? (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() => handleDownloadAso(item.id)}
                                  disabled={visualizandoAsoId === item.id}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold shadow-2xs transition-colors cursor-pointer disabled:opacity-50"
                                >
                                  {visualizandoAsoId === item.id ? (
                                    <>
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                      <span>Abrindo...</span>
                                    </>
                                  ) : (
                                    <>
                                      <FileText className="w-3.5 h-3.5" />
                                      <span>Ver ASO</span>
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedAtendimento(item);
                                    setIsAtendimentoModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-primary-50 text-primary-700 border border-primary-300 rounded-xl text-xs font-bold shadow-2xs transition-colors cursor-pointer"
                                  title="Visualizar este atendimento"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>Visualizar</span>
                                </button>
                                <button
                                  type="button"
                                  disabled={
                                    !podeAtender ||
                                    gerandoAsoId ===
                                    (item.agendamento_id || item.id)
                                  }
                                  onClick={() =>
                                    handleForcarGerarAso(
                                      item.agendamento_id || item.id,
                                    )
                                  }
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold shadow-2xs transition-colors ${
                                    podeAtender
                                      ? "bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 cursor-pointer disabled:opacity-50"
                                      : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-70"
                                  }`}
                                  title={
                                    podeAtender
                                      ? "Reemitir e assinar digitalmente o ASO deste atendimento"
                                      : "Certificado A1 obrigatório para reemitir ASO"
                                  }
                                >
                                  {gerandoAsoId ===
                                  (item.agendamento_id || item.id) ? (
                                    <>
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                      <span>Reemitindo...</span>
                                    </>
                                  ) : (
                                    <>
                                      <FileCheck2 className="w-3.5 h-3.5" />
                                      <span>Reemitir ASO</span>
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  disabled={!podeAtender}
                                  onClick={() =>
                                    handleAbrirModalReabrir(
                                      item.agendamento_id || item.id,
                                      item.colaborador?.nome || '',
                                    )
                                  }
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-xl text-xs font-bold shadow-2xs transition-colors ${
                                    podeAtender
                                      ? "bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 cursor-pointer"
                                      : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-70"
                                  }`}
                                  title={podeAtender ? "Reabrir este atendimento para emitir nova revisão de ASO" : "Certificado A1 obrigatório para reabrir"}
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                  <span>Reabrir</span>
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  type="button"
                                  onClick={() =>
                                    handleForcarGerarAso(
                                      item.agendamento_id || item.id,
                                    )
                                  }
                                  disabled={
                                    !podeAtender ||
                                    gerandoAsoId ===
                                    (item.agendamento_id || item.id)
                                  }
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold shadow-2xs transition-colors ${
                                    podeAtender
                                      ? "bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white cursor-pointer disabled:opacity-50"
                                      : "bg-slate-300 text-slate-500 cursor-not-allowed opacity-70"
                                  }`}
                                  title={podeAtender ? "Geração/assinatura pendente. Clique para emitir e assinar o ASO agora." : "Certificado A1 obrigatório para emitir ASO"}
                                >
                                  {gerandoAsoId ===
                                  (item.agendamento_id || item.id) ? (
                                    <>
                                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                      <span>Gerando ASO...</span>
                                    </>
                                  ) : (
                                    <>
                                      <FileCheck2 className="w-3.5 h-3.5" />
                                      <span>Forçar Gerar ASO</span>
                                    </>
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedAtendimento(item);
                                    setIsAtendimentoModalOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-primary-50 text-primary-700 border border-primary-300 rounded-xl text-xs font-bold shadow-2xs transition-colors cursor-pointer"
                                  title="Visualizar este atendimento"
                                >
                                  <Eye className="w-3.5 h-3.5" />
                                  <span>Visualizar</span>
                                </button>
                                <button
                                  type="button"
                                  disabled={!podeAtender}
                                  onClick={() =>
                                    handleAbrirModalReabrir(
                                      item.agendamento_id || item.id,
                                      item.colaborador?.nome || '',
                                    )
                                  }
                                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 border rounded-xl text-xs font-bold shadow-2xs transition-colors ${
                                    podeAtender
                                      ? "bg-rose-50 hover:bg-rose-100 text-rose-700 border-rose-200 cursor-pointer"
                                      : "bg-slate-100 text-slate-400 border-slate-200 cursor-not-allowed opacity-70"
                                  }`}
                                  title={podeAtender ? "Reabrir este atendimento para emitir nova revisão de ASO" : "Certificado A1 obrigatório para reabrir"}
                                >
                                  <RotateCcw className="w-3.5 h-3.5" />
                                  <span>Reabrir</span>
                                </button>
                              </div>
                            )
                          ) : medicoAbaAtiva === 'aguardando_exames' ? (
                            <button
                              type="button"
                              disabled={!podeAtender}
                              onClick={() => {
                                setSelectedAtendimento(item);
                                setIsAtendimentoModalOpen(true);
                              }}
                              className={`inline-flex items-center gap-1.5 px-3.5 py-2 font-bold rounded-xl text-xs transition-all shadow-2xs text-white ${
                                podeAtender
                                  ? "bg-amber-600 hover:bg-amber-700 cursor-pointer hover:shadow-xs"
                                  : "bg-slate-400 cursor-not-allowed opacity-70"
                              }`}
                              title={podeAtender ? "Acompanhar andamento dos exames complementares" : "Certificado A1 obrigatório para acompanhar"}
                            >
                              <Eye className="w-3.5 h-3.5" />
                              <span>Acompanhar</span>
                            </button>
                          ) : medicoAbaAtiva === 'aguardando_laudo' ? (
                            <button
                              type="button"
                              disabled={!podeAtender}
                              onClick={() => {
                                setSelectedAtendimento(item);
                                setIsAtendimentoModalOpen(true);
                              }}
                              className={`inline-flex items-center gap-1.5 px-3.5 py-2 font-bold rounded-xl text-xs transition-all shadow-2xs text-white ${
                                podeAtender
                                  ? "bg-indigo-600 hover:bg-indigo-700 cursor-pointer hover:shadow-xs"
                                  : "bg-slate-400 cursor-not-allowed opacity-70"
                              }`}
                              title={podeAtender ? "Analisar exames complementares e finalizar laudo" : "Certificado A1 obrigatório para finalizar laudo"}
                            >
                              <FileCheck2 className="w-3.5 h-3.5" />
                              <span>Finalizar Laudo</span>
                            </button>
                          ) : item.status === 'Realizado' ? (
                            <span className="text-xs font-bold text-primary-600 inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              <span>Concluído</span>
                            </span>
                          ) : (
                            <button
                              type="button"
                              disabled={!podeAtender || (isItemNoFuturo(item.data_agendamento) && !item.entrada_liberada)}
                              onClick={async () => {
                                const targetClinicaId = selectedClinica?.id || item.clinica_id;
                                if (!item.entrada_liberada && targetClinicaId) {
                                  try {
                                    await clinicaService.darEntradaRecepcao(
                                      item.agendamento_id || item.id,
                                      targetClinicaId,
                                    );
                                  } catch (e) {
                                    console.warn('Registro de entrada na recepção já existente ou automático:', e);
                                  }
                                }
                                setSelectedAtendimento(item);
                                setIsAtendimentoModalOpen(true);
                              }}
                              className={`inline-flex items-center gap-1.5 px-3.5 py-2 font-bold rounded-xl text-xs transition-all shadow-2xs text-white ${
                                podeAtender && (!isItemNoFuturo(item.data_agendamento) || item.entrada_liberada)
                                  ? "bg-primary-600 hover:bg-primary-700 active:bg-primary-800 cursor-pointer hover:shadow-xs"
                                  : "bg-slate-400 cursor-not-allowed opacity-70"
                              }`}
                              title={
                                !item.entrada_liberada && isItemNoFuturo(item.data_agendamento)
                                  ? "Agendamento futuro"
                                  : podeAtender
                                  ? "Iniciar Atendimento Clínico"
                                  : "Certificado A1 obrigatório para atender"
                              }
                            >
                              <Stethoscope className="w-3.5 h-3.5" />
                              <span>Atender</span>
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </PageContainer>
        </main>

        {/* Footer do Portal */}
        <footer className="bg-white border-t border-slate-200 py-4 mt-8">
          <div className="w-full px-4 sm:px-6 lg:px-8 text-center text-xs text-slate-500">
            SST Exames • Portal de Atendimentos Clínicos do Médico Parceiro •
            Assinatura Digital ICP-Brasil A1
          </div>
        </footer>

        {/* Modal: Atendimento Médico com Triagem e Anamnese */}
        {selectedAtendimento && (
          <AtendimentoMedicoModal
            isOpen={isAtendimentoModalOpen}
            onClose={() => {
              setIsAtendimentoModalOpen(false);
              setSelectedAtendimento(null);
            }}
            atendimento={selectedAtendimento}
            medico={medicoPerfil}
            clinicaId={selectedClinica?.id || selectedAtendimento.clinica_id}
            readOnly={!podeAtender}
            onReabrir={(agendamentoId) => {
              setIsAtendimentoModalOpen(false);
              handleAbrirModalReabrir(
                agendamentoId,
                selectedAtendimento.colaborador?.nome || '',
              );
            }}
            onForcarAso={handleForcarGerarAso}
            onSuccess={(resp) => {
              setAtendimentoSucessoUltimo(resp);
              setSuccessToast(
                resp.message ||
                  "Atendimento concluído com sucesso e ASO assinado digitalmente!",
              );
              carregarAtendimentosMedico();
              setIsAtendimentoModalOpen(false);
              setSelectedAtendimento(null);
            }}
            onOpenCertificadoModal={() => setIsCertificadoModalOpen(true)}
          />
        )}

        {/* Modal: Upload e Validação de Certificado A1 */}
        <CertificadoA1Modal
          isOpen={isCertificadoModalOpen}
          onClose={() => setIsCertificadoModalOpen(false)}
          medico={medicoPerfil}
          onSuccess={(status) => {
            setMedicoPerfil((prev) =>
              prev
                ? {
                    ...prev,
                    has_valid_certificate: status.is_valid,
                    situacao_certificado: status.situacao,
                    certificado_validade: status.validade,
                  }
                : null,
            );
            setIsCertificadoModalOpen(false);
            setSuccessToast(
              status.is_valid
                ? "Certificado Digital A1 validado e vinculado com sucesso!"
                : "Certificado Digital recebido, porém está inválido ou expirado.",
            );
          }}
        />

        {/* Modal: Selecionar Clínica de Atendimento */}
        <ClinicaSelectorModal
          isOpen={isClinicaSelectorOpen}
          onClose={() => setIsClinicaSelectorOpen(false)}
          clinicas={medicoClinicas}
          selectedClinicaId={selectedClinica?.id || null}
          onSelectClinica={(c) => {
            setSelectedClinica(c);
            setSelectedClinicaId(c.id);
            carregarAtendimentosMedico(c.id);
            setIsClinicaSelectorOpen(false);
          }}
        />

        {/* Modal do Sistema: Reabertura de Atendimento Clínico (Auditoria SST) */}
        {renderModalReabrir()}
      </div>
    );
  }

  // =========================================================================
  // FLUXO AUTENTICADO: DASHBOARD DE PENDÊNCIAS DA CLÍNICA
  // =========================================================================
  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 flex flex-col justify-between">
      {/* Top Header com Identidade Visual Padrão SST Exames */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30 shadow-2xs">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-3.5 flex flex-wrap justify-between items-center gap-4">
          <div className="flex items-center gap-3">
            <img
              src="/sst_logo.svg"
              alt="SST Exames"
              className="h-8 w-auto object-contain"
            />
            <div className="h-5 w-px bg-gray-200 hidden sm:block" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700 hidden sm:block">
              Portal da Clínica Credenciada
            </span>
            <div className="h-5 w-px bg-gray-200 hidden md:block" />
            <button
              type="button"
              onClick={() =>
                setClinicaAbaAtiva(
                  clinicaAbaAtiva === 'repasses' ? 'laudos' : 'repasses',
                )
              }
              className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold transition-all cursor-pointer shadow-2xs ${
                clinicaAbaAtiva === 'repasses'
                  ? 'bg-emerald-600 text-white shadow-xs ring-2 ring-emerald-400/40'
                  : 'bg-emerald-50 text-emerald-800 hover:bg-emerald-100 border border-emerald-300'
              }`}
              title="Acessar Apuração de Repasses e Envio de Documentos (Boleto / NFS-e)"
            >
              <DollarSign
                className={`w-3.5 h-3.5 shrink-0 ${
                  clinicaAbaAtiva === 'repasses'
                    ? 'text-white'
                    : 'text-emerald-600'
                }`}
              />
              <span>Apuração de Repasses & Faturamento</span>
              <span className="flex h-2 w-2 relative">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
              </span>
            </button>
          </div>

          <div className="flex items-center gap-3">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200 shadow-2xs">
              <ShieldCheck className="w-4 h-4 text-primary-600" /> Acesso Seguro
            </div>

            <button
              type="button"
              onClick={recarregarPendencias}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-primary-700 bg-white hover:bg-primary-50/50 border border-gray-200 px-3 py-1.5 rounded-xl transition-all shadow-2xs disabled:opacity-50"
              title="Atualizar lista"
            >
              <RefreshCw
                className={`w-3.5 h-3.5 text-primary-600 ${loading ? "animate-spin" : ""}`}
              />
              <span className="hidden sm:inline">Atualizar</span>
            </button>

            <button
              type="button"
              onClick={() => setShowAlterarSenhaModal(true)}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-700 hover:text-primary-700 bg-white hover:bg-primary-50/50 border border-gray-200 px-3 py-1.5 rounded-xl transition-all shadow-2xs"
              title="Alterar Senha"
            >
              <KeyRound className="w-3.5 h-3.5 text-primary-600" />
              <span className="hidden sm:inline">Alterar Senha</span>
            </button>

            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-600 hover:text-rose-600 bg-white hover:bg-rose-50 border border-gray-200 hover:border-rose-200 px-3 py-1.5 rounded-xl transition-all shadow-2xs"
              title="Sair do Portal"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Sair</span>
            </button>
          </div>
        </div>
      </header>

      {/* Área Principal de Conteúdo */}
      <main className="flex-grow w-full px-4 sm:px-6 lg:px-8 py-8">
        <PageContainer>
        {/* Toast Notificação de Sucesso */}
        {successToast && (
          <div className="mb-6 p-4 bg-primary-50 border border-primary-200 rounded-2xl flex items-center justify-between text-primary-900 shadow-xs animate-fade-in">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-5 h-5 text-primary-600 flex-shrink-0" />
              <span className="text-sm font-semibold">{successToast}</span>
            </div>
            <button
              type="button"
              onClick={() => setSuccessToast(null)}
              className="text-primary-700 hover:text-primary-900 p-1 rounded-lg"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Notificação de Erro */}
        {error && (
          <div className="mb-6 p-4 bg-rose-50 border border-rose-200 rounded-2xl flex items-center justify-between text-rose-900 shadow-xs">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-rose-600 flex-shrink-0" />
              <span className="text-sm font-semibold">{error}</span>
            </div>
            <button
              type="button"
              onClick={() => setError(null)}
              className="text-rose-700 hover:text-rose-900 p-1 rounded-lg"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <div className="space-y-6">
          {/* Header da Clínica Identificada */}
          <div className="bg-white rounded-3xl p-6 sm:p-7 border border-gray-200 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-xs font-bold uppercase tracking-wider text-primary-700 bg-primary-50 px-2.5 py-1 rounded-full border border-primary-200">
                  Clínica Credenciada Ativa
                </span>
                {clinica.cidade && clinica.uf && (
                  <span className="text-xs text-gray-500 font-medium">
                    • {clinica.cidade}/{clinica.uf}
                  </span>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl font-black text-gray-900">
                {clinica.nome}
              </h1>
              <p className="text-xs text-gray-500 mt-1 font-mono">
                CNPJ: {formatCnpj(clinica.cnpj)}
              </p>
            </div>

            {/* Total de exames pendentes em destaque */}
            <div className="flex items-center gap-3">
              <div className="bg-amber-50/80 border border-amber-200 rounded-2xl px-5 py-3 text-center min-w-[140px]">
                <span className="block text-2xl font-black text-amber-700">
                  {resumoSla?.total_exames_pendentes ?? 0}
                </span>
                <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider">
                  Exames a Laudar
                </span>
              </div>
            </div>
          </div>

          {/* SELETOR DE ABAS DO PORTAL DA CLÍNICA */}
          {clinicaAbaAtiva === 'repasses' ? (
            <div className="flex items-center justify-between border-b border-gray-200 pb-3">
              <button
                type="button"
                onClick={() => setClinicaAbaAtiva('laudos')}
                className="inline-flex items-center gap-2 text-xs font-bold text-gray-700 hover:text-primary-700 bg-white hover:bg-gray-50 border border-gray-200 px-3.5 py-2 rounded-xl transition cursor-pointer shadow-2xs"
              >
                <ArrowRight className="w-3.5 h-3.5 rotate-180" />
                <span>Voltar aos Atendimentos e Laudos</span>
              </button>
              <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-300">
                <DollarSign className="w-3.5 h-3.5 text-emerald-600" />
                <span>Apuração de Repasses e Envio de Documentos</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
              <button
                type="button"
                onClick={() => setClinicaAbaAtiva('laudos')}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  clinicaAbaAtiva === 'laudos'
                    ? 'bg-primary-600 text-white shadow-xs'
                    : 'bg-white text-primary-700 hover:bg-primary-50 border border-primary-200'
                }`}
              >
                <FileText className="w-4 h-4" />
                <span>Laudos e Resultados Pendentes</span>
                <span
                  className={`px-2 py-0.5 rounded-full text-2xs ${
                    clinicaAbaAtiva === 'laudos'
                      ? 'bg-primary-700 text-white'
                      : 'bg-primary-100 text-primary-800'
                  }`}
                >
                  {resumoSla?.total_exames_pendentes ?? 0}
                </span>
              </button>
              {clinica?.atende_pelo_sistema && (
                <button
                  type="button"
                  onClick={() => {
                    setClinicaAbaAtiva('recepcao');
                    carregarRecepcao();
                  }}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                    clinicaAbaAtiva === 'recepcao'
                      ? 'bg-primary-600 text-white shadow-xs'
                      : 'bg-white text-primary-700 hover:bg-primary-50 border border-primary-200'
                  }`}
                >
                  <ClipboardCheck className="w-4 h-4" />
                  <span>Recepção / Entrada de Pacientes</span>
                  {recepcaoItens.length > 0 && (
                    <span
                      className={`px-2 py-0.5 rounded-full text-2xs ${
                        clinicaAbaAtiva === 'recepcao'
                          ? 'bg-primary-700 text-white'
                          : 'bg-primary-100 text-primary-800'
                      }`}
                    >
                      {recepcaoItens.length}
                    </span>
                  )}
                </button>
              )}
            </div>
          )}

          {clinicaAbaAtiva === 'laudos' && (
            <div className="space-y-6">

          {/* CARDS INTERATIVOS DE RESUMO SLA (FILTROS RÁPIDOS) */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3.5">
            {/* Card Todos */}
            <button
              type="button"
              onClick={() => handleSelectSlaFilter("todos")}
              className={`p-4 rounded-2xl border text-left transition-all relative overflow-hidden ${
                statusSla === "todos"
                  ? "bg-primary-50/80 border-primary-500 ring-2 ring-primary-500/20 shadow-xs"
                  : "bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50/60 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                  Todos
                </span>
                <div
                  className={`p-2 rounded-xl ${statusSla === "todos" ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-600"}`}
                >
                  <User className="w-4 h-4" />
                </div>
              </div>
              <span className="block text-2xl font-black text-gray-900">
                {resumoSla?.total ?? 0}
              </span>
              <span className="text-[11px] font-medium text-gray-500">
                Colaboradores pendentes
              </span>
            </button>

            {/* Card Atrasados */}
            <button
              type="button"
              onClick={() => handleSelectSlaFilter("atrasado")}
              className={`p-4 rounded-2xl border text-left transition-all relative overflow-hidden ${
                statusSla === "atrasado"
                  ? "bg-rose-50/90 border-rose-500 ring-2 ring-rose-500/20 shadow-xs"
                  : "bg-white border-gray-200 hover:border-rose-200 hover:bg-rose-50/30 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-rose-600">
                  SLA Atrasado
                </span>
                <div
                  className={`p-2 rounded-xl ${statusSla === "atrasado" ? "bg-rose-100 text-rose-700" : "bg-rose-50 text-rose-600"}`}
                >
                  <AlertTriangle className="w-4 h-4" />
                </div>
              </div>
              <span className="block text-2xl font-black text-rose-700">
                {resumoSla?.atrasado ?? 0}
              </span>
              <span className="text-[11px] font-medium text-rose-600/80">
                Prazo de entrega vencido
              </span>
            </button>

            {/* Card No Prazo */}
            <button
              type="button"
              onClick={() => handleSelectSlaFilter("no_prazo")}
              className={`p-4 rounded-2xl border text-left transition-all relative overflow-hidden ${
                statusSla === "no_prazo"
                  ? "bg-primary-50/90 border-primary-500 ring-2 ring-primary-500/20 shadow-xs"
                  : "bg-white border-gray-200 hover:border-primary-200 hover:bg-primary-50/30 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-primary-700">
                  No Prazo
                </span>
                <div
                  className={`p-2 rounded-xl ${statusSla === "no_prazo" ? "bg-primary-100 text-primary-700" : "bg-primary-50 text-primary-600"}`}
                >
                  <Clock className="w-4 h-4" />
                </div>
              </div>
              <span className="block text-2xl font-black text-primary-700">
                {resumoSla?.no_prazo ?? 0}
              </span>
              <span className="text-[11px] font-medium text-primary-600/80">
                Dentro do prazo de SLA
              </span>
            </button>

            {/* Card Aguardando Previsão */}
            <button
              type="button"
              onClick={() => handleSelectSlaFilter("aguardando_previsao")}
              className={`p-4 rounded-2xl border text-left transition-all relative overflow-hidden ${
                statusSla === "aguardando_previsao"
                  ? "bg-amber-50/90 border-amber-500 ring-2 ring-amber-500/20 shadow-xs"
                  : "bg-white border-gray-200 hover:border-amber-200 hover:bg-amber-50/30 shadow-2xs"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-amber-700">
                  Sem Previsão
                </span>
                <div
                  className={`p-2 rounded-xl ${statusSla === "aguardando_previsao" ? "bg-amber-100 text-amber-700" : "bg-amber-50 text-amber-600"}`}
                >
                  <Clock className="w-4 h-4" />
                </div>
              </div>
              <span className="block text-2xl font-black text-amber-700">
                {resumoSla?.aguardando_previsao ?? 0}
              </span>
              <span className="text-[11px] font-medium text-amber-700/80">
                Necessário informar prazo
              </span>
            </button>
          </div>

          {/* BARRA DE FILTROS, BUSCA E CONTROLES DE PAGINAÇÃO */}
          <div className="bg-white rounded-3xl p-5 sm:p-6 border border-gray-200 shadow-2xs space-y-4">
            <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-4">
              {/* Busca Textual */}
              <form
                onSubmit={handleSearchSubmit}
                className="flex-1 flex items-center gap-2"
              >
                <div className="relative flex-1">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={searchInput}
                    onChange={handleSearchInputChange}
                    placeholder="Buscar por colaborador, CPF ou empresa solicitante..."
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl text-xs sm:text-sm text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  />
                  {searchInput && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <button
                  type="submit"
                  className="px-4 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold transition-colors shadow-2xs flex items-center gap-1.5"
                >
                  <span>Buscar</span>
                </button>
              </form>

              {/* Filtro de Intervalo de Datas com Atalhos Rápidos */}
              <div className="flex flex-col gap-2 text-xs">
                {/* Pílulas de Seleção Rápida */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="text-gray-400 font-semibold text-[11px] mr-1">Período:</span>
                  <button
                    type="button"
                    onClick={() => handleSetQuickDate('todos')}
                    className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                      quickDate === 'todos' && !dataInicio && !dataFim
                        ? 'bg-primary-600 text-white shadow-2xs'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Todas as Datas
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetQuickDate('hoje')}
                    className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                      quickDate === 'hoje'
                        ? 'bg-primary-600 text-white shadow-2xs'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Hoje
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetQuickDate('7dias')}
                    className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                      quickDate === '7dias'
                        ? 'bg-primary-600 text-white shadow-2xs'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Últimos 7 dias
                  </button>
                  <button
                    type="button"
                    onClick={() => handleSetQuickDate('mes')}
                    className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                      quickDate === 'mes'
                        ? 'bg-primary-600 text-white shadow-2xs'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    Este Mês
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-500 font-medium">De:</span>
                    <input
                      type="date"
                      value={dataInicio}
                      onChange={(e) => {
                        setDataInicio(e.target.value);
                        setQuickDate('todos');
                      }}
                      className="bg-transparent text-gray-800 text-xs font-semibold outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-500 font-medium">Até:</span>
                    <input
                      type="date"
                      value={dataFim}
                      onChange={(e) => {
                        setDataFim(e.target.value);
                        setQuickDate('todos');
                      }}
                      className="bg-transparent text-gray-800 text-xs font-semibold outline-none"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleApplyDateFilter}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-xs font-semibold transition-colors cursor-pointer"
                  >
                    Filtrar Datas
                  </button>

                  {hasActiveFilters && (
                    <button
                      type="button"
                      onClick={handleClearAllFilters}
                      className="inline-flex items-center gap-1 px-3 py-2 text-xs font-semibold text-rose-600 hover:bg-rose-50 rounded-xl transition-colors cursor-pointer"
                    >
                      <RotateCcw className="w-3.5 h-3.5" />
                      <span>Limpar Filtros</span>
                    </button>
                  )}
                </div>
              </div>

              {/* Seletor de Ordenação por Data de Agendamento */}
              <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 text-xs">
                <ArrowUpDown className="w-3.5 h-3.5 text-gray-500 shrink-0" />
                <span className="text-gray-500 font-medium">Ordem:</span>
                <select
                  value={ordenacao}
                  onChange={(e) =>
                    handleOrdenacaoChange(
                      e.target.value as "recentes" | "antigos",
                    )
                  }
                  className="bg-transparent text-gray-800 text-xs font-bold outline-none cursor-pointer"
                >
                  <option value="recentes">Mais recentes primeiro</option>
                  <option value="antigos">Mais antigos primeiro</option>
                </select>
              </div>

              {/* Seletor de Limite de Registros por Página */}
              <div className="flex items-center gap-2 self-end lg:self-center">
                <span className="text-xs text-gray-500 font-medium">
                  Itens por pág:
                </span>
                <select
                  value={limit}
                  onChange={(e) => handleChangeLimit(Number(e.target.value))}
                  className="bg-white border border-gray-200 text-gray-700 text-xs font-bold rounded-xl px-2.5 py-1.5 outline-none focus:border-primary-500 shadow-2xs"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>
          </div>

          {/* LISTAGEM DE COLABORADORES E EXAMES PENDENTES */}
          {loading ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-gray-200 shadow-2xs space-y-3">
              <RefreshCw className="w-8 h-8 text-primary-600 animate-spin mx-auto" />
              <p className="text-sm font-semibold text-gray-700">
                Carregando atendimentos da clínica...
              </p>
            </div>
          ) : pendencias.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 text-center border border-gray-200 shadow-2xs space-y-3">
              <div className="inline-flex p-3 bg-primary-50 text-primary-600 rounded-2xl">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h3 className="text-base font-bold text-gray-900">
                Nenhum atendimento pendente encontrado!
              </h3>
              <p className="text-xs sm:text-sm text-gray-500 max-w-md mx-auto">
                {hasActiveFilters
                  ? "Nenhum resultado corresponde aos filtros aplicados. Tente redefinir os filtros para ver outros atendimentos."
                  : "Parabéns! Todos os laudos de exames desta clínica credenciada estão em dia e cadastrados."}
              </p>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={handleClearAllFilters}
                  className="mt-2 inline-flex items-center gap-1.5 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold transition-colors"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Ver todos os atendimentos</span>
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                {pendencias.map((item) => {
                  const isExpanded =
                    expandedColabs[item.colaborador_id] ?? false;
                  // Exame clínico sempre em primeiro na lista
                  const examesList = [...(item.exames ?? [])]
                    .filter((e) => {
                      const n = (e.nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                      if (n.includes('psicossocial')) return false;
                      if (
                        !e.is_clinico &&
                        (n.includes('admissional') ||
                          n.includes('periodico') ||
                          n.includes('demissional') ||
                          n.includes('retorno') ||
                          n.includes('mudanca'))
                      ) {
                        return false;
                      }
                      return true;
                    })
                    .sort((a, b) => {
                      const aClinico =
                        a.is_clinico || isAsoExam(a.nome, a.is_aso, a.is_clinico)
                          ? 1
                          : 0;
                      const bClinico =
                        b.is_clinico || isAsoExam(b.nome, b.is_aso, b.is_clinico)
                          ? 1
                          : 0;
                      return bClinico - aClinico;
                    });
                  const examesPendentesCount = examesList.filter(
                    (e) => !e.is_concluido,
                  ).length;

                  return (
                    <div
                      key={item.colaborador_id}
                      className="bg-white rounded-3xl border border-gray-200 shadow-2xs overflow-hidden transition-all"
                    >
                      {/* CABEÇALHO DO ACORDEON (COLABORADOR) */}
                      <div
                        onClick={() =>
                          toggleColaboradorAccordion(item.colaborador_id)
                        }
                        className="p-5 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer hover:bg-gray-50/60 transition-colors"
                      >
                        <div className="flex items-start sm:items-center gap-3.5">
                          <div className="p-2.5 bg-primary-50 text-primary-700 rounded-2xl shrink-0">
                            <User className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="flex flex-wrap items-center gap-2 mb-1">
                              <h3 className="text-sm sm:text-base font-black text-gray-900">
                                {item.colaborador_nome}
                              </h3>
                              {item.colaborador_cpf_mascarado && (
                                <span className="text-xs text-gray-400 font-mono">
                                  ({item.colaborador_cpf_mascarado})
                                </span>
                              )}
                              {item.tipo_exame_ocupacional && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-primary-50 text-primary-700 border border-primary-200">
                                  {item.tipo_exame_ocupacional}
                                </span>
                              )}
                              {item.pedido_codigo && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                  Pedido: {item.pedido_codigo}
                                </span>
                              )}
                              {item.data_agendamento && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 border border-gray-200">
                                  <Calendar className="w-3 h-3 text-gray-500" />
                                  Atendimento:{" "}
                                  {formatDateBR(item.data_agendamento)}
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500">
                              Empresa Solicitante:{" "}
                              <strong className="text-gray-700">
                                {item.empresa_nome}
                              </strong>
                              {item.colaborador_cargo &&
                                ` • Cargo: ${item.colaborador_cargo}`}
                            </p>
                          </div>
                        </div>

                        <div className="flex items-center justify-between md:justify-end gap-4">
                          {/* Badges Informativos */}
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-800 border border-amber-200">
                              {examesPendentesCount} a laudar
                            </span>
                            {renderSlaBadge(item.status_sla)}
                          </div>

                          <button
                            type="button"
                            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
                            title={
                              isExpanded ? "Recolher exames" : "Expandir exames"
                            }
                          >
                            {isExpanded ? (
                              <ChevronUp className="w-5 h-5" />
                            ) : (
                              <ChevronDown className="w-5 h-5" />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* CONTEÚDO DO ACORDEON (LISTA DE EXAMES DO COLABORADOR) */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 bg-gray-50/75 p-4 sm:p-6 space-y-3">
                          <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">
                            Exames Solicitados ({examesList.length})
                          </div>

                          <div className="space-y-2.5">
                            {examesList.map((ex) => {
                              const isConcluido = ex.is_concluido;
                              const isEditingPrevisao =
                                editingPrevisaoId === ex.agendamento_id;
                              const isUploading =
                                uploadingExamId === ex.agendamento_id;
                              const isAso = isAsoExam(
                                ex.nome,
                                ex.is_aso,
                                ex.is_clinico,
                              );

                              return (
                                <div
                                  key={ex.agendamento_item_id ? `item_${ex.agendamento_item_id}` : `ag_${ex.agendamento_id}`}
                                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 rounded-2xl border transition-all ${
                                    isAso
                                      ? isConcluido
                                        ? "bg-primary-50/60 border-primary-300/80 ring-1 ring-primary-300/60 shadow-2xs"
                                        : "bg-primary-50/90 border-primary-400 ring-2 ring-primary-400/40 shadow-xs hover:border-primary-500 hover:bg-primary-50"
                                      : isConcluido
                                        ? "bg-gray-50/70 border-gray-200"
                                        : "bg-white border-gray-200 hover:border-primary-300 hover:shadow-2xs"
                                  }`}
                                >
                                  {/* Informações do Exame */}
                                  <div className="flex items-start gap-3">
                                    <div
                                      className={`p-2.5 rounded-xl shrink-0 ${
                                        isAso
                                          ? isConcluido
                                            ? "bg-primary-600 text-white shadow-2xs"
                                            : "bg-primary-700 text-white shadow-2xs"
                                          : isConcluido
                                            ? "bg-primary-50 text-primary-700"
                                            : "bg-primary-50 text-primary-700"
                                      }`}
                                    >
                                      <Stethoscope className="w-4 h-4" />
                                    </div>

                                    <div className="space-y-1">
                                      <div className="flex flex-wrap items-center gap-2">
                                        <span
                                          className={`text-xs sm:text-sm font-bold ${isAso ? "text-primary-950" : "text-gray-900"}`}
                                        >
                                          {ex.nome}
                                        </span>
                                        {isAso && (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider bg-primary-700 text-white shadow-2xs">
                                            Exame Clínico (Principal)
                                          </span>
                                        )}
                                        {isConcluido ? (
                                          <div className="inline-flex items-center gap-1.5 flex-wrap">
                                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary-100 text-primary-800">
                                              <Check className="w-3 h-3 text-primary-600" />
                                              Laudado
                                            </span>
                                            {isAso &&
                                              ex.resultado?.valor &&
                                              (ex.resultado.valor
                                                .toLowerCase()
                                                .includes("temporar") ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-orange-100 text-orange-800 border border-orange-200">
                                                  <AlertTriangle className="w-3 h-3 text-orange-600" />
                                                  Inapto Temporariamente
                                                </span>
                                              ) : ex.resultado.valor
                                                  .toLowerCase()
                                                  .includes("inapto") ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-200">
                                                  <X className="w-3 h-3 text-rose-600" />
                                                  Inapto
                                                </span>
                                              ) : ex.resultado.valor
                                                  .toLowerCase()
                                                  .includes("restricao") ? (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                                  <AlertTriangle className="w-3 h-3 text-amber-600" />
                                                  Apto com Restrição
                                                </span>
                                              ) : (
                                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold bg-primary-100 text-primary-800 border border-primary-200">
                                                  <CheckCircle2 className="w-3 h-3 text-primary-600" />
                                                  Apto
                                                </span>
                                              ))}
                                          </div>
                                        ) : (
                                          renderSlaBadge(ex.status_sla)
                                        )}
                                      </div>

                                      {ex.revisao_solicitada && (
                                        <div className="my-2 p-2.5 bg-amber-50 border border-amber-300 rounded-xl flex items-start gap-2 text-xs text-amber-900 shadow-2xs animate-fade-in">
                                          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                                          <div className="space-y-0.5">
                                            <span className="font-bold text-amber-950 flex items-center gap-1.5">
                                              <span>Revisão Solicitada pela Auditoria Médica</span>
                                              <span className="px-1.5 py-0.2 rounded text-[10px] uppercase font-black bg-amber-200 text-amber-900">Correção Necessária</span>
                                            </span>
                                            <p className="text-amber-800">{ex.motivo_revisao || "O arquivo anexado precisa de correções. Por favor, anexe o laudo retificado abaixo."}</p>
                                          </div>
                                        </div>
                                      )}

                                      {/* Detalhes de Datas e SLA */}
                                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                                        <span>
                                          Realizado em:{" "}
                                          <strong className="text-gray-700">
                                            {formatDateBR(
                                              ex.resultado?.data_realizacao ||
                                                item.data_agendamento,
                                            )}
                                          </strong>
                                        </span>

                                        {/* Previsão de Entrega com Ação de Edição Inline */}
                                        {isEditingPrevisao ? (
                                          <div className="inline-flex items-center gap-1.5 bg-white p-1 rounded-xl border border-primary-300 shadow-xs">
                                            <input
                                              type="date"
                                              value={dataPrevisaoInput}
                                              onChange={(e) =>
                                                setDataPrevisaoInput(
                                                  e.target.value,
                                                )
                                              }
                                              className="text-xs font-medium text-gray-800 outline-none"
                                            />
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleSalvarPrevisao(
                                                  ex.agendamento_id,
                                                )
                                              }
                                              disabled={salvandoPrevisao}
                                              className="p-1 text-primary-600 hover:text-primary-800 rounded-lg"
                                              title="Salvar"
                                            >
                                              {salvandoPrevisao ? (
                                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                              ) : (
                                                <Check className="w-3.5 h-3.5" />
                                              )}
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => {
                                                setEditingPrevisaoId(null);
                                                setDataPrevisaoInput("");
                                              }}
                                              className="p-1 text-gray-400 hover:text-gray-600 rounded-lg"
                                              title="Cancelar"
                                            >
                                              <X className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        ) : (
                                          <div className="inline-flex items-center gap-1">
                                            <span>
                                              Previsão:{" "}
                                              <strong className="text-gray-700">
                                                {ex.data_previsao_resultado
                                                  ? formatDateBR(
                                                      ex.data_previsao_resultado,
                                                    )
                                                  : "Não informada"}
                                              </strong>
                                            </span>
                                            {!isConcluido && (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  setEditingPrevisaoId(
                                                    ex.agendamento_id,
                                                  );
                                                  setDataPrevisaoInput(
                                                    ex.data_previsao_resultado
                                                      ? ex.data_previsao_resultado.split(
                                                          "T",
                                                        )[0]
                                                      : "",
                                                  );
                                                }}
                                                className="text-[11px] text-primary-700 hover:underline font-semibold ml-1"
                                              >
                                                (Ajustar SLA)
                                              </button>
                                            )}
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Botão de Ação / Upload Direto ou Modal */}
                                  <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                                    {isConcluido ? (
                                      <div className="flex items-center gap-2">
                                        {ex.resultado?.arquivo_url && (
                                          <button
                                            type="button"
                                            onClick={() =>
                                              handleVisualizarPdf(
                                                ex.resultado?.id,
                                              )
                                            }
                                            disabled={
                                              visualizandoResultadoId ===
                                              ex.resultado?.id
                                            }
                                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-primary-50 border border-primary-300 text-primary-700 rounded-xl text-xs font-bold transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
                                            title="Visualizar laudo em PDF"
                                          >
                                            {visualizandoResultadoId ===
                                            ex.resultado?.id ? (
                                              <>
                                                <RefreshCw className="w-3.5 h-3.5 animate-spin text-primary-600" />
                                                <span>Abrindo...</span>
                                              </>
                                            ) : (
                                              <>
                                                <Eye className="w-3.5 h-3.5 text-primary-600" />
                                                <span>Visualizar PDF</span>
                                              </>
                                            )}
                                          </button>
                                        )}

                                        {/* Substituir: se for ASO abre modal com aptidão, se for complementar substitui direto */}
                                        {tipoAcesso !== "MEDICO" && (
                                          isAso ? (
                                            (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  if (item.todos_exames_concluidos || item.tem_aso || item.atendimento_finalizado) {
                                                    handleAbrirModalReabrir(
                                                      ex.agendamento_id,
                                                      item.colaborador_nome,
                                                    );
                                                    return;
                                                  }
                                                  const pendentesComp =
                                                    examesList.filter(
                                                      (e) =>
                                                        !isAsoExam(
                                                          e.nome,
                                                          e.is_aso,
                                                          e.is_clinico,
                                                        ) && !e.is_concluido,
                                                    );
                                                  if (pendentesComp.length > 0) {
                                                    setModalExamesPendentes({
                                                      colaboradorNome:
                                                        item.colaborador_nome,
                                                      exames: pendentesComp.map(
                                                        (e) => e.nome,
                                                      ),
                                                    });
                                                    return;
                                                  }

                                                  setModalUploadAso({
                                                    agendamentoId:
                                                      ex.agendamento_id,
                                                    colaboradorId:
                                                      item.colaborador_id,
                                                    colaboradorNome:
                                                      item.colaborador_nome,
                                                    colaboradorCpf:
                                                      item.colaborador_cpf_mascarado,
                                                    empresaNome: item.empresa_nome,
                                                    tipoExameOcupacional:
                                                      item.tipo_exame_ocupacional,
                                                    exameNome: ex.nome,
                                                    isSubstituicao: true,
                                                  });
                                                  const v =
                                                    ex.resultado?.valor?.toLowerCase() ||
                                                    "";
                                                  const aptidaoInicial:
                                                    | "apto"
                                                    | "inapto"
                                                    | "apto_com_restricao"
                                                    | "inapto_temporario" =
                                                    v.includes("temporar")
                                                      ? "inapto_temporario"
                                                      : v.includes("inapto")
                                                        ? "inapto"
                                                        : v.includes("restricao")
                                                          ? "apto_com_restricao"
                                                          : "apto";
                                                  setModalAptidao(aptidaoInicial);
                                                  setModalArquivo(null);
                                                  setModalObservacao(
                                                    ex.resultado?.observacao || "",
                                                  );
                                                  setModalErro(null);
                                                }}
                                                className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors"
                                              >
                                                <Paperclip className="w-3 h-3" />
                                                <span>Substituir</span>
                                              </button>
                                            )
                                          ) : (
                                            (item.todos_exames_concluidos || item.tem_aso || item.atendimento_finalizado) ? (
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  handleAbrirModalReabrir(
                                                    ex.agendamento_id,
                                                    item.colaborador_nome,
                                                  );
                                                }}
                                                className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-semibold text-rose-600 hover:text-rose-700 hover:bg-rose-50 rounded-xl cursor-pointer transition-colors"
                                                title="Atendimento já concluído com ASO emitido. Reabra o atendimento para substituir este laudo."
                                              >
                                                <RotateCcw className="w-3 h-3" />
                                                <span>Substituir</span>
                                              </button>
                                            ) : (
                                              <label className="inline-flex items-center gap-1 px-2.5 py-2 text-xs font-semibold text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-xl cursor-pointer transition-colors">
                                                <Paperclip className="w-3 h-3" />
                                                <span>Substituir</span>
                                                <input
                                                  type="file"
                                                  accept="application/pdf"
                                                  className="hidden"
                                                  onChange={(e) => {
                                                    if (
                                                      e.target.files &&
                                                      e.target.files[0]
                                                    ) {
                                                      handleUploadExamePdf(
                                                        ex.agendamento_id,
                                                        item.colaborador_id,
                                                        item.colaborador_nome,
                                                        e.target.files[0],
                                                        "laudo_emitido",
                                                        ex.agendamento_item_id,
                                                      );
                                                    }
                                                  }}
                                                />
                                              </label>
                                            )
                                          )
                                        )}
                                      </div>
                                    ) : tipoAcesso !== "MEDICO" ? (
                                      /* Exame Pendente: se ASO abre modal para informar aptidão; se complementar anexa direto */
                                      isAso ? (
                                        <div className="flex items-center gap-2 flex-wrap">
                                          {(ex.tem_anamnese || item.tem_anamnese) && (
                                            <span
                                              className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200"
                                              title="Anamnese médica realizada no sistema."
                                            >
                                              Anamnese no Sistema
                                            </span>
                                          )}
                                          <button
                                            type="button"
                                            onClick={() => {
                                              const pendentesComp =
                                                examesList.filter(
                                                  (e) =>
                                                    !isAsoExam(
                                                      e.nome,
                                                      e.is_aso,
                                                      e.is_clinico,
                                                    ) && !e.is_concluido,
                                                );
                                              if (pendentesComp.length > 0) {
                                                setModalExamesPendentes({
                                                  colaboradorNome:
                                                    item.colaborador_nome,
                                                  exames: pendentesComp.map(
                                                    (e) => e.nome,
                                                  ),
                                                });
                                                return;
                                              }

                                              setModalUploadAso({
                                                agendamentoId: ex.agendamento_id,
                                                colaboradorId: item.colaborador_id,
                                                colaboradorNome:
                                                  item.colaborador_nome,
                                                colaboradorCpf:
                                                  item.colaborador_cpf_mascarado,
                                                empresaNome: item.empresa_nome,
                                                tipoExameOcupacional:
                                                  item.tipo_exame_ocupacional,
                                                exameNome: ex.nome,
                                                isSubstituicao: false,
                                              });
                                              setModalAptidao("");
                                              setModalArquivo(null);
                                              setModalObservacao("");
                                              setModalErro(null);
                                            }}
                                            disabled={isUploading || isItemNoFuturo(item.data_agendamento)}
                                            className={`inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer ${
                                              isUploading || isItemNoFuturo(item.data_agendamento)
                                                ? "opacity-70 pointer-events-none cursor-not-allowed"
                                                : ""
                                            }`}
                                            title={isItemNoFuturo(item.data_agendamento) ? "Não é possível anexar resultados em agendamentos futuros" : "Anexar ASO"}
                                          >
                                            <UploadCloud className="w-4 h-4" />
                                            <span>Anexar ASO (PDF)</span>
                                          </button>
                                        </div>
                                      ) : (
                                        <label
                                          className={`inline-flex items-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer ${
                                            isUploading || isItemNoFuturo(item.data_agendamento)
                                              ? "opacity-70 pointer-events-none cursor-not-allowed"
                                              : ""
                                          }`}
                                          title={isItemNoFuturo(item.data_agendamento) ? "Não é possível anexar resultados em agendamentos futuros" : "Anexar Laudo PDF"}
                                        >
                                          {isUploading ? (
                                            <>
                                              <RefreshCw className="w-4 h-4 animate-spin" />
                                              <span>Enviando Laudo...</span>
                                            </>
                                          ) : (
                                            <>
                                              <UploadCloud className="w-4 h-4" />
                                              <span>Anexar Laudo PDF</span>
                                            </>
                                          )}
                                          <input
                                            type="file"
                                            accept="application/pdf"
                                            disabled={isUploading || isItemNoFuturo(item.data_agendamento)}
                                            className="hidden"
                                            onChange={(e) => {
                                              if (
                                                e.target.files &&
                                                e.target.files[0]
                                              ) {
                                                handleUploadExamePdf(
                                                  ex.agendamento_id,
                                                  item.colaborador_id,
                                                  item.colaborador_nome,
                                                  e.target.files[0],
                                                  "laudo_emitido",
                                                  ex.agendamento_item_id,
                                                );
                                              }
                                            }}
                                          />
                                        </label>
                                      )
                                    ) : (
                                      <span className="text-[11px] font-semibold text-amber-600 bg-amber-50 px-2.5 py-1 rounded-full border border-amber-200">
                                        Aguardando Laudo
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CONTROLES DE PAGINAÇÃO COMPLETA */}
              {totalColaboradores > 0 && (
                <div className="bg-white rounded-2xl p-4 border border-gray-200 shadow-2xs flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                  <div className="text-xs text-gray-500 font-medium">
                    Exibindo{" "}
                    <span className="font-bold text-gray-800">
                      {startRecord}
                    </span>{" "}
                    a{" "}
                    <span className="font-bold text-gray-800">{endRecord}</span>{" "}
                    de{" "}
                    <span className="font-bold text-gray-800">
                      {totalColaboradores}
                    </span>{" "}
                    atendimentos (Página {page} de {totalPages})
                  </div>

                  <div className="flex items-center gap-1.5">
                    {/* Botão Anterior */}
                    <button
                      type="button"
                      onClick={() => handleGoToPage(page - 1)}
                      disabled={page <= 1 || loading}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold bg-white hover:bg-primary-50 border border-primary-200 text-primary-700 disabled:opacity-40 disabled:pointer-events-none transition-colors shadow-2xs"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span className="hidden sm:inline">Anterior</span>
                    </button>

                    {/* Botões Numéricos de Página */}
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter((p) => {
                          if (totalPages <= 7) return true;
                          if (p === 1 || p === totalPages) return true;
                          if (Math.abs(p - page) <= 1) return true;
                          return false;
                        })
                        .map((p, idx, arr) => {
                          const prevP = arr[idx - 1];
                          const showEllipsis = prevP && p - prevP > 1;

                          return (
                            <React.Fragment key={p}>
                              {showEllipsis && (
                                <span className="px-1.5 text-xs text-gray-400 font-bold">
                                  ...
                                </span>
                              )}
                              <button
                                type="button"
                                onClick={() => handleGoToPage(p)}
                                disabled={loading}
                                className={`w-8 h-8 rounded-xl text-xs font-bold transition-all ${
                                  page === p
                                    ? "bg-primary-600 text-white shadow-2xs"
                                    : "bg-white hover:bg-primary-50 text-primary-700 border border-primary-200"
                                }`}
                              >
                                {p}
                              </button>
                            </React.Fragment>
                          );
                        })}
                    </div>

                    {/* Botão Próxima */}
                    <button
                      type="button"
                      onClick={() => handleGoToPage(page + 1)}
                      disabled={page >= totalPages || loading}
                      className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-bold bg-white hover:bg-primary-50 border border-primary-200 text-primary-700 disabled:opacity-40 disabled:pointer-events-none transition-colors shadow-2xs"
                    >
                      <span className="hidden sm:inline">Próxima</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        {/* CONTEÚDO DA ABA RECEPÇÃO / ENTRADA DE PACIENTES */}
        {clinicaAbaAtiva === 'recepcao' && clinica?.atende_pelo_sistema && (
          <div className="space-y-6 animate-fade-in">
            {/* Navegação entre Filas da Recepção: Entrada vs Concluídos */}
            <div className="flex items-center gap-2 border-b border-gray-200 pb-3">
              <button
                type="button"
                onClick={() => {
                  setRecepcaoSubAba('entrada');
                  setRecepcaoFiltroStatus('todos');
                  carregarRecepcao(recepcaoData, recepcaoSearch, 'entrada', recepcaoDataFim);
                }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  recepcaoSubAba === 'entrada'
                    ? 'bg-primary-600 text-white shadow-xs'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <ClipboardCheck className="w-4 h-4" />
                <span>Fila de Entrada & Check-in</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setRecepcaoSubAba('concluidos');
                  setRecepcaoFiltroStatus('todos');
                  carregarRecepcao(recepcaoData, recepcaoSearch, 'concluidos', recepcaoDataFim);
                }}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                  recepcaoSubAba === 'concluidos'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
                }`}
              >
                <CheckCircle2 className="w-4 h-4" />
                <span>Atendimentos Concluídos (Impressão de ASO)</span>
              </button>
            </div>

            {/* Cards de Resumo da Recepção */}
            {recepcaoSubAba === 'entrada' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div
                  onClick={() => setRecepcaoFiltroStatus('todos')}
                  className={`bg-white rounded-2xl p-5 border shadow-2xs flex items-center justify-between cursor-pointer transition-all hover:shadow-xs ${
                    recepcaoFiltroStatus === 'todos'
                      ? 'border-primary-500 ring-2 ring-primary-500/20 shadow-xs'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  title="Exibir todos os pacientes agendados para a data"
                >
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-500">
                      Agendados para a Data
                    </span>
                    <span className="block text-2xl font-black text-gray-900 mt-1">
                      {recepcaoItens.length}
                    </span>
                    <span className="text-2xs text-gray-400">Total na agenda</span>
                  </div>
                  <div className="p-3 bg-primary-50 text-primary-600 rounded-2xl">
                    <Users className="w-5 h-5" />
                  </div>
                </div>

                <div
                  onClick={() => setRecepcaoFiltroStatus('presentes')}
                  className={`bg-white rounded-2xl p-5 border shadow-2xs flex items-center justify-between cursor-pointer transition-all hover:shadow-xs ${
                    recepcaoFiltroStatus === 'presentes'
                      ? 'border-primary-600 ring-2 ring-primary-500/20 bg-primary-50/10 shadow-xs'
                      : 'border-gray-200 hover:border-primary-300'
                  }`}
                  title="Filtrar por pacientes que já deram entrada"
                >
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-primary-700">
                      Entradas Realizadas
                    </span>
                    <span className="block text-2xl font-black text-primary-600 mt-1">
                      {recepcaoItens.filter((i) => i.entrada_realizada).length}
                    </span>
                    <span className="text-2xs text-primary-600/80 font-medium">Liberados para atendimento</span>
                  </div>
                  <div className="p-3 bg-primary-50 text-primary-600 rounded-2xl">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                </div>

                <div
                  onClick={() => setRecepcaoFiltroStatus('aguardando')}
                  className={`bg-white rounded-2xl p-5 border shadow-2xs flex items-center justify-between cursor-pointer transition-all hover:shadow-xs ${
                    recepcaoFiltroStatus === 'aguardando'
                      ? 'border-amber-500 ring-2 ring-amber-500/20 bg-amber-50/10 shadow-xs'
                      : 'border-gray-200 hover:border-amber-300'
                  }`}
                  title="Filtrar por pacientes que ainda não chegaram"
                >
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-amber-700">
                      Aguardando Chegada
                    </span>
                    <span className="block text-2xl font-black text-amber-600 mt-1">
                      {recepcaoItens.filter((i) => !i.entrada_realizada && !i.status?.toLowerCase().includes('falt') && !i.status?.toLowerCase().includes('não compareceu')).length}
                    </span>
                    <span className="text-2xs text-amber-600/80 font-medium">Aguardando na recepção</span>
                  </div>
                  <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
                    <Clock className="w-5 h-5" />
                  </div>
                </div>

                <div
                  onClick={() => setRecepcaoFiltroStatus('faltas')}
                  className={`bg-white rounded-2xl p-5 border shadow-2xs flex items-center justify-between cursor-pointer transition-all hover:shadow-xs ${
                    recepcaoFiltroStatus === 'faltas'
                      ? 'border-rose-500 ring-2 ring-rose-500/20 bg-rose-50/50 shadow-xs'
                      : 'border-rose-200 bg-rose-50/20 hover:border-rose-300'
                  }`}
                  title="Filtrar por colaboradores que faltaram (No-Show)"
                >
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-rose-700">
                      Faltas (No-Show)
                    </span>
                    <span className="block text-2xl font-black text-rose-600 mt-1">
                      {recepcaoItens.filter((i) => i.status?.toLowerCase().includes('falt') || i.status?.toLowerCase().includes('não compareceu')).length}
                    </span>
                    <span className="text-2xs text-rose-600/80 font-medium">Não compareceram</span>
                  </div>
                  <div className="p-3 bg-rose-100 text-rose-700 rounded-2xl">
                    <UserX className="w-5 h-5" />
                  </div>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-2xl p-5 border border-emerald-200 bg-emerald-50/20 shadow-2xs flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-emerald-800">
                      Atendimentos Concluídos
                    </span>
                    <span className="block text-2xl font-black text-emerald-700 mt-1">
                      {recepcaoItens.length}
                    </span>
                    <span className="text-2xs text-emerald-600/80 font-medium">Prontos para consulta e vias</span>
                  </div>
                  <div className="p-3 bg-emerald-100 text-emerald-700 rounded-2xl">
                    <CheckCircle2 className="w-5 h-5" />
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-5 border border-primary-200 shadow-2xs flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-primary-700">
                      ASOs Emitidos / Prontos
                    </span>
                    <span className="block text-2xl font-black text-primary-600 mt-1">
                      {recepcaoItens.filter((i) => i.aso_url || i.atendimento_finalizado).length}
                    </span>
                    <span className="text-2xs text-primary-600/80 font-medium">Disponíveis para impressão</span>
                  </div>
                  <div className="p-3 bg-primary-50 text-primary-600 rounded-2xl">
                    <Printer className="w-5 h-5" />
                  </div>
                </div>

                <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-2xs flex items-center justify-between">
                  <div>
                    <span className="text-xs font-bold uppercase tracking-wider text-gray-600">
                      Exames Concluídos
                    </span>
                    <span className="block text-2xl font-black text-gray-900 mt-1">
                      {recepcaoItens.reduce((acc, i) => acc + (i.exames_concluidos_count ?? 0), 0)}
                    </span>
                    <span className="text-2xs text-gray-400">Total de procedimentos</span>
                  </div>
                  <div className="p-3 bg-gray-100 text-gray-600 rounded-2xl">
                    <FileCheck2 className="w-5 h-5" />
                  </div>
                </div>
              </div>
            )}

            {/* Filtros da Recepção */}
            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-2xs space-y-3">
              {/* Pílulas de seleção rápida de período */}
              <div className="flex items-center gap-1.5 flex-wrap text-xs">
                <span className="text-gray-400 font-semibold text-[11px] mr-1">Período:</span>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    setRecepcaoQuickDate('hoje');
                    setRecepcaoData(today);
                    setRecepcaoDataFim('');
                    carregarRecepcao(today, recepcaoSearch, recepcaoSubAba, '');
                  }}
                  className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                    recepcaoQuickDate === 'hoje'
                      ? 'bg-primary-600 text-white shadow-2xs'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Hoje
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    const d7 = new Date();
                    d7.setDate(d.getDate() - 7);
                    const ini = `${d7.getFullYear()}-${String(d7.getMonth() + 1).padStart(2, '0')}-${String(d7.getDate()).padStart(2, '0')}`;
                    setRecepcaoQuickDate('7dias');
                    setRecepcaoData(ini);
                    setRecepcaoDataFim(today);
                    carregarRecepcao(ini, recepcaoSearch, recepcaoSubAba, today);
                  }}
                  className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                    recepcaoQuickDate === '7dias'
                      ? 'bg-primary-600 text-white shadow-2xs'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Últimos 7 dias
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const d = new Date();
                    const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    const ini = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
                    setRecepcaoQuickDate('mes');
                    setRecepcaoData(ini);
                    setRecepcaoDataFim(today);
                    carregarRecepcao(ini, recepcaoSearch, recepcaoSubAba, today);
                  }}
                  className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                    recepcaoQuickDate === 'mes'
                      ? 'bg-primary-600 text-white shadow-2xs'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Este Mês
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setRecepcaoQuickDate('todos');
                    setRecepcaoData('');
                    setRecepcaoDataFim('');
                    carregarRecepcao('', recepcaoSearch, recepcaoSubAba, '');
                  }}
                  className={`px-2.5 py-1 rounded-lg font-bold text-xs transition-all cursor-pointer ${
                    recepcaoQuickDate === 'todos' && !recepcaoData && !recepcaoDataFim
                      ? 'bg-primary-600 text-white shadow-2xs'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                  }`}
                >
                  Todas as Datas
                </button>
              </div>

              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex-1 min-w-[260px] relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3.5 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    value={recepcaoSearch}
                    onChange={(e) => {
                      setRecepcaoSearch(e.target.value);
                      carregarRecepcao(recepcaoData, e.target.value, recepcaoSubAba, recepcaoDataFim);
                    }}
                    placeholder={
                      recepcaoSubAba === 'concluidos'
                        ? "Buscar concluído por colaborador, CPF ou empresa..."
                        : "Buscar por colaborador, CPF ou empresa na recepção..."
                    }
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-xs text-gray-900 font-medium focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  />
                  {recepcaoSearch && (
                    <button
                      type="button"
                      onClick={() => {
                        setRecepcaoSearch('');
                        carregarRecepcao(recepcaoData, '', recepcaoSubAba, recepcaoDataFim);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5 text-xs">
                    <Calendar className="w-3.5 h-3.5 text-gray-400" />
                    <span className="text-gray-500 font-medium">De:</span>
                    <input
                      type="date"
                      value={recepcaoData}
                      onChange={(e) => {
                        setRecepcaoData(e.target.value);
                        setRecepcaoQuickDate('todos');
                        carregarRecepcao(e.target.value, recepcaoSearch, recepcaoSubAba, recepcaoDataFim);
                      }}
                      className="bg-transparent text-gray-800 text-xs font-semibold outline-none"
                    />
                  </div>
                  {recepcaoDataFim && (
                    <div className="flex items-center gap-1.5 bg-gray-50 border border-gray-200 rounded-xl px-2.5 py-1.5 text-xs">
                      <Calendar className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-gray-500 font-medium">Até:</span>
                      <input
                        type="date"
                        value={recepcaoDataFim}
                        onChange={(e) => {
                          setRecepcaoDataFim(e.target.value);
                          setRecepcaoQuickDate('todos');
                          carregarRecepcao(recepcaoData, recepcaoSearch, recepcaoSubAba, e.target.value);
                        }}
                        className="bg-transparent text-gray-800 text-xs font-semibold outline-none"
                      />
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={() => carregarRecepcao(recepcaoData, recepcaoSearch, recepcaoSubAba, recepcaoDataFim)}
                    disabled={recepcaoLoading}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-primary-50 hover:bg-primary-100 text-primary-700 border border-primary-200 rounded-xl text-xs font-bold transition-colors cursor-pointer disabled:opacity-50"
                    title="Atualizar lista da recepção"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${recepcaoLoading ? 'animate-spin' : ''}`} />
                    <span>Atualizar</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Tabela de Agendados da Recepção */}
            <div className="bg-white rounded-3xl border border-gray-200 shadow-2xs overflow-hidden">
              {recepcaoLoading ? (
                <div className="py-20 flex flex-col items-center justify-center space-y-3">
                  <RefreshCw className="w-8 h-8 text-primary-600 animate-spin" />
                  <p className="text-xs font-semibold text-gray-500">
                    Carregando atendimentos na recepção...
                  </p>
                </div>
              ) : recepcaoItens.length === 0 ? (
                <div className="py-16 text-center space-y-3 px-4">
                  <div className="w-12 h-12 rounded-full bg-gray-100 text-gray-400 mx-auto flex items-center justify-center">
                    {recepcaoSubAba === 'concluidos' ? (
                      <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                    ) : (
                      <Users className="w-6 h-6 text-gray-400" />
                    )}
                  </div>
                  <h3 className="text-base font-bold text-gray-800">
                    {recepcaoSubAba === 'concluidos'
                      ? 'Nenhum atendimento concluído encontrado'
                      : 'Nenhum colaborador agendado para esta data'}
                  </h3>
                  <p className="text-xs text-gray-500 max-w-md mx-auto">
                    {recepcaoSubAba === 'concluidos'
                      ? 'Não foram localizados agendamentos concluídos ou com ASO emitido para o período/filtro selecionado.'
                      : 'Não encontramos agendamentos para a clínica credenciada no dia selecionado.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 text-left">
                    <thead className="bg-gray-50 select-none">
                      <tr>
                        <th
                          onClick={() => {
                            if (recepcaoSortField === 'colaborador') {
                              setRecepcaoSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setRecepcaoSortField('colaborador');
                              setRecepcaoSortOrder('asc');
                            }
                          }}
                          className="px-6 py-3.5 text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <span>Colaborador</span>
                            {recepcaoSortField === 'colaborador' ? (
                              recepcaoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => {
                            if (recepcaoSortField === 'empresa') {
                              setRecepcaoSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setRecepcaoSortField('empresa');
                              setRecepcaoSortOrder('asc');
                            }
                          }}
                          className="px-6 py-3.5 text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <span>Empresa</span>
                            {recepcaoSortField === 'empresa' ? (
                              recepcaoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => {
                            if (recepcaoSortField === 'exame') {
                              setRecepcaoSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setRecepcaoSortField('exame');
                              setRecepcaoSortOrder('asc');
                            }
                          }}
                          className="px-6 py-3.5 text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <span>Procedimento / Exame</span>
                            {recepcaoSortField === 'exame' ? (
                              recepcaoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                            )}
                          </div>
                        </th>
                        <th
                          onClick={() => {
                            if (recepcaoSortField === 'horario') {
                              setRecepcaoSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setRecepcaoSortField('horario');
                              setRecepcaoSortOrder('asc');
                            }
                          }}
                          className="px-6 py-3.5 text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <span>Data e Horário do Agendamento</span>
                            {recepcaoSortField === 'horario' ? (
                              recepcaoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                            )}
                          </div>
                        </th>
                        {recepcaoSubAba === 'concluidos' && (
                          <th className="px-6 py-3.5 text-xs font-bold text-gray-600 uppercase tracking-wider">
                            Médico Examinador
                          </th>
                        )}
                        <th className="px-6 py-3.5 text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Confirmações
                        </th>
                        <th
                          onClick={() => {
                            if (recepcaoSortField === 'status') {
                              setRecepcaoSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
                            } else {
                              setRecepcaoSortField('status');
                              setRecepcaoSortOrder('asc');
                            }
                          }}
                          className="px-6 py-3.5 text-xs font-bold text-gray-600 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center gap-1.5">
                            <span>Status de Entrada</span>
                            {recepcaoSortField === 'status' ? (
                              recepcaoSortOrder === 'asc' ? <ChevronUp className="w-3.5 h-3.5 text-primary-600" /> : <ChevronDown className="w-3.5 h-3.5 text-primary-600" />
                            ) : (
                              <ArrowUpDown className="w-3 h-3 text-gray-400 opacity-60" />
                            )}
                          </div>
                        </th>
                        <th className="px-6 py-3.5 text-right text-xs font-bold text-gray-600 uppercase tracking-wider">
                          Ação da Recepção
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {recepcaoItens
                        .filter((item) => {
                          const isFalta = item.status?.toLowerCase().includes('falt') || item.status?.toLowerCase().includes('não compareceu');
                          if (recepcaoFiltroStatus === 'presentes') {
                            if (!item.entrada_realizada) return false;
                          } else if (recepcaoFiltroStatus === 'aguardando') {
                            if (item.entrada_realizada || isFalta) return false;
                          } else if (recepcaoFiltroStatus === 'faltas') {
                            if (!isFalta) return false;
                          }

                          if (!recepcaoSearch.trim()) return true;
                          const q = recepcaoSearch.toLowerCase().trim();
                          const cleanQ = q.replace(/\D/g, '');
                          const nomeMatch = (item.colaborador_nome || '').toLowerCase().includes(q);
                          const empMatch = (item.empresa_nome || '').toLowerCase().includes(q);
                          const exameMatch = (item.tipo_exame || '').toLowerCase().includes(q);
                          const pedidoMatch = (item.pedido_codigo || '').toLowerCase().includes(q);
                          const cpfMatch = cleanQ.length > 0 && (item.colaborador_cpf || '').replace(/\D/g, '').includes(cleanQ);
                          const telMatch = cleanQ.length > 0 && (item.colaborador_telefone || '').replace(/\D/g, '').includes(cleanQ);
                          return nomeMatch || empMatch || exameMatch || pedidoMatch || cpfMatch || telMatch;
                        })
                        .sort((a, b) => {
                          let valA = '';
                          let valB = '';
                          if (recepcaoSortField === 'colaborador') {
                            valA = a.colaborador_nome || '';
                            valB = b.colaborador_nome || '';
                          } else if (recepcaoSortField === 'empresa') {
                            valA = a.empresa_nome || '';
                            valB = b.empresa_nome || '';
                          } else if (recepcaoSortField === 'exame') {
                            valA = a.tipo_exame || '';
                            valB = b.tipo_exame || '';
                          } else if (recepcaoSortField === 'horario') {
                            valA = a.recepcao_checkin_em || a.hora_agendamento || '';
                            valB = b.recepcao_checkin_em || b.hora_agendamento || '';
                          } else if (recepcaoSortField === 'status') {
                            valA = a.entrada_realizada ? '1' : '0';
                            valB = b.entrada_realizada ? '1' : '0';
                          }
                          const cmp = valA.localeCompare(valB, 'pt-BR', { numeric: true, sensitivity: 'base' });
                          return recepcaoSortOrder === 'asc' ? cmp : -cmp;
                        })
                        .map((item) => (
                        <React.Fragment key={item.agendamento_id}>
                          <tr className="hover:bg-gray-50/70 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-semibold text-gray-900 text-sm">
                                {item.colaborador_nome}
                              </div>
                              <div className="text-xs text-gray-500 font-mono mt-0.5">
                                CPF: {formatCpf(item.colaborador_cpf)}
                              </div>
                              {item.colaborador_telefone && (
                                <div className="text-xs text-gray-500 font-mono mt-0.5 flex items-center gap-1">
                                  <Phone className="w-3 h-3 text-emerald-600" />
                                  <span>{formatPhone(item.colaborador_telefone)}</span>
                                </div>
                              )}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="font-medium text-gray-800 text-sm">
                                {item.empresa_nome}
                              </div>
                              <div className="text-xs text-gray-400 font-mono mt-0.5">
                                CNPJ: {formatCnpj(item.empresa_cnpj)}
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                                {item.tipo_exame}
                              </span>
                              {item.pedido_codigo && (
                                <div className="mt-1">
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    Pedido: {item.pedido_codigo}
                                  </span>
                                </div>
                              )}
                              {(() => {
                                const examesValidos = (item.exames ?? []).filter((e) => {
                                  const n = (e.nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                                  if (n.includes('psicossocial')) return false;
                                  if (
                                    !e.is_clinico &&
                                    (n.includes('admissional') ||
                                      n.includes('periodico') ||
                                      n.includes('demissional') ||
                                      n.includes('retorno') ||
                                      n.includes('mudanca'))
                                  ) {
                                    return false;
                                  }
                                  return true;
                                });
                                return examesValidos.length > 0 ? (
                                  <div className="mt-1.5 flex items-center gap-1.5">
                                    <button
                                      type="button"
                                      onClick={() => toggleRecepcaoExpand(item.agendamento_id)}
                                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer"
                                      title="Clique para ver ou gerenciar os exames deste colaborador"
                                    >
                                      <span>{item.exames_concluidos_count ?? 0}/{examesValidos.length} exames</span>
                                      {expandedRecepcaoIds.has(item.agendamento_id) ? (
                                        <ChevronUp className="w-3 h-3 text-slate-500" />
                                      ) : (
                                        <ChevronDown className="w-3 h-3 text-slate-500" />
                                      )}
                                    </button>
                                  </div>
                                ) : null;
                              })()}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600 font-medium">
                              <div className="flex flex-col gap-0.5">
                                <div className="flex items-center gap-1.5 font-bold text-slate-800">
                                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                                  <span>{item.data_agendamento ? new Date(item.data_agendamento + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}</span>
                                </div>
                                <div className="flex items-center gap-1.5 text-slate-500">
                                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                                  <span>{item.hora_agendamento || 'Ordem de Chegada'}</span>
                                </div>
                              </div>
                            </td>

                            {recepcaoSubAba === 'concluidos' && (
                              <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-700">
                                {item.medico_examinador_nome ? (
                                  <div>
                                    <div className="font-bold text-slate-900">{item.medico_examinador_nome}</div>
                                    {item.medico_examinador_crm && (
                                      <div className="text-2xs text-slate-500 font-mono">CRM: {item.medico_examinador_crm}</div>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 italic">Não informado</span>
                                )}
                              </td>
                            )}

                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="space-y-1.5">
                                {/* Confirmou intenção de ir */}
                                <div className="flex items-center gap-1.5">
                                  {item.colaborador_confirmou ? (
                                    <div className="flex items-center gap-1 text-xs text-primary-700 font-medium">
                                      <CheckCircle2 className="w-3 h-3" />
                                      <span>
                                        Confirmou ida
                                        {item.colaborador_confirmou_em && (
                                          <span className="font-mono text-gray-500 ml-1">
                                            ({new Date(item.colaborador_confirmou_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })})
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 text-xs text-gray-400">
                                      <Clock className="w-3 h-3" />
                                      <span>Sem confirmação de ida</span>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Confirmou Presença */}
                                <div className="flex items-center gap-1.5">
                                  {item.confirmou_presenca ? (
                                    <div className="flex items-center gap-1 text-xs text-green-700 font-medium">
                                      <CheckCircle2 className="w-3 h-3" />
                                      <span>
                                        Provou presença
                                        {item.presenca_em && (
                                          <span className="font-mono text-gray-500 ml-1">
                                            ({new Date(item.presenca_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })})
                                          </span>
                                        )}
                                      </span>
                                    </div>
                                  ) : (
                                    <div className="flex items-center gap-1 text-xs text-gray-400">
                                      <Clock className="w-3 h-3" />
                                      <span>Sem prova de presença</span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap">
                              {(() => {
                                const isFalta = item.status?.toLowerCase().includes('falt') || item.status?.toLowerCase().includes('não compareceu');
                                if (isFalta) {
                                  return (
                                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-50 text-rose-800 border border-rose-200">
                                      <UserX className="w-3.5 h-3.5 text-rose-600" />
                                      <span>Não Compareceu (Falta)</span>
                                    </span>
                                  );
                                }
                                return (
                                  <>
                                    {item.is_proximo && (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-100 text-primary-800 animate-pulse border border-primary-200 mb-1">
                                        Próximo da Fila
                                      </span>
                                    )}
                                    {item.atendimento_finalizado || item.aso_url ? (
                                      <div className="space-y-1">
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200">
                                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                          <span>Atendimento Concluído</span>
                                        </span>
                                        {item.recepcao_checkin_em && (
                                          <div className="text-2xs text-gray-400">
                                            Entrada às {new Date(item.recepcao_checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                          </div>
                                        )}
                                        {(item.biometria_coletada || item.biometria_facial_url) && (
                                          <div className="text-2xs text-emerald-700 flex items-center gap-1 font-semibold">
                                            <Check className="w-3 h-3 text-emerald-600" />
                                            <span>Biometria Facial</span>
                                          </div>
                                        )}
                                      </div>
                                    ) : item.entrada_realizada ? (
                                      <div className="space-y-1">
                                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary-50 text-primary-800 border border-primary-200">
                                          <CheckCircle2 className="w-3 h-3 text-primary-600" />
                                          <span>Presente / Liberado</span>
                                        </span>
                                        {item.recepcao_checkin_em && (
                                          <div className="text-2xs text-gray-400">
                                            Entrada às {new Date(item.recepcao_checkin_em).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                          </div>
                                        )}
                                        {(item.biometria_coletada || item.biometria_facial_url) && (
                                          <div className="text-2xs text-emerald-700 flex items-center gap-1 font-semibold">
                                            <Check className="w-3 h-3 text-emerald-600" />
                                            <span>Biometria Facial</span>
                                          </div>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-800 border border-amber-200">
                                        <Clock className="w-3 h-3 text-amber-600" />
                                        <span>Aguardando Chegada</span>
                                      </span>
                                    )}
                                  </>
                                );
                              })()}
                            </td>

                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                              <div className="flex items-center justify-end gap-2">
                                {(() => {
                                  const isFalta = item.status?.toLowerCase().includes('falt') || item.status?.toLowerCase().includes('não compareceu');

                                  if (isFalta) {
                                    return (
                                      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-rose-50 text-rose-700 border border-rose-200 rounded-xl text-xs font-bold" title="Falta registrada na recepção">
                                        <UserX className="w-3.5 h-3.5 text-rose-600" />
                                        <span>Falta Registrada</span>
                                      </div>
                                    );
                                  }

                                  return (
                                    <>
                                      {/* Botão Gerenciar Exames */}
                                      {item.exames && item.exames.length > 0 && (
                                        <button
                                          type="button"
                                          onClick={() => toggleRecepcaoExpand(item.agendamento_id)}
                                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 font-bold rounded-xl text-xs transition-all shadow-xs cursor-pointer hover:shadow-sm ${
                                            expandedRecepcaoIds.has(item.agendamento_id)
                                              ? 'bg-primary-100 text-primary-800 border border-primary-300'
                                              : 'bg-white border border-slate-200 hover:bg-slate-50 text-slate-700'
                                          }`}
                                          title="Gerenciar exames individuais, SLA e upload de laudos"
                                        >
                                          <FileText className="w-3.5 h-3.5 text-primary-600" />
                                          <span>Exames ({item.exames_concluidos_count ?? 0}/{item.exames_total_count ?? item.exames.length})</span>
                                          {expandedRecepcaoIds.has(item.agendamento_id) ? (
                                            <ChevronUp className="w-3 h-3 text-slate-500" />
                                          ) : (
                                            <ChevronDown className="w-3 h-3 text-slate-500" />
                                          )}
                                        </button>
                                      )}

                                      {/* Imprimir Kit Option */}
                                      {!item.atendimento_finalizado && !item.aso_url && (
                                        <button
                                          type="button"
                                          disabled={downloadingKitId === item.agendamento_id}
                                          onClick={() => handleDownloadKit(item.pedido_id || item.agendamento_id, item.colaborador_id, item.agendamento_id)}
                                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-xs transition-all shadow-xs cursor-pointer hover:shadow-sm disabled:opacity-50"
                                          title="Imprimir Kit do Exame (Guia, Ficha e 3 Vias do ASO)"
                                        >
                                          {downloadingKitId === item.agendamento_id ? (
                                            <>
                                              <RefreshCw className="w-3.5 h-3.5 animate-spin text-primary-600" />
                                              <span>Gerando Kit...</span>
                                            </>
                                          ) : (
                                            <>
                                              <Printer className="w-3.5 h-3.5" />
                                              <span>Imprimir Kit</span>
                                            </>
                                          )}
                                        </button>
                                      )}

                                      {item.atendimento_finalizado || item.aso_url ? (
                                        <div className="flex items-center gap-1.5">
                                          <button
                                            type="button"
                                            onClick={() => handleDownloadAso(item.agendamento_id)}
                                            disabled={visualizandoAsoId === item.agendamento_id}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white font-bold rounded-xl text-xs transition-all shadow-xs cursor-pointer hover:shadow-sm disabled:opacity-50"
                                            title="Visualizar e imprimir o ASO do colaborador"
                                          >
                                            {visualizandoAsoId === item.agendamento_id ? (
                                              <>
                                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                <span>Abrindo...</span>
                                              </>
                                            ) : (
                                              <>
                                                <Printer className="w-3.5 h-3.5" />
                                                <span>Imprimir ASO</span>
                                              </>
                                            )}
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => handleForcarGerarAso(item.agendamento_id)}
                                            disabled={gerandoAsoId === item.agendamento_id}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-800 border border-amber-300 rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer disabled:opacity-50"
                                            title="Reemitir e assinar digitalmente o ASO deste atendimento"
                                          >
                                            {gerandoAsoId === item.agendamento_id ? (
                                              <>
                                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                <span>Reemitindo...</span>
                                              </>
                                            ) : (
                                              <>
                                                <RotateCcw className="w-3.5 h-3.5 text-amber-700" />
                                                <span>Reemitir ASO</span>
                                              </>
                                            )}
                                          </button>
                                        </div>
                                      ) : item.entrada_realizada ? (
                                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 text-primary-700 border border-primary-200 rounded-xl text-xs font-bold">
                                          <Check className="w-3.5 h-3.5 text-primary-600" />
                                          <span>Liberado ao Médico</span>
                                        </span>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            setCheckinModalItem(item);
                                          }}
                                          className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white font-bold rounded-xl text-xs transition-all shadow-xs hover:shadow-sm cursor-pointer`}
                                          title="Confirmar dados do colaborador e dar entrada na fila de atendimento"
                                        >
                                          <ClipboardCheck className="w-3.5 h-3.5" />
                                          <span>Dar Entrada</span>
                                        </button>
                                      )}

                                      {/* Botão Registrar Falta / No-Show */}
                                      {!item.atendimento_finalizado && !item.aso_url && (
                                        <button
                                          type="button"
                                          disabled={registrandoFaltaId === item.agendamento_id}
                                          onClick={() => handleRegistrarFalta(item.agendamento_id, item.colaborador_nome)}
                                          className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-rose-50 border border-rose-200 hover:bg-rose-100 text-rose-700 font-bold rounded-xl text-xs transition-all shadow-xs cursor-pointer hover:shadow-sm disabled:opacity-50"
                                          title="Registrar que o colaborador não compareceu (Falta / No-Show)"
                                        >
                                          {registrandoFaltaId === item.agendamento_id ? (
                                            <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                          ) : (
                                            <UserX className="w-3.5 h-3.5" />
                                          )}
                                          <span>Falta</span>
                                        </button>
                                      )}

                                      {/* Upload ASO Option */}
                                      {!item.aso_url && (
                                        <button
                                          type="button"
                                          onClick={() => {
                                            const pendentesComp = (item.exames ?? []).filter(
                                              (e) => !e.is_clinico && e.status !== 'CONCLUIDO' && !e.realizado,
                                            );
                                            if (pendentesComp.length > 0) {
                                              setModalExamesPendentes({
                                                colaboradorNome: item.colaborador_nome,
                                                exames: pendentesComp.map((e) => e.nome),
                                              });
                                              return;
                                            }

                                            setModalUploadAso({
                                              agendamentoId: item.agendamento_id,
                                              colaboradorId: item.colaborador_id,
                                              colaboradorNome: item.colaborador_nome,
                                              colaboradorCpf: item.colaborador_cpf,
                                              empresaNome: item.empresa_nome,
                                              tipoExameOcupacional: item.tipo_exame,
                                              exameNome: item.tipo_exame,
                                              isSubstituicao: false,
                                            });
                                            setModalAptidao("");
                                            setModalArquivo(null);
                                            setModalObservacao("");
                                            setModalErro(null);
                                          }}
                                          disabled={isItemNoFuturo(item.data_agendamento)}
                                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-200 hover:border-slate-300 hover:bg-slate-50 text-slate-700 font-bold rounded-xl text-xs transition-all shadow-xs cursor-pointer hover:shadow-sm ${isItemNoFuturo(item.data_agendamento) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                          title={isItemNoFuturo(item.data_agendamento) ? "Não é possível anexar resultados em agendamentos futuros" : "Fazer Upload do ASO"}
                                        >
                                          <UploadCloud className="w-3.5 h-3.5" />
                                          <span>Anexar ASO</span>
                                        </button>
                                      )}

                                      {/* Indicador de Atendimento no Sistema */}
                                      {!item.atendimento_finalizado && !item.aso_url && item.tem_anamnese && (
                                        <span
                                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 text-emerald-700 font-medium rounded-xl text-[11px] border border-emerald-200"
                                          title="Atendimento médico realizado no sistema."
                                        >
                                          <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                          <span>Anamnese no Sistema</span>
                                        </span>
                                      )}
                                    </>
                                  );
                                })()}
                              </div>
                            </td>
                          </tr>

                          {/* Sub-tabela Expandida dos Exames Individuais */}
                          {expandedRecepcaoIds.has(item.agendamento_id) && (() => {
                            const examesValidos = (item.exames ?? []).filter((e) => {
                              const n = (e.nome || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
                              if (n.includes('psicossocial')) return false;
                              if (
                                !e.is_clinico &&
                                (n.includes('admissional') ||
                                  n.includes('periodico') ||
                                  n.includes('demissional') ||
                                  n.includes('retorno') ||
                                  n.includes('mudanca'))
                              ) {
                                return false;
                              }
                              return true;
                            });

                            return (
                              <tr className="bg-slate-50/80 border-b border-slate-200">
                                <td colSpan={recepcaoSubAba === 'concluidos' ? 8 : 7} className="px-6 py-4">
                                  <div className="bg-white rounded-xl border border-slate-200 shadow-xs p-4 space-y-3">
                                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-100">
                                      <div className="flex items-center gap-2">
                                        <FileText className="w-4 h-4 text-primary-600" />
                                        <span className="font-bold text-xs uppercase tracking-wider text-slate-800">
                                          Exames do Agendamento — {item.colaborador_nome}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-50 text-primary-700 border border-primary-200">
                                          {item.exames_concluidos_count ?? 0} de {examesValidos.length} realizados
                                        </span>
                                        <span className="text-2xs text-slate-400">
                                          Marque a realização, defina o SLA ou suba os laudos
                                        </span>
                                      </div>
                                    </div>

                                    {examesValidos.length > 0 ? (
                                      <div className="divide-y divide-slate-100">
                                        {examesValidos.map((ex) => (
                                        <div
                                          key={ex.id || ex.agendamento_id}
                                          className="py-3 flex flex-wrap items-center justify-between gap-3 text-xs"
                                        >
                                          {/* Informações do Exame */}
                                          <div className="flex items-center gap-2.5 min-w-[240px]">
                                            <span
                                              className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${
                                                ex.is_clinico
                                                  ? 'bg-blue-50 text-blue-700 border border-blue-200'
                                                  : ex.tipo === 'LABORATORIAL'
                                                  ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                                  : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                              }`}
                                            >
                                              {ex.is_clinico
                                                ? 'Clínico (ASO)'
                                                : ex.tipo === 'LABORATORIAL'
                                                ? 'Laboratorial'
                                                : 'Complementar'}
                                            </span>
                                            <div>
                                              <div className="font-semibold text-slate-900">
                                                {ex.nome}
                                              </div>
                                              <div className="text-[11px] text-slate-400">
                                                Status: <span className="font-medium text-slate-600">{ex.status}</span>
                                              </div>
                                            </div>
                                          </div>

                                          {/* Ações da Recepção para este Exame */}
                                          <div className="flex items-center flex-wrap gap-2.5">
                                            {/* Botão Fez / Marcar Realizado */}
                                            <button
                                              type="button"
                                              onClick={() =>
                                                handleToggleExameRealizado(
                                                  ex.agendamento_id,
                                                  Boolean(ex.realizado),
                                                  ex.is_clinico ? undefined : ex.id,
                                                )
                                              }
                                              disabled={togglingExameId === (ex.is_clinico ? ex.agendamento_id : ex.id)}
                                              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-bold text-xs transition-all cursor-pointer ${
                                                ex.realizado
                                                  ? 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800 border border-emerald-300'
                                                  : 'bg-white hover:bg-slate-100 text-slate-700 border border-slate-300 shadow-xs'
                                              }`}
                                              title={ex.realizado ? 'Clique para desmarcar' : 'Clique para marcar que o paciente realizou o exame'}
                                            >
                                              {togglingExameId === (ex.is_clinico ? ex.agendamento_id : ex.id) ? (
                                                <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                              ) : ex.realizado ? (
                                                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                                              ) : (
                                                <div className="w-3.5 h-3.5 rounded border border-slate-400" />
                                              )}
                                              <span>{ex.realizado ? 'Fez' : 'Marcar Fez'}</span>
                                              {ex.data_realizacao && (
                                                <span className="text-[10px] opacity-75 font-mono">
                                                  ({new Date(ex.data_realizacao + 'T12:00:00').toLocaleDateString('pt-BR')})
                                                </span>
                                              )}
                                            </button>

                                            {/* Controle de SLA / Previsão */}
                                            <div className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 rounded-xl px-2.5 py-1">
                                              <Clock className="w-3.5 h-3.5 text-slate-400" />
                                              <span className="text-[11px] text-slate-500 font-medium">SLA:</span>
                                              <input
                                                type="date"
                                                defaultValue={ex.data_previsao_resultado || ''}
                                                onBlur={(e) => {
                                                  if (e.target.value !== (ex.data_previsao_resultado || '')) {
                                                    handlePrevisaoExameRecepcao(ex.agendamento_id, e.target.value);
                                                  }
                                                }}
                                                className="text-xs bg-transparent border-0 p-0 text-slate-700 font-medium focus:ring-0 focus:outline-none cursor-pointer"
                                                title="Data prevista para liberação do resultado"
                                              />
                                            </div>

                                            {/* Upload Laudo / Download */}
                                            {ex.resultado_url ? (
                                              <button
                                                type="button"
                                                onClick={() => ex.resultado_id && handleVisualizarPdf(ex.resultado_id)}
                                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-xl text-xs transition-colors border border-slate-200 cursor-pointer shadow-xs"
                                                title="Baixar Laudo"
                                              >
                                                <Download className="w-3.5 h-3.5 text-primary-600" />
                                                <span>Ver Laudo</span>
                                              </button>
                                            ) : ex.is_clinico && item.tem_anamnese ? (
                                              <span className="text-[11px] font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                                                Consulta no sistema
                                              </span>
                                            ) : (
                                              <label
                                                className={`inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 font-bold rounded-xl text-xs transition-colors border border-primary-200 cursor-pointer shadow-xs ${
                                                  uploadingExamId === ex.agendamento_id ? 'opacity-50 pointer-events-none' : ''
                                                }`}
                                                title="Anexar PDF do Laudo deste exame"
                                              >
                                                {uploadingExamId === ex.agendamento_id ? (
                                                  <>
                                                    <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                                                    <span>Enviando...</span>
                                                  </>
                                                ) : (
                                                  <>
                                                    <UploadCloud className="w-3.5 h-3.5 text-primary-600" />
                                                    <span>Subir Exame</span>
                                                    <input
                                                      type="file"
                                                      accept="application/pdf"
                                                      className="hidden"
                                                      onChange={(e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                          handleUploadExamePdf(
                                                            ex.agendamento_id,
                                                            item.colaborador_id,
                                                            item.colaborador_nome,
                                                            file,
                                                            undefined,
                                                            ex.agendamento_item_id,
                                                          );
                                                        }
                                                        e.target.value = '';
                                                      }}
                                                    />
                                                  </>
                                                )}
                                              </label>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <div className="text-xs text-slate-400 italic py-2">
                                      Nenhum exame detalhado disponível para este agendamento.
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })()}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* CONTEÚDO DA ABA APURAÇÃO DE REPASSES E FATURAMENTO */}
        {clinicaAbaAtiva === 'repasses' && (
          <RepassesClinicaView clinicaId={clinica?.id} clinicaNome={clinica?.nome} />
        )}
      </div>
      </PageContainer>
      </main>

      {/* Modal de Recepção: Confirmação de Celular e Biometria Facial */}
      {checkinModalItem && clinica && (
        <RecepcaoCheckinModal
          key={checkinModalItem.agendamento_id}
          isOpen={!!checkinModalItem}
          onClose={() => setCheckinModalItem(null)}
          item={checkinModalItem}
          clinicaId={clinica.id}
          exigeBiometria={clinica.biometria_facial_recepcao !== false}
          onSuccess={(res) => {
            setSuccessToast(
              res.message || 'Entrada registrada com sucesso! Paciente liberado para o médico.',
            );
            const agendamentoId = checkinModalItem.agendamento_id;
            setRecepcaoItens((prev) =>
              prev.map((it) =>
                it.agendamento_id === agendamentoId
                  ? {
                      ...it,
                      entrada_realizada: true,
                      status: 'Presente / Liberado',
                      recepcao_checkin_em: res.recepcao_checkin_em,
                      biometria_coletada: res.biometria_coletada ?? it.biometria_coletada,
                      biometria_facial_url: res.biometria_facial_url ?? it.biometria_facial_url,
                      colaborador_telefone: res.colaborador_telefone ?? it.colaborador_telefone,
                      data_agendamento: res.data_agendamento ?? it.data_agendamento,
                    }
                  : it,
              ),
            );
            setCheckinModalItem(null);
          }}
        />
      )}

      {/* MODAL DE ALERTA: EXAMES COMPLEMENTARES PENDENTES */}
      {modalExamesPendentes && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full border border-gray-200 shadow-2xl relative animate-fade-in">
            <button
              type="button"
              onClick={() => setModalExamesPendentes(null)}
              className="absolute right-5 top-5 p-1 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-amber-50 text-amber-700 rounded-2xl">
                <AlertTriangle className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900">
                  Exames Complementares Pendentes
                </h3>
                <p className="text-xs text-gray-500">
                  Pré-requisito para conclusão do ASO
                </p>
              </div>
            </div>

            <div className="space-y-3 mb-6 text-xs text-gray-600 leading-relaxed">
              <p>
                Não é permitido anexar ou concluir o Atestado de Saúde
                Ocupacional (ASO) de{" "}
                <strong className="text-gray-900">
                  {modalExamesPendentes.colaboradorNome}
                </strong>{" "}
                sem antes realizar o upload dos resultados de todos os exames
                complementares solicitados.
              </p>
              <div className="bg-amber-50/70 border border-amber-200 rounded-2xl p-3.5 space-y-2">
                <span className="font-bold text-[11px] uppercase tracking-wider text-amber-900 block">
                  Exames pendentes de laudo (
                  {modalExamesPendentes.exames.length}):
                </span>
                <ul className="space-y-1.5">
                  {modalExamesPendentes.exames.map((exNome, idx) => (
                    <li
                      key={idx}
                      className="flex items-center gap-2 text-amber-950 font-medium"
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />
                      <span>{exNome}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <p className="text-[11px] text-gray-500">
                Por favor, anexe primeiro os arquivos dos exames complementares
                acima na lista de exames. Assim que todos estiverem laudados, o
                botão de emissão do ASO será liberado.
              </p>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => setModalExamesPendentes(null)}
                className="w-full sm:w-auto px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold transition shadow-xs cursor-pointer"
              >
                Entendido, laudar exames complementares
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE UPLOAD DE ASO COM PARECER DE APTIDÃO */}
      {modalUploadAso && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-lg w-full border border-gray-200 shadow-2xl relative animate-fade-in max-h-[90vh] overflow-y-auto">
            <button
              type="button"
              onClick={() => {
                if (uploadingExamId) return;
                setModalUploadAso(null);
                setModalArquivo(null);
                setModalAptidao("");
                setModalObservacao("");
                setModalErro(null);
              }}
              className="absolute right-5 top-5 p-1 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Cabeçalho do Modal */}
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary-50 text-primary-700 rounded-2xl">
                <Stethoscope className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  {modalUploadAso.isSubstituicao
                    ? "Substituir ASO"
                    : "Upload do ASO"}{" "}
                  - {modalUploadAso.exameNome}
                </h3>
                <p className="text-xs text-gray-500">
                  Informe o parecer de aptidão e anexe o Atestado de Saúde
                  Ocupacional em PDF.
                </p>
              </div>
            </div>

            {/* Resumo do Colaborador / Empresa */}
            <div className="mb-5 p-3.5 bg-gray-50 border border-gray-200 rounded-2xl text-xs space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Colaborador:</span>
                <span className="font-bold text-gray-900">
                  {modalUploadAso.colaboradorNome}
                </span>
              </div>
              {modalUploadAso.colaboradorCpf && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">CPF:</span>
                  <span className="font-mono text-gray-700">
                    {formatCpf(modalUploadAso.colaboradorCpf)}
                  </span>
                </div>
              )}
              {modalUploadAso.empresaNome && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Empresa:</span>
                  <span className="font-semibold text-gray-700 truncate max-w-[280px]">
                    {modalUploadAso.empresaNome}
                  </span>
                </div>
              )}
              {modalUploadAso.tipoExameOcupacional && (
                <div className="flex justify-between items-center">
                  <span className="text-gray-500">Tipo de Exame:</span>
                  <span className="font-bold text-primary-700">
                    {modalUploadAso.tipoExameOcupacional}
                  </span>
                </div>
              )}
            </div>

            {modalErro && (
              <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 font-medium flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-rose-600 shrink-0" />
                <span>{modalErro}</span>
              </div>
            )}

            <form onSubmit={handleConfirmarUploadAso} className="space-y-5">
              {/* Parecer de Aptidão (Obrigatório) */}
              <FormField
                label="Parecer de Aptidão"
                required
                error={modalErro && !modalAptidao ? "Selecione o parecer de aptidão" : undefined}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {/* Apto */}
                  <button
                    type="button"
                    onClick={() => {
                      setModalAptidao("apto");
                      setModalErro(null);
                    }}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between transition-all ${
                      modalAptidao === "apto"
                        ? "border-primary-500 bg-primary-50/70 ring-2 ring-primary-500"
                        : "border-gray-200 hover:border-primary-300 hover:bg-primary-50/30"
                    }`}
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <span className="font-bold text-xs text-primary-800">
                        Apto
                      </span>
                      <CheckCircle2
                        className={`w-4 h-4 ${
                          modalAptidao === "apto"
                            ? "text-primary-600"
                            : "text-gray-300"
                        }`}
                      />
                    </div>
                    <span className="text-[11px] text-primary-700/80">
                      Sem restrições para a função
                    </span>
                  </button>

                  {/* Apto com Restrições */}
                  <button
                    type="button"
                    onClick={() => {
                      setModalAptidao("apto_com_restricao");
                      setModalErro(null);
                    }}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between transition-all ${
                      modalAptidao === "apto_com_restricao"
                        ? "border-amber-500 bg-amber-50/70 ring-2 ring-amber-500"
                        : "border-gray-200 hover:border-amber-300 hover:bg-amber-50/30"
                    }`}
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <span className="font-bold text-xs text-amber-800">
                        Apto com Restrições
                      </span>
                      <AlertTriangle
                        className={`w-4 h-4 ${
                          modalAptidao === "apto_com_restricao"
                            ? "text-amber-600"
                            : "text-gray-300"
                        }`}
                      />
                    </div>
                    <span className="text-[11px] text-amber-700/80">
                      Apto com recomendações médicas
                    </span>
                  </button>

                  {/* Inapto Temporariamente */}
                  <button
                    type="button"
                    onClick={() => {
                      setModalAptidao("inapto_temporario");
                      setModalErro(null);
                    }}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between transition-all ${
                      modalAptidao === "inapto_temporario"
                        ? "border-orange-500 bg-orange-50/70 ring-2 ring-orange-500"
                        : "border-gray-200 hover:border-orange-300 hover:bg-orange-50/30"
                    }`}
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <span className="font-bold text-xs text-orange-800">
                        Inapto Temporariamente
                      </span>
                      <AlertTriangle
                        className={`w-4 h-4 ${
                          modalAptidao === "inapto_temporario"
                            ? "text-orange-600"
                            : "text-gray-300"
                        }`}
                      />
                    </div>
                    <span className="text-[11px] text-orange-700/80">
                      Inaptidão temporária (reavaliação)
                    </span>
                  </button>

                  {/* Inapto */}
                  <button
                    type="button"
                    onClick={() => {
                      setModalAptidao("inapto");
                      setModalErro(null);
                    }}
                    className={`p-3 rounded-2xl border text-left flex flex-col justify-between transition-all ${
                      modalAptidao === "inapto"
                        ? "border-rose-500 bg-rose-50/70 ring-2 ring-rose-500"
                        : "border-gray-200 hover:border-rose-300 hover:bg-rose-50/30"
                    }`}
                  >
                    <div className="flex items-center justify-between w-full mb-1">
                      <span className="font-bold text-xs text-rose-800">
                        Inapto
                      </span>
                      <X
                        className={`w-4 h-4 ${
                          modalAptidao === "inapto"
                            ? "text-rose-600"
                            : "text-gray-300"
                        }`}
                      />
                    </div>
                    <span className="text-[11px] text-rose-700/80">
                      Inaptidão definitiva para a função
                    </span>
                  </button>
                </div>
              </FormField>

              {/* Anexo do PDF do ASO (Obrigatório) */}
              <FormField
                label="Arquivo do ASO (PDF)"
                required
                error={modalErro && !modalArquivo ? "Selecione o arquivo do ASO em PDF" : undefined}
              >
                {modalArquivo ? (
                  <div className="flex items-center justify-between p-3.5 bg-primary-50/60 border border-primary-200 rounded-2xl">
                    <div className="flex items-center gap-3 overflow-hidden">
                      <div className="p-2 bg-primary-100 text-primary-700 rounded-xl shrink-0">
                        <FileText className="w-5 h-5" />
                      </div>
                      <div className="overflow-hidden">
                        <p className="text-xs font-bold text-gray-900 truncate">
                          {modalArquivo.name}
                        </p>
                        <p className="text-[11px] text-gray-500">
                          {(modalArquivo.size / 1024).toFixed(1)} KB • PDF
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setModalArquivo(null)}
                      className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg transition-colors ml-2"
                      title="Remover arquivo"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-gray-300 hover:border-primary-500 rounded-2xl bg-gray-50/50 hover:bg-primary-50/30 cursor-pointer transition-all">
                    <UploadCloud className="w-8 h-8 text-primary-600 mb-2" />
                    <span className="text-xs font-semibold text-gray-700">
                      Clique para selecionar o PDF do ASO
                    </span>
                    <span className="text-[11px] text-gray-400 mt-0.5">
                      Apenas arquivos .pdf de até 10MB
                    </span>
                    <input
                      type="file"
                      accept="application/pdf"
                      className="hidden"
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          const file = e.target.files[0];
                          if (file.type !== "application/pdf") {
                            setModalErro(
                              "Por favor, selecione um arquivo no formato PDF.",
                            );
                            return;
                          }
                          setModalArquivo(file);
                          setModalErro(null);
                        }
                      }}
                    />
                  </label>
                )}
              </FormField>

              {/* Observação Opcional */}
              <FormField label="Observações Médicas / Recomendações (Opcional)">
                <textarea
                  value={modalObservacao}
                  onChange={(e) => setModalObservacao(e.target.value)}
                  placeholder="Ex: Uso obrigatório de EPI auditivo; restrição para trabalho em altura..."
                  rows={2}
                  className="w-full px-3.5 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-xs font-medium focus:ring-2 focus:ring-primary-500 outline-none resize-none"
                />
              </FormField>

              {/* Ações */}
              <div className="pt-2 flex items-center justify-end gap-2.5">
                <button
                  type="button"
                  disabled={uploadingExamId !== null}
                  onClick={() => {
                    setModalUploadAso(null);
                    setModalArquivo(null);
                    setModalAptidao("");
                    setModalObservacao("");
                    setModalErro(null);
                  }}
                  className="px-4 py-2.5 border border-primary-200 text-primary-700 bg-white hover:bg-primary-50 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    !modalAptidao || !modalArquivo || uploadingExamId !== null
                  }
                  className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 active:bg-primary-800 text-white rounded-xl text-xs font-bold transition-colors shadow-2xs flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadingExamId === modalUploadAso.agendamentoId ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Enviando ASO...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-3.5 h-3.5" />
                      <span>Confirmar e Enviar ASO</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Solicitação de Contato Ativo (Parceiro sem acesso aos e-mails cadastrados) */}
      {renderModalContatoAtivo()}

      {/* MODAL DE ALTERAR SENHA (QUANDO LOGADO) */}
      {showAlterarSenhaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white rounded-3xl p-6 sm:p-8 max-w-md w-full border border-gray-200 shadow-xl relative animate-fade-in">
            <button
              type="button"
              onClick={() => {
                setShowAlterarSenhaModal(false);
                setErroAlterarSenha(null);
                setSenhaAtual("");
                setNovaSenhaModal("");
                setConfirmarNovaSenhaModal("");
              }}
              className="absolute right-5 top-5 p-1 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-primary-50 text-primary-700 rounded-2xl">
                <KeyRound className="w-6 h-6" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Alterar Senha de Acesso
                </h3>
                <p className="text-xs text-gray-500">
                  Defina uma nova senha para a clínica credenciada.
                </p>
              </div>
            </div>

            {erroAlterarSenha && (
              <div className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 font-medium">
                {erroAlterarSenha}
              </div>
            )}

            <form onSubmit={handleAlterarSenha} className="space-y-4">
              <FormField
                label="Senha Atual"
                required
                htmlFor="senha-atual-input"
              >
                <div className="relative">
                  <input
                    id="senha-atual-input"
                    type={showSenhaAtual ? "text" : "password"}
                    value={senhaAtual}
                    onChange={(e) => setSenhaAtual(e.target.value)}
                    placeholder="Informe a senha atual"
                    required
                    className="w-full pl-4 pr-11 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSenhaAtual(!showSenhaAtual)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showSenhaAtual ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </FormField>

              <FormField
                label="Nova Senha"
                required
                htmlFor="nova-senha-modal-input"
              >
                <div className="relative">
                  <input
                    id="nova-senha-modal-input"
                    type={showNovaSenhaModal ? "text" : "password"}
                    value={novaSenhaModal}
                    onChange={(e) => setNovaSenhaModal(e.target.value)}
                    placeholder="Mínimo de 6 caracteres"
                    minLength={6}
                    required
                    className="w-full pl-4 pr-11 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNovaSenhaModal(!showNovaSenhaModal)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1"
                  >
                    {showNovaSenhaModal ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </FormField>

              <FormField
                label="Confirmar Nova Senha"
                required
                htmlFor="confirmar-nova-senha-modal-input"
              >
                <input
                  id="confirmar-nova-senha-modal-input"
                  type={showNovaSenhaModal ? "text" : "password"}
                  value={confirmarNovaSenhaModal}
                  onChange={(e) => setConfirmarNovaSenhaModal(e.target.value)}
                  placeholder="Repita a nova senha"
                  minLength={6}
                  required
                  className="w-full px-4 py-2.5 border border-gray-300 rounded-xl text-gray-900 text-sm font-medium focus:ring-2 focus:ring-primary-500 outline-none"
                />
              </FormField>

              <div className="pt-2 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setShowAlterarSenhaModal(false)}
                  className="px-4 py-2.5 border border-primary-200 text-primary-700 bg-white hover:bg-primary-50 rounded-xl text-xs font-bold transition-colors cursor-pointer"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={
                    alterandoSenha ||
                    !senhaAtual ||
                    !novaSenhaModal ||
                    !confirmarNovaSenhaModal
                  }
                  className="px-5 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold transition-colors shadow-2xs flex items-center gap-1.5 disabled:opacity-50"
                >
                  {alterandoSenha ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Salvando...</span>
                    </>
                  ) : (
                    <span>Salvar Nova Senha</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL: Confirmação e Celebração de Conclusão e Envio para Auditoria */}
      {conclusaoFeedback && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-xs flex items-center justify-center p-4 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl border border-gray-100 text-center space-y-4 animate-in zoom-in-95 duration-200">
            <div className="w-16 h-16 bg-primary-100 text-primary-600 rounded-2xl mx-auto flex items-center justify-center shadow-xs">
              <CheckCircle2 className="w-9 h-9" />
            </div>
            <div>
              <span className="text-[11px] font-bold uppercase tracking-wider text-primary-700 bg-primary-50 px-3 py-1 rounded-full border border-primary-100">
                Laudo Enviado para Auditoria
              </span>
              <h3 className="text-lg font-bold text-gray-900 mt-2">
                Atendimento Concluído com Sucesso!
              </h3>
              <p className="text-xs text-gray-600 mt-1.5 leading-relaxed">
                {conclusaoFeedback.mensagem}
              </p>
            </div>
            <div className="bg-gray-50 rounded-2xl p-3.5 border border-gray-200 text-left text-xs space-y-1">
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-semibold">
                  Colaborador:
                </span>
                <span className="font-bold text-gray-900">
                  {conclusaoFeedback.colaboradorNome}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-500 font-semibold">Destino:</span>
                <span className="font-medium text-primary-700 bg-primary-50 px-2 py-0.5 rounded-md">
                  Auditoria Técnica SST Exames
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setConclusaoFeedback(null)}
              className="w-full py-2.5 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs cursor-pointer"
            >
              Continuar Atendimentos
            </button>
          </div>
        </div>
      )}

      {/* Modal do Sistema: Reabertura de Atendimento Clínico (Auditoria SST) */}
      {renderModalReabrir()}

      {/* Footer Padrão SST Exames */}
      <footer className="bg-white border-t border-gray-200 py-4 text-center text-xs text-gray-500 mt-8">
        <p>
          © 2026 SST Exames • Gestão Ocupacional Inteligente & Rede de Clínicas
          Credenciadas
        </p>
      </footer>
    </div>
  );
}
